import { useParams, Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BORGHI } from '@/mocks/borghi';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import SeoHead from '@/components/feature/SeoHead';

const OSIMO_ATTRACTIONS = [
  {
    icon: 'ri-door-open-line',
    title: 'I sotterranei di Osimo',
    text: 'Il vero elemento distintivo della città: un patrimonio ipogeo scavato nell\'arenaria, tra gallerie, ambienti sotterranei, segni incisi e percorsi che attraversano la memoria più nascosta di Osimo.',
  },
  {
    icon: 'ri-building-line',
    title: 'Piazza del Comune e Piazza Boccolino',
    text: 'Sono il cuore civico e sociale della città, il punto da cui partire per comprendere il volto urbano di Osimo e la sua continuità tra epoca romana, medioevo e città contemporanea.',
  },
  {
    icon: 'ri-building-4-line',
    title: 'La Cattedrale di San Leopardo e il Battistero',
    text: 'Uno dei nuclei monumentali più significativi di Osimo, tra architettura medievale, stratificazione storica e uno dei più importanti fonti battesimali barocchi delle Marche.',
  },
  {
    icon: 'ri-gallery-line',
    title: 'Palazzo Campana',
    text: 'Tra gli edifici più importanti della città, lega il profilo architettonico di Osimo alla sua vocazione culturale e museale. Nello stesso complesso trovano spazio raccolte e testimonianze che attraversano secoli di storia artistica e archeologica del territorio.',
  },
  {
    icon: 'ri-ancient-gate-line',
    title: 'Fonte Magna',
    text: 'Alle pendici della collina osimana, poco fuori dall\'antica cinta muraria, si trova uno dei ninfei romani meglio conservati delle Marche: un luogo che restituisce con forza il legame tra Osimo e la sua eredità romana.',
  },
  {
    icon: 'ri-landscape-line',
    title: 'La Riviera del Conero a pochi minuti',
    text: 'Uno dei grandi vantaggi di Osimo è la vicinanza con la costa del Conero: spiagge, scogliere, sentieri e località come Sirolo e Numana sono facilmente raggiungibili e completano l\'esperienza di un territorio straordinariamente vario.',
  },
];

const SIROLO_STATS = [
  { value: '4.000+', label: 'Abitanti', icon: 'ri-group-line' },
  { value: '18 km', label: 'da Ancona', icon: 'ri-road-map-line' },
  { value: '2.890 €/m²', label: 'prezzo medio residenziale', icon: 'ri-home-line' },
  { value: 'Parco Conero', label: 'area protetta', icon: 'ri-leaf-line' },
];

const SIROLO_ATTRACTIONS = [
  {
    icon: 'ri-landscape-line',
    title: 'Spiaggia delle Due Sorelle',
    text: 'Il simbolo più iconico di Sirolo e del Conero, raggiungibile solo via mare. Falesie bianche a picco sull\'acqua, ciottoli e un paesaggio che non si dimentica.',
  },
  {
    icon: 'ri-building-4-line',
    title: 'Centro storico e piazzetta panoramica',
    text: 'Il cuore del borgo, tra vicoli, archi, belvederi e scorci sul mare. La piazzetta si apre improvvisamente sul Conero come una rivelazione.',
  },
  {
    icon: 'ri-building-4-line',
    title: 'Chiesa di San Nicola di Bari',
    text: 'Il principale riferimento religioso e storico-artistico del paese, in stile neoclassico. Situata nella piazza principale, è uno dei simboli di Sirolo.',
  },
  {
    icon: 'ri-leaf-line',
    title: 'Parco Regionale del Conero',
    text: 'Sentieri, boschi mediterranei e falesie a picco sul mare in una delle aree protette più rappresentative delle Marche. 18 sentieri percorribili a piedi, a cavallo o in mountain bike.',
  },
  {
    icon: 'ri-sailboat-line',
    title: 'Spiaggia Urbani e San Michele',
    text: 'Due delle spiagge più amate del territorio, perfette per vivere il lato più balneare e panoramico di Sirolo.',
  },
];

const CASTELFIDARDO_STATS = [
  { value: '1.395 €/m²', label: 'prezzo medio residenziale', icon: 'ri-home-line' },
  { value: '19 km', label: 'da Ancona', icon: 'ri-road-map-line' },
  { value: '12-13 km', label: 'da Sirolo e Conero', icon: 'ri-sailboat-line' },
  { value: 'PIF', label: 'competizione mondiale fisarmonica', icon: 'ri-music-line' },
];

const CASTELFIDARDO_ATTRACTIONS = [
  {
    icon: 'ri-music-line',
    title: 'Museo Internazionale della Fisarmonica',
    text: 'La tappa chiave per capire perché Castelfidardo sia conosciuta nel mondo come città della fisarmonica. Inaugurato il 9 maggio 1981 e ospitato nel Palazzo Municipale in Piazza della Repubblica, conserva strumenti rari, documenti, fotografie e registrazioni che illustrano l\'evoluzione tecnica ed estetica dello strumento.',
  },
  {
    icon: 'ri-trophy-line',
    title: 'Premio Internazionale della Fisarmonica',
    text: 'Ogni settembre la città diventa un riferimento internazionale per musicisti e appassionati, con concerti, concorsi e appuntamenti diffusi nel centro urbano. Il PIF è considerato la competizione più importante al mondo dedicata allo strumento simbolo della città.',
  },
  {
    icon: 'ri-sword-line',
    title: 'Ossario-Sacrario della battaglia',
    text: 'Uno dei luoghi simbolo della memoria risorgimentale, collocato nell\'area della Selva di Castelfidardo dove si svolse la fase più aspra della battaglia del 18 settembre 1860, quando le truppe sardo-piemontesi sconfissero le forze pontificie.',
  },
  {
    icon: 'ri-ancient-gate-line',
    title: 'Monumento Nazionale delle Marche "Ai Vittoriosi"',
    text: 'Il grande monumento commemorativo completato nel 1912, legato in modo diretto alla memoria della battaglia e alla costruzione dell\'identità civica cittadina. Uno dei simboli monumentali più forti del paesaggio fidardense.',
  },
  {
    icon: 'ri-building-4-line',
    title: 'Centro storico, Palazzo Municipale e Chiesa di Sant\'Agostino',
    text: 'Il centro conserva edifici civili e religiosi di interesse, tra cui il Palazzo Municipale che ospita il museo della fisarmonica e il complesso di Sant\'Agostino, fondato nel 1491.',
  },
  {
    icon: 'ri-landscape-line',
    title: 'Belvederi, Selva e paesaggio collinare',
    text: 'Castelfidardo non è solo musica e storia: i punti panoramici e le aree verdi raccontano il lato più paesaggistico della città, tra colline, sentieri e aperture visive verso il mare e i Sibillini.',
  },
];

const CAMERANO_STATS = [
  { value: '1.523 €/m²', label: 'prezzo medio residenziale', icon: 'ri-home-line' },
  { value: '24,3 km', label: 'dall\'aeroporto Ancona-Falconara', icon: 'ri-flight-takeoff-line' },
  { value: 'Rosso Conero', label: 'DOC del territorio', icon: 'ri-goblet-line' },
  { value: 'Parco Conero', label: 'a due passi', icon: 'ri-leaf-line' },
];

const CAMERANO_ATTRACTIONS = [
  {
    icon: 'ri-door-open-line',
    title: 'Le Grotte di Camerano',
    text: 'Il tratto più distintivo del borgo: un articolato percorso ipogeo sotto il centro storico, tra sale, corridoi, simboli e ambienti scavati nell\'arenaria. L\'origine non è ancora certa — la data più antica leggibile all\'interno è il 1327 — e il complesso è cresciuto e si è trasformato in epoche diverse.',
  },
  {
    icon: 'ri-building-line',
    title: 'Piazza Roma e il centro storico',
    text: 'Il cuore urbano di Camerano, da cui si diramano i vicoli e i principali punti di interesse del paese.',
  },
  {
    icon: 'ri-building-4-line',
    title: 'La Chiesa di San Francesco',
    text: 'Uno dei monumenti principali del borgo, con origini duecentesche e importanti interventi settecenteschi. Facciata in cotto, campanile slanciato e interni decorati.',
  },
  {
    icon: 'ri-goblet-line',
    title: 'Il Rosso Conero DOC',
    text: 'Camerano rientra a pieno titolo nell\'area di produzione della denominazione: il disciplinare include l\'intero territorio comunale. Il vino, ottenuto principalmente da Montepulciano, è uno dei tratti più identitari del territorio.',
  },
  {
    icon: 'ri-landscape-line',
    title: 'Il Parco del Conero',
    text: 'Colline, sentieri, costa e paesaggio naturale: Camerano vive dentro uno dei sistemi ambientali più forti e riconoscibili delle Marche, che comprende i comuni di Ancona, Camerano, Numana e Sirolo.',
  },
];

const SERVIGLIANO_STATS = [
  { value: '2.169', label: 'abitanti al 1° gennaio 2025', icon: 'ri-group-line' },
  { value: '215 m s.l.m.', label: 'altitudine della casa comunale', icon: 'ri-landscape-line' },
  { value: '26-27 min', label: 'da Fermo in auto', icon: 'ri-road-map-line' },
  { value: '902 €/m²', label: 'prezzo medio richiesto in vendita, marzo 2026', icon: 'ri-home-line' },
];

const SERVIGLIANO_ATTRACTIONS = [
  {
    icon: 'ri-building-line',
    title: 'Piazza Roma e l\'impianto della città ideale',
    text: 'Il vero fulcro della visita: la piazza, gli assi ortogonali, le porte monumentali e la forma quadrata del borgo spiegano da soli l\'unicità di Servigliano.',
  },
  {
    icon: 'ri-building-4-line',
    title: 'Collegiata di San Marco',
    text: 'Uno dei principali riferimenti religiosi e storici del centro, con organo settecentesco Fedeli e reliquie di San Servigliano e San Gualtiero.',
  },
  {
    icon: 'ri-ancient-pavilion-line',
    title: 'Palazzo Vecchiotti',
    text: 'Tra gli edifici civili più rappresentativi del borgo, contribuisce al carattere signorile di Piazza Roma e del centro storico.',
  },
  {
    icon: 'ri-heart-line',
    title: 'Santuario di Santa Maria del Piano',
    text: 'Luogo importante della storia religiosa locale, con un complesso monastico che si lega anche a precedenze di età romana del I secolo.',
  },
  {
    icon: 'ri-history-line',
    title: 'Parco della Pace e Casa della Memoria',
    text: 'Una visita che aggiunge profondità al borgo, raccontando il ruolo di Servigliano nelle vicende del Novecento attraverso il museo e la mostra fotografica permanente.',
  },
];

const MONTESICURO_STATS = [
  { value: 'Frazione di Ancona', label: 'Montesicuro appartiene al Comune di Ancona', icon: 'ri-map-pin-line' },
  { value: '1928', label: 'anno dell\'accorpamento al Comune di Ancona', icon: 'ri-calendar-line' },
  { value: 'XVI secolo', label: 'epoca della parte centrale della Chiesa di Santo Stefano', icon: 'ri-building-4-line' },
  { value: 'Poco più di 300', label: 'abitanti (stima RaiNews Marche, 2023)', icon: 'ri-group-line' },
];

const MONTESICURO_ATTRACTIONS = [
  {
    icon: 'ri-building-4-line',
    title: 'Chiesa di Santo Stefano',
    text: 'La parrocchiale del borgo, con nucleo cinquecentesco e ampliamenti settecenteschi, è il principale riferimento religioso e identitario di Montesicuro.',
  },
  {
    icon: 'ri-building-line',
    title: 'Chiesa di San Pancrazio',
    text: 'Piccola chiesa rurale fuori dal centro abitato, tra i luoghi più evocativi della memoria storica locale, datata probabilmente alla prima metà del Cinquecento.',
  },
  {
    icon: 'ri-ancient-gate-line',
    title: 'Vicoli dell\'antico castello',
    text: 'Il paese conserva ancora l\'andamento del vecchio nucleo medievale, legato alla funzione difensiva dei castelli anconetani della Repubblica di Ancona.',
  },
  {
    icon: 'ri-heart-line',
    title: 'Tradizioni di comunità',
    text: 'Il presepe meccanico, le feste locali e le iniziative della nuova Pro Loco Montesicuro APS (2026) raccontano una frazione ancora molto partecipata.',
  },
];

const PETRITOLI_STATS = [
  { value: '2.115', label: 'abitanti al 1° gennaio 2025', icon: 'ri-group-line' },
  { value: '358 m s.l.m.', label: 'altitudine del borgo', icon: 'ri-landscape-line' },
  { value: '15,9 km', label: 'da Fermo su strada', icon: 'ri-road-map-line' },
  { value: '560 €/m²', label: 'prezzo medio richiesto in vendita, marzo 2026', icon: 'ri-home-line' },
];

const PETRITOLI_ATTRACTIONS = [
  {
    icon: 'ri-ancient-gate-line',
    title: 'I Tre Archi e l\'ingresso al borgo',
    text: 'Il varco monumentale con tre archi gotici e due torri quattrocentesche è uno degli elementi più riconoscibili di Petritoli.',
  },
  {
    icon: 'ri-building-4-line',
    title: 'Teatro dell\'Iride',
    text: 'Un piccolo teatro storico ottocentesco che continua a essere uno dei cuori culturali del paese, progettato dall\'ingegner Giuseppe Sabbatini.',
  },
  {
    icon: 'ri-building-line',
    title: 'Santa Maria in Piazza',
    text: 'Uno dei riferimenti religiosi più importanti del borgo, ricostruito nel XVI secolo su una chiesa precedente.',
  },
  {
    icon: 'ri-heart-line',
    title: 'Santuario della Madonna della Liberata',
    text: 'Poco fuori dal centro, aggiunge al borgo una dimensione spirituale e storica di lunga durata, con origini che risalgono attorno all\'anno Mille.',
  },
  {
    icon: 'ri-landscape-line',
    title: 'Panorama sulla Valdaso',
    text: 'Il borgo offre visuali ampie che spaziano dalle colline interne ai Sibillini fino all\'Adriatico.',
  },
];

const ALTIDONA_STATS = [
  { value: '3.748', label: 'abitanti al 1° gennaio 2025', icon: 'ri-group-line' },
  { value: '224 m s.l.m.', label: 'altitudine della casa comunale', icon: 'ri-landscape-line' },
  { value: '3 km', label: 'di spiaggia nella frazione Marina', icon: 'ri-sailboat-line' },
  { value: '1.983 €/m²', label: 'prezzo medio richiesto in vendita, marzo 2026', icon: 'ri-home-line' },
];

const ALTIDONA_ATTRACTIONS = [
  {
    icon: 'ri-building-4-line',
    title: 'Centro storico collinare',
    text: 'Il nucleo antico di Altidona conserva una dimensione semplice e raccolta, con scorci sulla campagna e un rapporto diretto con il paesaggio della valle dell\'Aso.',
  },
  {
    icon: 'ri-building-line',
    title: 'Chiesa dei Santi Maria e Ciriaco',
    text: 'È il principale edificio religioso del borgo, con facciata neoclassica, campanile ottocentesco e una storia strettamente legata allo sviluppo del castello collinare.',
  },
  {
    icon: 'ri-sailboat-line',
    title: 'Marina di Altidona',
    text: 'La presenza della frazione costiera rende Altidona un comune particolare, capace di unire il borgo in altura e una vera dimensione balneare con circa 3 km di spiaggia.',
  },
  {
    icon: 'ri-award-line',
    title: 'Bandiera Blu 2025',
    text: 'Un riconoscimento che rafforza il profilo turistico della parte costiera del comune, assegnato al Lungomare Paolo Borsellino.',
  },
];

const TORRE_DI_PALME_STATS = [
  { value: '104 m', label: 's.l.m. — altitudine del borgo', icon: 'ri-landscape-line' },
  { value: '10,7 km', label: 'da Fermo su strada', icon: 'ri-road-map-line' },
  { value: '6,5 km', label: 'da Porto San Giorgio su strada', icon: 'ri-sailboat-line' },
  { value: '2019', label: 'anno di inaugurazione del Museo Archeologico', icon: 'ri-ancient-gate-line' },
];

const TORRE_DI_PALME_ATTRACTIONS = [
  {
    icon: 'ri-building-4-line',
    title: 'Tempio di Sant\'Agostino',
    text: 'Uno dei principali edifici del borgo, con il polittico di Vittore Crivelli tra le opere più importanti conservate a Torre di Palme.',
  },
  {
    icon: 'ri-heart-line',
    title: 'Chiesa di Santa Maria a Mare',
    text: 'Piccolo gioiello romanico-gotico del borgo, con campanile medievale, affreschi e un forte valore devozionale.',
  },
  {
    icon: 'ri-building-line',
    title: 'Chiesa di San Giovanni Battista',
    text: 'L\'edificio più antico del borgo, legato alle prime fasi di formazione dell\'abitato medievale, con origini probabilmente tra IX e X secolo.',
  },
  {
    icon: 'ri-eye-line',
    title: 'Belvedere di Piazza Lattanzi',
    text: 'Il punto panoramico più noto del paese, da cui si aprono vedute molto ampie sulla costa, sulle colline e sul paesaggio agrario circostante.',
  },
  {
    icon: 'ri-leaf-line',
    title: 'Bosco del Cugnolo e Grotta degli Amanti',
    text: 'Un itinerario naturalistico breve ma molto caratteristico, tra macchia mediterranea, duna fossile e leggenda locale.',
  },
  {
    icon: 'ri-ancient-gate-line',
    title: 'Museo Archeologico di Torre di Palme',
    text: 'Una visita che completa il racconto del borgo con i reperti delle tombe picene rinvenute nel territorio, inaugurato nel 2019.',
  },
];

const FERMO_STATS = [
  { value: '35.727', label: 'Abitanti', icon: 'ri-group-line' },
  { value: '319 m', label: 'sul livello del mare', icon: 'ri-landscape-line' },
  { value: '5 km', label: 'da Porto San Giorgio', icon: 'ri-sailboat-line' },
  { value: 'I sec. d.C.', label: 'epoca delle Cisterne Romane', icon: 'ri-ancient-gate-line' },
];

const FERMO_ATTRACTIONS = [
  {
    icon: 'ri-ancient-gate-line',
    title: 'Cisterne Romane',
    text: 'Il grande complesso sotterraneo romano, articolato in 30 ambienti su tre file per circa 2.200 mq, è una delle visite più sorprendenti della città. Costruite nel I secolo d.C. per la raccolta e distribuzione dell\'acqua, sono tra i più importanti impianti idrici romani conservati nelle Marche.',
  },
  {
    icon: 'ri-building-4-line',
    title: 'Cattedrale di Santa Maria Assunta',
    text: 'Uno dei simboli di Fermo, con facciata originaria in pietra d\'Istria. La chiesa attuale fu completata nel 1227; l\'interno è prevalentemente neoclassico per effetto dei rifacimenti del Settecento.',
  },
  {
    icon: 'ri-building-line',
    title: 'Museo Diocesano e casula di Thomas Becket',
    text: 'Un museo piccolo ma prezioso, dove si conserva la celebre casula di San Thomas Becket, rarissimo tessuto di origine araba datato 1116. È il luogo corretto a cui riferire questo cimelio.',
  },
  {
    icon: 'ri-gallery-line',
    title: 'Palazzo dei Priori e Pinacoteca Civica',
    text: 'Cuore culturale del centro storico, con la Pinacoteca, la Sala del Mappamondo e opere di Rubens e Giovanni Lanfranco, oltre al ciclo di dipinti murali di Andrea Boscoli.',
  },
  {
    icon: 'ri-map-pin-line',
    title: 'Piazza del Popolo',
    text: 'La grande piazza storica di Fermo, su cui affaccia Palazzo dei Priori, è uno degli spazi urbani più riconoscibili della città.',
  },
];

const MONTELUPONE_STATS = [
  { value: '3.355', label: 'abitanti al 1° gennaio 2025', icon: 'ri-group-line' },
  { value: '13,1 km', label: 'da Macerata su strada', icon: 'ri-road-map-line' },
  { value: '13,6 km', label: 'dalla costa verso Porto Potenza Picena', icon: 'ri-sailboat-line' },
  { value: '818 €/m²', label: 'prezzo medio richiesto in vendita, marzo 2026', icon: 'ri-home-line' },
];

const MONTELUPONE_ATTRACTIONS = [
  {
    icon: 'ri-ancient-gate-line',
    title: 'La cinta muraria e le porte medievali',
    text: 'Il borgo conserva ancora l\'antica cinta muraria e le quattro porte storiche, tra gli elementi più forti della sua identità urbana.',
  },
  {
    icon: 'ri-building-line',
    title: 'Palazzetto del Podestà e Torre Civica',
    text: 'Sono il cuore architettonico del centro storico e uno dei punti panoramici più suggestivi del paese. Il Palazzetto è un edificio trecentesco, mentre la torre domina il profilo del borgo con visuali che arrivano fino ai Monti Sibillini e al mare Adriatico.',
  },
  {
    icon: 'ri-building-4-line',
    title: 'Chiesa di San Francesco',
    text: 'Uno dei principali edifici religiosi del borgo, con origini duecentesche (costruzione 1251, consacrazione 1397) e importanti trasformazioni nei secoli successivi. L\'interno è in larga parte tardo barocco, pur conservando elementi più antichi.',
  },
  {
    icon: 'ri-music-2-line',
    title: 'Teatro Nicola Degli Angeli',
    text: 'Un piccolo teatro storico di impianto ottocentesco inserito nel palazzo civico, che aggiunge una dimensione culturale importante alla visita del borgo.',
  },
  {
    icon: 'ri-ancient-pavilion-line',
    title: 'Abbazia di San Firmano',
    text: 'Poco fuori dal centro, rappresenta uno dei riferimenti storico-religiosi più importanti del territorio di Montelupone.',
  },
];

const FIASTRA_STATS = [
  { value: '607', label: 'Abitanti al 1° gennaio 2025', icon: 'ri-group-line' },
  { value: '1955', label: 'anno di creazione del Lago di Fiastra', icon: 'ri-water-flash-line' },
  { value: '71.437 ha', label: 'superficie del Parco Nazionale dei Monti Sibillini', icon: 'ri-map-2-line' },
  { value: '583 €/m²', label: 'prezzo medio richiesto in vendita, marzo 2026', icon: 'ri-home-line' },
];

const FIASTRA_ATTRACTIONS = [
  {
    icon: 'ri-water-flash-line',
    title: 'Lago di Fiastra',
    text: 'Il luogo simbolo del comune: un grande lago artificiale nel Parco dei Sibillini, creato nel 1955 sul fiume Fiastrone. Meta popolare per relax, nuoto, kayak ed escursioni, è descritto dalle fonti regionali come il più grande bacino idroelettrico delle Marche.',
  },
  {
    icon: 'ri-landscape-line',
    title: 'Lame Rosse',
    text: 'Uno degli itinerari più noti dei Monti Sibillini, celebre per i pinnacoli e i piccoli canyon di roccia rossa sopra il lago. Il Parco lo presenta come un percorso immerso nella cornice del lago, tra formazioni rocciose tra le più riconoscibili delle Marche.',
  },
  {
    icon: 'ri-leaf-line',
    title: 'Parco Nazionale dei Monti Sibillini',
    text: 'Il contesto naturale in cui si inserisce Fiastra: un\'area protetta di oltre 71.000 ettari tra Marche e Umbria, con montagne, vallate profonde, faggete, dorsali montane e un patrimonio naturalistico di grande rilievo.',
  },
  {
    icon: 'ri-building-4-line',
    title: 'Abbazia di Santa Maria di Rio Sacro',
    text: 'Uno dei principali riferimenti storici e religiosi del territorio comunale, nell\'area di Meriggio lungo la sponda sinistra del Fiastrone. L\'attuale struttura in stile romanico locale conserva una cripta romanica del IX secolo.',
  },
];

const TOLENTINO_STATS = [
  { value: '17.557', label: 'Abitanti al 31 dicembre 2024', icon: 'ri-group-line' },
  { value: '40 km', label: 'dalla costa adriatica (Civitanova Marche)', icon: 'ri-sailboat-line' },
  { value: '58,8 km', label: 'dai Monti Sibillini, su strada', icon: 'ri-map-2-line' },
  { value: '1.212 €/m²', label: 'prezzo medio richiesto in vendita, marzo 2026', icon: 'ri-home-line' },
];

const TOLENTINO_ATTRACTIONS = [
  {
    icon: 'ri-building-4-line',
    title: 'Basilica di San Nicola',
    text: 'Il principale complesso monumentale della città, tra spiritualità, arte medievale, chiostro e museo. Il portale marmoreo di Nanni di Bartolo e il Cappellone con il ciclo trecentesco attribuito a Pietro da Rimini sono tra i punti più preziosi.',
  },
  {
    icon: 'ri-gallery-line',
    title: 'Cappellone di San Nicola',
    text: 'Uno dei luoghi più preziosi di Tolentino, con il ciclo trecentesco attribuito a Pietro da Rimini, articolato nelle storie della Vergine, di Cristo e di San Nicola.',
  },
  {
    icon: 'ri-emotion-laugh-line',
    title: 'MIUMOR – Museo Internazionale dell\'Umorismo nell\'Arte',
    text: 'Nel Palazzo Sangallo, raccoglie oltre 5.000 opere nate anche dalla Biennale Internazionale dell\'Umorismo. Una raccolta unica nel panorama cittadino e uno dei poli culturali più caratteristici di Tolentino.',
  },
  {
    icon: 'ri-ancient-gate-line',
    title: 'Ponte del Diavolo',
    text: 'Uno dei simboli civili di Tolentino, ponte fortificato del 1268 con cinque arcate sul fiume Chienti.',
  },
  {
    icon: 'ri-ancient-pavilion-line',
    title: 'Castello della Rancia',
    text: 'Tra i luoghi più noti del territorio tolentinate, oggi inserito nella rete dei Musei Civici.',
  },
  {
    icon: 'ri-building-4-line',
    title: 'Concattedrale di San Catervo',
    text: 'Un punto chiave della storia religiosa cittadina, con origini anteriori all\'anno Mille e una storia architettonica complessa e molto antica.',
  },
];

const SAN_SEVERINO_STATS = [
  { value: '25 km', label: 'da Macerata', icon: 'ri-road-map-line' },
  { value: '75 km', label: 'da Ancona', icon: 'ri-car-line' },
  { value: 'Valle Potenza', label: 'posizione strategica', icon: 'ri-landscape-line' },
  { value: 'Pinacoteca', label: 'museo civico d\'eccellenza', icon: 'ri-gallery-line' },
];

const SAN_SEVERINO_ATTRACTIONS = [
  {
    icon: 'ri-building-4-line',
    title: 'Piazza del Popolo',
    text: 'Cuore del centro storico, si distingue per la forma ellittica allungata, i portici, i palazzi signorili, il teatro Feronia e le architetture civili e religiose che la incorniciano.',
  },
  {
    icon: 'ri-gallery-line',
    title: 'Pinacoteca civica "P. Tacchi Venturi"',
    text: 'Un museo di grande qualità, con opere dal XIV al XVII secolo e una presenza importante dei fratelli Salimbeni.',
  },
  {
    icon: 'ri-ancient-gate-line',
    title: 'Castello al Monte e Torre degli Smeducci',
    text: 'La parte alta della città custodisce uno dei nuclei più identitari di San Severino, tra memoria medievale, torre civica e museo archeologico.',
  },
  {
    icon: 'ri-map-2-line',
    title: 'Area archeologica di Septempeda',
    text: 'Un luogo chiave per capire le origini romane del territorio, con mura, porta urbica, terme e fornaci.',
  },
  {
    icon: 'ri-home-line',
    title: 'Mercato immobiliare accessibile',
    text: 'Un contesto interessante per chi cerca autenticità, spazio e valori medi inferiori rispetto alla media della provincia di Macerata.',
  },
];

const CAMPOCAVALLO_STATS = [
  { value: '2,5 km', label: 'dal centro di Osimo', icon: 'ri-road-map-line' },
  { value: '18 km', label: 'da Ancona', icon: 'ri-car-line' },
  { value: '1.344 €/m²', label: 'prezzo medio Osimo, marzo 2026', icon: 'ri-home-line' },
  { value: '1892', label: 'anno del prodigio', icon: 'ri-heart-line' },
];

const CAMPOCAVALLO_ATTRACTIONS = [
  {
    icon: 'ri-building-4-line',
    title: 'Santuario della Beata Vergine Addolorata',
    text: 'Il principale luogo di interesse di Campocavallo, nato tra fine Ottocento e inizi Novecento e ancora oggi centro di devozione e pellegrinaggio. Progettato dall\'architetto osimano Costantino Costantini in stile neogotico con influenze lombarde, custodisce sette altari, vetrate istoriate e la tomba di don Giovanni Sorbellini.',
  },
  {
    icon: 'ri-seedling-line',
    title: 'Festa del Covo',
    text: 'La prima domenica di agosto, Campocavallo porta in processione il Covo: un grande monumento votivo intrecciato a mano con 2-3 milioni di spighe di grano. La tradizione nasce nel 1939 ed è il gesto identitario più forte della frazione.',
  },
  {
    icon: 'ri-landscape-line',
    title: 'Campagna osimana e Riviera del Conero',
    text: 'Campocavallo si colloca in una posizione intermedia tra la campagna osimana e la costa. La Riviera del Conero è facilmente raggiungibile, così come i sentieri del Parco del Conero.',
  },
  {
    icon: 'ri-building-line',
    title: 'Vicinanza a Osimo e Ancona',
    text: 'Il santuario si trova a circa 2,5 km dal centro di Osimo, mentre la distanza stradale da Ancona è di circa 18 km. Una dimensione più tranquilla, senza rinunciare alla vicinanza con la città e i principali servizi.',
  },
];

const STAFFOLO_STATS = [
  { value: '2.168', label: 'Abitanti (1° gen. 2025)', icon: 'ri-group-line' },
  { value: '16-18 km', label: 'da Jesi', icon: 'ri-road-map-line' },
  { value: '37 min', label: 'da Ancona in auto', icon: 'ri-car-line' },
  { value: '773 €/m²', label: 'prezzo medio residenziale, marzo 2026', icon: 'ri-home-line' },
];

const OFFAGNA_STATS = [
  { value: '2.120', label: 'abitanti al 1° gennaio 2025', icon: 'ri-group-line' },
  { value: '1454–1456', label: 'anni di costruzione della Rocca', icon: 'ri-ancient-gate-line' },
  { value: '~10 km', label: 'da Osimo', icon: 'ri-road-map-line' },
  { value: '1.340 €/m²', label: 'prezzo medio richiesto, marzo 2026', icon: 'ri-home-4-line' },
];

const OFFAGNA_ATTRACTIONS = [
  {
    icon: 'ri-ancient-gate-line',
    title: 'Rocca di Offagna',
    description: 'Il cuore del borgo e la visita imprescindibile, tra architettura militare quattrocentesca, panorami sulle colline e collezione di armi antiche.',
  },
  {
    icon: 'ri-sword-line',
    title: 'Feste Medievali',
    description: 'La grande rievocazione storica che ogni estate (luglio) trasforma il borgo in uno scenario medievale con spettacoli, mercati e cortei in costume.',
  },
  {
    icon: 'ri-book-open-line',
    title: 'Museo della Liberazione di Ancona',
    description: 'Un percorso sulla seconda guerra mondiale e sulla liberazione del territorio, ospitato nel centro storico di Offagna.',
  },
  {
    icon: 'ri-leaf-line',
    title: 'Museo di Scienze Naturali "Luigi Paolucci"',
    description: 'Uno spazio dedicato alla fauna, alla flora e alla geologia marchigiana, parte integrante del polo museale del borgo.',
  },
];

const MONTEFANO_STATS = [
  { value: '3.304', label: 'abitanti al 1° gennaio 2026', icon: 'ri-group-line' },
  { value: '17,1 km', label: 'da Macerata su strada', icon: 'ri-road-map-line' },
  { value: '12,5 km', label: 'da Recanati su strada', icon: 'ri-road-map-line' },
  { value: '774 €/m²', label: 'prezzo medio richiesto in vendita, aprile 2026', icon: 'ri-home-line' },
];

const MONTEFANO_ATTRACTIONS = [
  {
    icon: 'ri-building-2-line',
    title: 'Teatro La Rondinella',
    text: 'Una piccola sala storica con due ordini di palchi, tra i luoghi culturali più identitari di Montefano. Progettata da Virginio Tombolini, visitabile su prenotazione.',
  },
  {
    icon: 'ri-camera-line',
    title: 'Museo Ghergo',
    text: 'Nel Palazzo dei Priori, sotto il loggiato di Piazza Bracaccini, conserva opere, strumenti di lavoro e spazi espositivi dedicati ad Arturo Ghergo, fotografo nato a Montefano nel 1901.',
  },
  {
    icon: 'ri-building-4-line',
    title: 'Collegiata di San Donato',
    text: 'La principale chiesa del centro, con reliquie di Sant\'Onorio e San Saverio e una tela con San Donato e San Giovanni Nepomuceno ai lati dell\'Assunta.',
  },
  {
    icon: 'ri-ancient-gate-line',
    title: 'Torrione scarpato',
    text: 'La testimonianza più evidente del passato difensivo di Montefano: ricostruito dagli osimani sui ruderi di una fortificazione distrutta nel 1434, con merli ghibellini.',
  },
  {
    icon: 'ri-flag-line',
    title: 'Palio delle Contrade',
    text: 'L\'evento più riconoscibile della vita del borgo, che si svolge tradizionalmente a luglio e riporta Montefano a un\'atmosfera di gusto storico.',
  },
];

const ASPIO_STATS = [
  { value: 'Quartiere Osimo', label: 'San Biagio – Santo Stefano – Aspio', icon: 'ri-map-pin-line' },
  { value: '~12 km', label: 'da Aspio Terme a Osimo', icon: 'ri-road-map-line' },
  { value: '~8 km', label: 'da Aspio Terme ad Ancona', icon: 'ri-car-line' },
  { value: '1.344 €/m²', label: 'prezzo medio Osimo, marzo 2026', icon: 'ri-home-line' },
];

const ASPIO_ATTRACTIONS = [
  {
    icon: 'ri-community-line',
    title: 'Parrocchia di San Giuseppe Lavoratore',
    text: 'Il principale riferimento religioso e comunitario della località, aperto ai fedeli il 1° maggio 1966, legato allo sviluppo residenziale dell\'Aspio nel secondo Novecento.',
  },
  {
    icon: 'ri-drop-line',
    title: 'Terme dell\'Aspio',
    text: 'Un luogo identitario dell\'area, nel vicino territorio di Camerano, legato alle acque salsobromoiodiche conosciute già dalla fine dell\'Ottocento e alla tradizione termale locale.',
  },
  {
    icon: 'ri-map-pin-2-line',
    title: 'Vicinanza a Osimo e Ancona',
    text: 'Uno dei punti di forza dell\'Aspio è la facilità dei collegamenti con il centro di Osimo e con il capoluogo di regione, raggiungibili in pochi minuti.',
  },
  {
    icon: 'ri-landscape-line',
    title: 'Porta di accesso al Conero',
    text: 'Per posizione, Aspio si presta bene come base pratica per muoversi tra città, fondovalle e Riviera del Conero, grazie alla sua collocazione tra Osimo, Camerano e Ancona.',
  },
];

const STAFFOLO_ATTRACTIONS = [
  {
    icon: 'ri-ancient-gate-line',
    title: 'Il torrione e il giro delle mura',
    text: 'Il torrione dell\'Albornoz (XIV sec.) e il percorso lungo la cinta muraria sono tra i punti più suggestivi del borgo, anche per la qualità del panorama sulla Vallesina. Le mura hanno forma ellittica con due accessi contrapposti: Porta Venezia a nord e Porta San Martino a sud.',
  },
  {
    icon: 'ri-goblet-line',
    title: 'Museo del Vino e dell\'Arte Contadina',
    text: 'Una tappa centrale per comprendere la vocazione vitivinicola di Staffolo, con strumenti storici di cantina, attrezzi da lavoro e materiali legati alla produzione del vino. Tra i pezzi più noti viene ricordato un antico torchio del 1695. Annessa l\'Enoteca Comunale.',
  },
  {
    icon: 'ri-building-4-line',
    title: 'Piazza Leopardi, Sant\'Egidio e San Francesco',
    text: 'Il cuore del centro storico, dove si concentrano i principali riferimenti civili e religiosi del paese. La parrocchiale di Sant\'Egidio conserva opere di pregio; San Francesco è uno dei principali riferimenti storici del borgo.',
  },
  {
    icon: 'ri-landscape-line',
    title: 'Il paesaggio del Verdicchio',
    text: 'Vigneti, colline coltivate e aziende vinicole definiscono l\'identità agricola e culturale del territorio. Staffolo rientra nel sistema dei Castelli del Verdicchio, uno dei paesaggi enologici più rappresentativi delle Marche.',
  },
  {
    icon: 'ri-award-line',
    title: 'Le feste del vino',
    text: 'Tra gli appuntamenti più riconoscibili ci sono la Festa del Verdicchio e il Premio Verdicchio d\'Oro, che rafforzano il legame tra Staffolo e la sua tradizione enogastronomica. Staffolo è anche Bandiera Arancione del Touring Club Italiano.',
  },
];

const SARNANO_STATS = [
  { value: '3.058', label: 'Abitanti', icon: 'ri-group-line' },
  { value: '526 m s.l.m.', label: 'altitudine casa comunale', icon: 'ri-landscape-line' },
  { value: '837 €/m²', label: 'prezzo medio richiesto, maggio 2026', icon: 'ri-home-line' },
  { value: '317–1.914 m', label: 'escursione altimetrica', icon: 'ri-map-2-line' },
];

const SARNANO_ATTRACTIONS = [
  {
    icon: 'ri-building-line',
    title: 'Piazza Alta',
    text: 'Il cuore medievale del borgo, con i principali edifici civili e religiosi del centro storico.',
  },
  {
    icon: 'ri-building-4-line',
    title: 'Chiesa di Santa Maria Assunta',
    text: 'La principale chiesa del centro storico, completata nel 1396 con la torre campanaria.',
  },
  {
    icon: 'ri-gallery-line',
    title: 'Pinacoteca Comunale e Musei Civici',
    text: 'Una raccolta di grande interesse, con opere di Simone De Magistris, Stefano Folchetti e Vittore Crivelli.',
  },
  {
    icon: 'ri-music-2-line',
    title: 'Teatro della Vittoria',
    text: 'Piccolo teatro storico nel Palazzo del Popolo, tra i luoghi culturali più identitari di Sarnano.',
  },
  {
    icon: 'ri-water-flash-line',
    title: 'Via delle Cascate Perdute',
    text: 'Uno degli itinerari naturalistici più amati del territorio, tra bosco e salti d\'acqua nei pressi del paese.',
  },
];

const NUMANA_STATS = [
  { value: '3.737', label: 'abitanti al 1° gennaio 2026', icon: 'ri-group-line' },
  { value: '18,4 km', label: 'da Ancona su strada', icon: 'ri-road-map-line' },
  { value: '1,9 km', label: 'da Sirolo a piedi', icon: 'ri-walk-line' },
  { value: '3.433 €/m²', label: 'prezzo medio richiesto in vendita, maggio 2026', icon: 'ri-home-line' },
];

const NUMANA_ATTRACTIONS = [
  {
    icon: 'ri-stairs-line',
    title: 'La Costarella',
    text: 'La scalinata più caratteristica del borgo, tra case in pietra del Conero, scorci sul porto e memoria marinara.',
  },
  {
    icon: 'ri-ancient-gate-line',
    title: 'Arco di Torre',
    text: 'Il simbolo di Numana, resto medievale in posizione panoramica sopra la marina.',
  },
  {
    icon: 'ri-building-4-line',
    title: 'Santuario del Santissimo Crocifisso',
    text: 'Uno dei luoghi di fede più importanti del centro storico, legato alla devozione locale e alla festa del Cristo Re.',
  },
  {
    icon: 'ri-bank-line',
    title: 'Antiquarium Statale',
    text: 'Il museo che racconta la Numana picena attraverso reperti archeologici di grande rilievo, tra cui la Tomba della Regina.',
  },
  {
    icon: 'ri-sailboat-line',
    title: 'Spiaggia del Frate',
    text: 'Una delle spiagge più suggestive di Numana Alta, raggiungibile a piedi dal centro.',
  },
];

const BorgoDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const lang = i18n.language as 'it' | 'en' | 'de';
  const content = useScrollAnimation();
  const osimoParagraph = useScrollAnimation({ threshold: 0.05 });
  const osimoLife = useScrollAnimation({ threshold: 0.05 });
  const osimoAttractions = useScrollAnimation({ threshold: 0.05 });
  const cameranoParagraph = useScrollAnimation({ threshold: 0.05 });
  const cameranoLife = useScrollAnimation({ threshold: 0.05 });
  const cameranoAttractions = useScrollAnimation({ threshold: 0.05 });
  const castelfidardoParagraph = useScrollAnimation({ threshold: 0.05 });
  const castelfidardoLife = useScrollAnimation({ threshold: 0.05 });
  const castelfidardoAttractions = useScrollAnimation({ threshold: 0.05 });
  const siroloParagraph = useScrollAnimation({ threshold: 0.05 });
  const siroloLife = useScrollAnimation({ threshold: 0.05 });
  const siroloAttractions = useScrollAnimation({ threshold: 0.05 });
  const staffoloParagraph = useScrollAnimation({ threshold: 0.05 });
  const staffoloLife = useScrollAnimation({ threshold: 0.05 });
  const staffoloAttractions = useScrollAnimation({ threshold: 0.05 });
  const campocavalloParagraph = useScrollAnimation({ threshold: 0.05 });
  const campocavalloLife = useScrollAnimation({ threshold: 0.05 });
  const campocavalloAttractions = useScrollAnimation({ threshold: 0.05 });
  const sanSeverinoParagraph = useScrollAnimation({ threshold: 0.05 });
  const sanSeverinoLife = useScrollAnimation({ threshold: 0.05 });
  const sanSeverinoAttractions = useScrollAnimation({ threshold: 0.05 });
  const tolentinoParagraph = useScrollAnimation({ threshold: 0.05 });
  const tolentinoLife = useScrollAnimation({ threshold: 0.05 });
  const tolentinoAttractions = useScrollAnimation({ threshold: 0.05 });
  const fiastraParagraph = useScrollAnimation({ threshold: 0.05 });
  const fiastraLife = useScrollAnimation({ threshold: 0.05 });
  const fiastraAttractions = useScrollAnimation({ threshold: 0.05 });
  const monteluponeParagraph = useScrollAnimation({ threshold: 0.05 });
  const monteluponeLife = useScrollAnimation({ threshold: 0.05 });
  const monteluponeAttractions = useScrollAnimation({ threshold: 0.05 });
  const fermoParagraph = useScrollAnimation({ threshold: 0.05 });
  const fermoLife = useScrollAnimation({ threshold: 0.05 });
  const fermoAttractions = useScrollAnimation({ threshold: 0.05 });
  const torreParagraph = useScrollAnimation({ threshold: 0.05 });
  const torreLife = useScrollAnimation({ threshold: 0.05 });
  const torreAttractions = useScrollAnimation({ threshold: 0.05 });
  const altidonaParagraph = useScrollAnimation({ threshold: 0.05 });
  const altidonaLife = useScrollAnimation({ threshold: 0.05 });
  const altidonaAttractions = useScrollAnimation({ threshold: 0.05 });
  const petritoloParagraph = useScrollAnimation({ threshold: 0.05 });
  const petritoloLife = useScrollAnimation({ threshold: 0.05 });
  const petritoloAttractions = useScrollAnimation({ threshold: 0.05 });
  const monteSicuroParagraph = useScrollAnimation({ threshold: 0.05 });
  const monteSicuroLife = useScrollAnimation({ threshold: 0.05 });
  const monteSicuroAttractions = useScrollAnimation({ threshold: 0.05 });
  const serviglianoP = useScrollAnimation({ threshold: 0.05 });
  const serviglianoLife = useScrollAnimation({ threshold: 0.05 });
  const serviglianoAttractions = useScrollAnimation({ threshold: 0.05 });
  const aspioParagraph = useScrollAnimation({ threshold: 0.05 });
  const aspioLife = useScrollAnimation({ threshold: 0.05 });
  const aspioAttractions = useScrollAnimation({ threshold: 0.05 });
  const offagnaParagraph = useScrollAnimation({ threshold: 0.05 });
  const offagnaLife = useScrollAnimation({ threshold: 0.05 });
  const offagnaAttractions = useScrollAnimation({ threshold: 0.05 });
  const montefanoParagraph = useScrollAnimation({ threshold: 0.05 });
  const montefanoLife = useScrollAnimation({ threshold: 0.05 });
  const montefanoAttractions = useScrollAnimation({ threshold: 0.05 });
  const sarnanoParagraph = useScrollAnimation({ threshold: 0.05 });
  const sarnanoLife = useScrollAnimation({ threshold: 0.05 });
  const sarnanoAttractions = useScrollAnimation({ threshold: 0.05 });
  const numanaParagraph = useScrollAnimation({ threshold: 0.05 });
  const numanaLife = useScrollAnimation({ threshold: 0.05 });
  const numanaAttractions = useScrollAnimation({ threshold: 0.05 });
  const borgo = BORGHI.find((b) => b.slug === slug);
  if (!borgo) return <Navigate to="/borghi" replace />;

  const isOsimo = slug === 'osimo';
  const isCamerano = slug === 'camerano';
  const isCastelfidardo = slug === 'castelfidardo';
  const isSirolo = slug === 'sirolo';
  const isVarano = slug === 'varano';
  const isStaffolo = slug === 'staffolo';
  const isCampocavallo = slug === 'campocavallo';
  const isSanSeverino = slug === 'san-severino';
  const isTolentino = slug === 'tolentino';
  const isFiastra = slug === 'fiastra';
  const isMontelupone = slug === 'montelupone';
  const isFermo = slug === 'fermo';
  const isTorreDiPalme = slug === 'torre-di-palme';
  const isAltidona = slug === 'altidona';
  const isPetritoli = slug === 'petritoli';
  const isMonteSicuro = slug === 'monte-sicuro';
  const isServigliano = slug === 'servigliano';
  const isAspio = slug === 'aspio';
  const isOffagna = slug === 'offagna';
  const isMontefano = slug === 'montefano';
  const isSarnano = slug === 'sarnano';
  const isNumana = slug === 'numana';

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.discoveringmarche.com';
  const borgoJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Place',
      name: borgo.name,
      description: borgo.description,
      url: `${siteUrl}/borghi/${borgo.slug}`,
      image: borgo.heroImage,
      address: {
        '@type': 'PostalAddress',
        addressRegion: borgo.provincia,
        addressCountry: 'IT',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Borghi', item: `${siteUrl}/borghi` },
        { '@type': 'ListItem', position: 3, name: borgo.name, item: `${siteUrl}/borghi/${borgo.slug}` },
      ],
    },
  ];

  const getDescription = () => {
    if (lang === 'en') return borgo.descriptionEn;
    if (lang === 'de') return borgo.descriptionDe;
    return borgo.description;
  };

  const getHighlights = () => {
    if (lang === 'en') return borgo.highlightsEn;
    if (lang === 'de') return borgo.highlightsDe;
    return borgo.highlights;
  };

  const highlightIcons = ['ri-building-line', 'ri-eye-line', 'ri-palette-line', 'ri-map-pin-line'];

  return (
    <main className="min-h-screen bg-white">
      <SeoHead
        title={`${borgo.name} — Borghi storici Marche | Marche Casa`}
        description={`${borgo.name} (${borgo.provincia}): ${borgo.tagline}. Scopri storia, cultura e qualità della vita a ${borgo.name}, tra i borghi più belli delle Marche.`}
        keywords={`${borgo.name}, borghi Marche, ${borgo.provincia}, borghi storici Marche, vivere nelle Marche, ${borgo.tagline}`}
        canonical={`/borghi/${borgo.slug}`}
        ogImage={borgo.heroImage}
        jsonLd={borgoJsonLd}
      />
      {/* Hero */}
      <section className="relative h-[65vh] min-h-[480px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          {[
                'osimo',
                'camerano',
                'castelfidardo',
                'varano',
                'sirolo',
                'campocavallo',
                'staffolo',
                'san-severino',
                'tolentino',
                'fiastra',
                'torre-di-palme',
                'offagna',
                'altidona',
                'montelupone',
                'fermo',
                'petritoli',
                'monte-sicuro',
                'servigliano',
                'montefano',
                'sarnano',
                'numana',
              ].includes(borgo.slug) ? (
            <img
              src={borgo.heroImage}
              alt={`${borgo.name} — borgo Marche`}
              title={`${borgo.name} — immobili borgo Marche`}
              className="w-full h-full object-cover object-center"
              fetchPriority="high"
              decoding="async"
            />
          ) : (
            <div className="w-full h-full bg-[#C8956D]" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-black/10"></div>
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pb-16">
          <nav className="flex items-center gap-2 mb-6" aria-label="Breadcrumb">
            <Link to="/borghi" className="font-sans text-xs text-white/60 hover:text-white transition-colors whitespace-nowrap">
              {t('nav.borghi')}
            </Link>
            <i className="ri-arrow-right-s-line text-white/40 text-xs"></i>
            <span className="font-sans text-xs text-white/80 whitespace-nowrap">{borgo.name}</span>
          </nav>
          <span className={`inline-block font-sans text-xs tracking-widest uppercase px-3 py-1.5 rounded-full text-white mb-4 ${
            borgo.zona === 'mare' ? 'bg-sky-600/80' :
            borgo.zona === 'collina' ? 'bg-olive-600/80' :
            'bg-anthracite-600/80'
          }`}>
            {t(`borghi.zona.${borgo.zona}`)} · {borgo.provincia}
          </span>
          <h1 className="font-serif text-5xl md:text-7xl text-white font-light leading-none">
            {borgo.name}
          </h1>
        </div>
      </section>

      {/* ── Sezione principale: generica per tutti i borghi tranne Osimo e Camerano ── */}
      {!isOsimo && !isCamerano && !isCastelfidardo && !isSirolo && !isVarano && !isStaffolo && !isCampocavallo && !isSanSeverino && !isTolentino && !isFiastra && !isMontelupone && !isFermo && !isTorreDiPalme && !isAltidona && !isPetritoli && !isMonteSicuro && !isServigliano && !isAspio && !isOffagna && !isMontefano && !isSarnano && !isNumana && (
        <section
          ref={content.ref}
          className={`fade-in-up ${content.isVisible ? 'visible' : ''} max-w-7xl mx-auto px-6 py-20`}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">
                {t('borgo.storia')}
              </p>
              <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-6 leading-snug">
                {t('borgo.storia.title', { name: borgo.name })}
              </h2>
              <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-8">
                {getDescription()}
              </p>
              <div className="grid grid-cols-2 gap-4">
                {getHighlights().map((h, i) => (
                  <div key={i} className="flex items-start gap-3 p-4 bg-ivory-50 rounded-xl border border-ivory-200">
                    <div className="w-8 h-8 flex items-center justify-center text-terracotta-300 flex-shrink-0">
                      <i className={`${highlightIcons[i % highlightIcons.length]} text-lg`}></i>
                    </div>
                    <p className="font-sans text-sm text-anthracite-600 leading-snug">{h}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 rounded-xl overflow-hidden aspect-[16/9]">
                {['osimo', 'camerano', 'castelfidardo', 'varano', 'sirolo', 'campocavallo', 'staffolo', 'san-severino', 'tolentino', 'fiastra', 'sarnano', 'numana'].includes(borgo.slug) ? (
                  <img
                    src={`https://readdy.ai/api/search-image?query=$%7Bborgo.name%7D%20Marche%20Italy%20historic%20village%20stone%20buildings%20medieval%20architecture%20beautiful%20Italian%20landscape%20warm%20light&width=800&height=450&seq=borgoGrid1${slug}&orientation=landscape`}
                    alt={`${borgo.name} centro storico`}
                    className="w-full h-full object-cover object-center"
                  />
                ) : (
                  <div className="w-full h-full bg-[#C8956D]" />
                )}
              </div>
              <div className="rounded-xl overflow-hidden aspect-square">
                {['osimo', 'camerano', 'castelfidardo', 'varano', 'sirolo', 'campocavallo', 'staffolo', 'san-severino', 'tolentino', 'fiastra', 'sarnano', 'numana'].includes(borgo.slug) ? (
                  <img
                    src={`https://readdy.ai/api/search-image?query=$%7Bborgo.name%7D%20Marche%20Italy%20detail%20architecture%20stone%20arch%20doorway%20medieval%20texture%20warm%20light&width=400&height=400&seq=borgoGrid2${slug}&orientation=squarish`}
                    alt={`${borgo.name} dettaglio architettura`}
                    className="w-full h-full object-cover object-top"
                  />
                ) : (
                  <div className="w-full h-full bg-[#C8956D]" />
                )}
              </div>
              <div className="rounded-xl overflow-hidden aspect-square">
                {['osimo', 'camerano', 'castelfidardo', 'varano', 'sirolo', 'campocavallo', 'staffolo', 'san-severino', 'tolentino', 'fiastra', 'sarnano', 'numana'].includes(borgo.slug) ? (
                  <img
                    src={`https://readdy.ai/api/search-image?query=$%7Bborgo.name%7D%20Marche%20Italy%20landscape%20countryside%20view%20hills%20vineyards%20olive%20trees%20golden%20light&width=400&height=400&seq=borgoGrid3${slug}&orientation=squarish`}
                    alt={`${borgo.name} paesaggio`}
                    className="w-full h-full object-cover object-top"
                  />
                ) : (
                  <div className="w-full h-full bg-[#C8956D]" />
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── OSIMO ONLY: testo approfondito al posto della sezione generica ── */}
      {isOsimo && (
        <>
          {/* Intro + storia + griglia foto (sostituisce la sezione generica) */}
          <section
            ref={osimoParagraph.ref}
            className={`fade-in-up ${osimoParagraph.isVisible ? 'visible' : ''} max-w-7xl mx-auto px-6 py-20`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              {/* Testo */}
              <div>
                <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">La città</p>
                <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                  Osimo: la città dei sotterranei e delle colline marchigiane
                </h2>
                <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                  Adagiata su una delle dolci colline dell&apos;entroterra anconetano, a pochi chilometri dal mare e dalla Riviera del Conero, Osimo è una città d&apos;arte e di storia che unisce fascino monumentale, memoria antica e una posizione particolarmente favorevole tra costa e colline marchigiane. Più che un semplice borgo, è una città storica con un&apos;identità precisa, elegante e riconoscibile.
                </p>
                <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-8">
                  Antica Auximum, Osimo affonda le sue radici nel mondo piceno e romano. La sua posizione strategica, a controllo delle vie di transito fra l&apos;entroterra e il mare, ne ha fatto per secoli un centro importante, lasciando tracce visibili ancora oggi nel tessuto urbano, nei reperti archeologici e negli spazi monumentali del centro.
                </p>
                <div className="border-l-2 border-terracotta-200 pl-6 mb-6">
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Una storia che scorre in superficie e sottoterra</p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Osimo ha una particolarità che la rende unica: accanto alla città visibile, fatta di piazze, palazzi, chiese e scorci panoramici, esiste una città sotterranea composta da grotte e cunicoli scavati nell&apos;arenaria. Il patrimonio ipogeo è uno degli elementi più affascinanti del luogo e rappresenta una delle esperienze più distintive dell&apos;intera area.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Tra i percorsi più noti ci sono le Grotte del Cantinone, con un tratto visitabile di circa 300 metri sotto il centro storico, e le Grotte di Piazza Dante, note per gli altorilievi e le simbologie scolpite sulle pareti. Più che un semplice itinerario archeologico, i sotterranei di Osimo raccontano il lato più enigmatico e stratificato della città.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed">
                    In superficie, il cuore civico di Osimo ruota attorno a Piazza del Comune e Piazza Boccolino, che da secoli rappresentano il centro della vita sociale cittadina. Salendo verso Piazza del Duomo si incontra la Cattedrale di San Leopardo, la cui struttura attuale risale agli anni a cavallo tra XII e XIII secolo. Accanto alla cattedrale si trova il Battistero di San Giovanni Battista, che custodisce il monumentale fonte battesimale in bronzo realizzato nel 1629 dai fratelli Jacometti, considerato uno dei capolavori barocchi delle Marche.
                  </p>
                </div>
              </div>

              {/* Photo grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 rounded-xl overflow-hidden aspect-[16/9]">
                  <img
                    src={(borgo as typeof borgo & { gridImages?: string[] }).gridImages?.[0] ?? `https://readdy.ai/api/search-image?query=Osimo%20Marche%20Italy%20historic%20village%20stone%20buildings%20medieval%20architecture%20beautiful%20Italian%20landscape%20warm%20light&width=800&height=450&seq=borgoGrid1osimo&orientation=landscape`}
                    alt="Osimo centro storico"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <div className="rounded-xl overflow-hidden aspect-square">
                  <img
                    src={(borgo as typeof borgo & { gridImages?: string[] }).gridImages?.[1] ?? `https://readdy.ai/api/search-image?query=Osimo%20Marche%20Italy%20detail%20architecture%20stone%20arch%20doorway%20medieval%20texture%20warm%20light&width=400&height=400&seq=borgoGrid2osimo&orientation=squarish`}
                    alt="Osimo dettaglio architettura"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="rounded-xl overflow-hidden aspect-square">
                  <img
                    src={`https://static.readdy.ai/image/13e0fae89bbbd02d36954a6d83bda5fb/692cf2d92e2bde8b5be140ef634ea184.png`}
                    alt="Osimo paesaggio"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Qualità della vita */}
          <section
            ref={osimoLife.ref}
            className={`fade-in-up ${osimoLife.isVisible ? 'visible' : ''} bg-ivory-50 py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div>
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Vivere a Osimo</p>
                  <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                    La qualità della vita: tra silenzio e servizi
                  </h2>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                    Osimo conserva il fascino del centro storico ma non ha l&apos;aria di un luogo immobile: il corso cittadino continua a essere il fulcro della vita quotidiana, con locali, negozi e botteghe artigiane, mentre il sistema di itinerari, piazze e punti di interesse rende la città adatta sia alla visita sia a una dimensione abitativa stabile.
                  </p>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                    Anche la posizione è uno dei suoi punti forti. Il portale turistico ufficiale la descrive come una città situata in posizione strategica, a pochi chilometri dal mare e dalla Riviera del Conero, mentre dal centro storico si aprono vedute sulle colline marchigiane e verso la costa. Questo equilibrio tra entroterra e mare è uno degli aspetti che più definiscono la qualità del luogo.
                  </p>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed">
                    Sul piano immobiliare, Osimo resta interessante anche in termini di accessibilità. A marzo 2026 il prezzo medio richiesto per gli immobili residenziali in vendita era di 1.344 €/m², inferiore alla media della provincia di Ancona, pari a 1.602 €/m². Questo permette di raccontare Osimo come una realtà solida e ben posizionata, con valori ancora più contenuti rispetto alla media provinciale.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-5">
                  {[
                    { value: '1.344 €/m²', label: 'prezzo medio residenziale', icon: 'ri-home-line' },
                    { value: '18 km', label: 'da Ancona', icon: 'ri-road-map-line' },
                    { value: '< 20 min', label: 'dal mare Conero', icon: 'ri-sailboat-line' },
                    { value: 'A14', label: 'autostrada vicina', icon: 'ri-car-line' },
                  ].map((stat) => (
                    <div key={stat.label} className="p-6 bg-white rounded-2xl border border-ivory-200 flex flex-col gap-3">
                      <div className="w-10 h-10 flex items-center justify-center text-terracotta-300">
                        <i className={`${stat.icon} text-2xl`}></i>
                      </div>
                      <p className="font-serif text-3xl text-anthracite-800 font-light">{stat.value}</p>
                      <p className="font-sans text-xs text-anthracite-400 tracking-wide uppercase">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Cosa vedere */}
          <section
            ref={osimoAttractions.ref}
            className={`fade-in-up ${osimoAttractions.isVisible ? 'visible' : ''} py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Esperienze</p>
              <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-12 leading-snug">
                Cosa vedere e vivere a Osimo
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {OSIMO_ATTRACTIONS.map((attr) => (
                  <article key={attr.title} className="bg-ivory-50 rounded-2xl p-7 border border-ivory-200 flex flex-col gap-4">
                    <div className="w-11 h-11 flex items-center justify-center text-terracotta-300 bg-white rounded-xl border border-ivory-200">
                      <i className={`${attr.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-serif text-lg text-anthracite-800 font-medium leading-snug">{attr.title}</h3>
                    <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{attr.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* Fonti Osimo */}
          <section className="max-w-7xl mx-auto px-6 pb-2 pt-0">
            <div className="border-l-2 border-ivory-200 pl-6">
              <p className="font-sans text-xs tracking-widest uppercase text-anthracite-400 mb-4">Fonti</p>
              <ul className="flex flex-col gap-2">
                {[
                  { label: 'osimoturismo.it', url: 'https://www.osimoturismo.it/home/' },
                ].map((source) => (
                  <li key={source.url} className="flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-external-link-line text-xs text-terracotta-300"></i>
                    </div>
                    <a
                      href={source.url}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors leading-relaxed break-all cursor-pointer"
                    >
                      {source.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Video Osimo */}
          <section className="max-w-7xl mx-auto px-6 pt-10 pb-20">
            <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Video</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="max-w-sm rounded-2xl overflow-hidden border border-ivory-200 aspect-[9/16]">
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/RmF7HSsxY-k"
                  title="Video Osimo 1"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
              <div className="max-w-sm rounded-2xl overflow-hidden border border-ivory-200 aspect-[9/16]">
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/VxHyLCqFp3g"
                  title="Video Osimo 2"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
              <div className="max-w-sm rounded-2xl overflow-hidden border border-ivory-200 aspect-[9/16]">
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/aht_o1uHPmU"
                  title="Video Osimo 3"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
              <div className="max-w-sm rounded-2xl overflow-hidden border border-ivory-200 aspect-[9/16]">
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/RrR3ZxnsK20"
                  title="Video Osimo 4"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
            </div>
          </section>
        </>
      )}

      {/* ── CAMERANO ONLY ── */}
      {isCamerano && (
        <>
          {/* Intro + storia + grotte */}
          <section
            ref={cameranoParagraph.ref}
            className={`fade-in-up ${cameranoParagraph.isVisible ? 'visible' : ''} max-w-7xl mx-auto px-6 py-20`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              {/* Testo */}
              <div>
                <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Il borgo</p>
                <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                  Camerano: il borgo delle grotte, del Rosso Conero e delle colline del Conero
                </h2>
                <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                  Camerano è uno dei centri più interessanti del territorio del Conero: un piccolo comune dell&apos;entroterra anconetano, immerso nel paesaggio collinare del Parco Regionale del Conero, a breve distanza dalla costa e da Ancona. È un luogo che unisce natura, storia e tradizione enogastronomica, con un&apos;identità fortemente legata sia al suo straordinario patrimonio ipogeo sia alla cultura del vino.
                </p>
                <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                  La sua immagine più riconoscibile è quella della città sotterranea: sotto il centro storico si sviluppa una rete di ambienti ipogei e cunicoli scavati dall&apos;uomo, principalmente nell&apos;arenaria, che occupano il sottosuolo dell&apos;area compresa tra Piazza Roma e le vie Maratti e San Francesco. Le grotte rappresentano oggi uno dei simboli più forti del borgo e una delle esperienze più particolari dell&apos;intero comprensorio del Conero.
                </p>
                <div className="border-l-2 border-terracotta-200 pl-6 mb-8">
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Le grotte e la storia silenziosa</p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Le Grotte di Camerano non hanno ancora un&apos;origine storica certa. Le fonti disponibili spiegano che non esistono documenti in grado di fissarne con precisione la nascita, e che la data più antica leggibile all&apos;interno dell&apos;ipogeo, 1327, non può essere considerata la data di fondazione. Gli studi e le ipotesi raccolte nel tempo fanno pensare a un complesso cresciuto e trasformato in epoche diverse, con riusi successivi e funzioni differenti.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Quello che sappiamo con maggiore solidità è che l&apos;ipogeo ha avuto una funzione di rifugio per la popolazione in età medievale e che fu nuovamente utilizzato come ricovero nel luglio del 1944, durante la Seconda guerra mondiale. Oggi il percorso visitabile mostra una rete labirintica di sale e corridoi che, oltre all&apos;aspetto funzionale, presenta fregi, decorazioni, simboli religiosi e ambienti di forte suggestione.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed">
                    In superficie, il centro storico si raccoglie intorno a Piazza Roma, da cui si sviluppano i principali percorsi del borgo. Poco sopra si incontra la Chiesa di San Francesco, fondata nel XIII secolo e rimaneggiata nel Settecento, con facciata in cotto, campanile slanciato e interni decorati.
                  </p>
                </div>
                <div className="border-l-2 border-terracotta-200 pl-6">
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Il Rosso Conero e la vocazione enogastronomica</p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Camerano è strettamente legato al Rosso Conero DOC, una delle denominazioni simbolo della zona. Il disciplinare ufficiale include l&apos;intero territorio comunale di Camerano nell&apos;area di produzione del vino, ottenuto principalmente da Montepulciano, con eventuale presenza di Sangiovese entro i limiti previsti.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed">
                    Il legame con il vino non è solo produttivo ma anche identitario: il territorio di Camerano è da anni associato al Rosso Conero anche nella promozione locale e nelle manifestazioni dedicate.
                  </p>
                </div>
              </div>

              {/* Photo grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 rounded-xl overflow-hidden aspect-[16/9]">
                  <img
                    src="https://static.readdy.ai/image/13e0fae89bbbd02d36954a6d83bda5fb/06b5ff262890281e02c238c55fb691d0.png"
                    alt="Camerano centro storico"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <div className="rounded-xl overflow-hidden aspect-square">
                  <img
                    src="https://static.readdy.ai/image/13e0fae89bbbd02d36954a6d83bda5fb/6d0668206086acb025f1c30ca8f31d9f.png"
                    alt="Grotte di Camerano"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="rounded-xl overflow-hidden aspect-square">
                  <img
                    src="https://static.readdy.ai/image/13e0fae89bbbd02d36954a6d83bda5fb/ed73113bcf67655755c6213ee3bedd9e.png"
                    alt="Vigneti Rosso Conero"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Vivere a Camerano */}
          <section
            ref={cameranoLife.ref}
            className={`fade-in-up ${cameranoLife.isVisible ? 'visible' : ''} bg-ivory-50 py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div>
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Vivere a Camerano</p>
                  <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                    Posizione, mercato, opportunità
                  </h2>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                    Uno dei punti di forza di Camerano è la posizione. Il borgo si trova all&apos;interno del sistema territoriale del Parco del Conero, che comprende i comuni di Ancona, Camerano, Numana e Sirolo, e consente di vivere una dimensione più raccolta e tranquilla restando vicini sia alla costa sia al capoluogo.
                  </p>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                    Anche sul piano immobiliare Camerano ha un profilo interessante. A marzo 2026 il prezzo medio richiesto per gli immobili residenziali in vendita era di 1.523 €/m², leggermente inferiore alla media della provincia di Ancona, pari a 1.602 €/m².
                  </p>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed">
                    Per chi viaggia spesso, anche l&apos;aeroporto delle Marche resta relativamente vicino: la distanza stradale tra Camerano e l&apos;aeroporto di Ancona-Falconara è di circa 24,3 km.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-5">
                  {CAMERANO_STATS.map((stat) => (
                    <div key={stat.label} className="p-6 bg-white rounded-2xl border border-ivory-200 flex flex-col gap-3">
                      <div className="w-10 h-10 flex items-center justify-center text-terracotta-300">
                        <i className={`${stat.icon} text-2xl`}></i>
                      </div>
                      <p className="font-serif text-2xl text-anthracite-800 font-light">{stat.value}</p>
                      <p className="font-sans text-xs text-anthracite-400 tracking-wide uppercase">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Cosa vedere */}
          <section
            ref={cameranoAttractions.ref}
            className={`fade-in-up ${cameranoAttractions.isVisible ? 'visible' : ''} py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Esperienze</p>
              <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-12 leading-snug">
                Cosa vedere e vivere a Camerano
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {CAMERANO_ATTRACTIONS.map((attr) => (
                  <article key={attr.title} className="bg-ivory-50 rounded-2xl p-7 border border-ivory-200 flex flex-col gap-4">
                    <div className="w-11 h-11 flex items-center justify-center text-terracotta-300 bg-white rounded-xl border border-ivory-200">
                      <i className={`${attr.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-serif text-lg text-anthracite-800 font-medium leading-snug">{attr.title}</h3>
                    <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{attr.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* Fonti Camerano */}
          <section className="max-w-7xl mx-auto px-6 pb-2 pt-0">
            <div className="border-l-2 border-ivory-200 pl-6">
              <p className="font-sans text-xs tracking-widest uppercase text-anthracite-400 mb-4">Fonti</p>
              <ul className="flex flex-col gap-2">
                {[
                  { label: 'comune.camerano.an.it — Luoghi', url: 'https://comune.camerano.an.it/vivere-il-comune/luoghi/' },
                  { label: 'operaturismo.it — Grotte di Camerano', url: 'https://www.operaturismo.it/grottedicamerano/' },
                  { label: 'marcheoutdoor.it — Grotte di Camerano', url: 'https://www.marcheoutdoor.it/PuntiDiInteresse/Dettagli/grotte_di_camerano' },
                  { label: 'parcodelconero.org', url: 'https://www.parcodelconero.org/lente/' },
                  { label: 'catalogoviti.politicheagricole.it — Rosso Conero DOC', url: 'https://catalogoviti.politicheagricole.it/scheda_denom.php?q=2248&t=dsc' },
                  { label: 'gazzettaufficiale.it — Disciplinare Rosso Conero', url: 'https://www.gazzettaufficiale.it/atto/serie_generale/caricaArticolo?art.codiceRedazionale=089A373' },
                ].map((source) => (
                  <li key={source.url} className="flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-external-link-line text-xs text-terracotta-300"></i>
                    </div>
                    <a
                      href={source.url}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors leading-relaxed break-all cursor-pointer"
                    >
                      {source.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Video Camerano */}
          <section className="max-w-7xl mx-auto px-6 pt-10 pb-20">
            <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Video</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="max-w-sm rounded-2xl overflow-hidden border border-ivory-200 aspect-[9/16]">
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/tdElWCwsFBg"
                  title="Video Camerano 1"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
              <div className="max-w-sm rounded-2xl overflow-hidden border border-ivory-200 aspect-[9/16]">
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/J4UZMrVcMZ8"
                  title="Video Camerano 2"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
              <div className="max-w-sm rounded-2xl overflow-hidden border border-ivory-200 aspect-[9/16]">
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/WPOYs0rksYI"
                  title="Video Camerano 3"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
              <div className="max-w-sm rounded-2xl overflow-hidden border border-ivory-200 aspect-[9/16]">
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/JRZGRPHivM4"
                  title="Video Camerano 4"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
            </div>
          </section>
        </>
      )}

      {/* ── CASTELFIDARDO ONLY ── */}
      {isCastelfidardo && (
        <>
          {/* Intro + storia */}
          <section
            ref={castelfidardoParagraph.ref}
            className={`fade-in-up ${castelfidardoParagraph.isVisible ? 'visible' : ''} max-w-7xl mx-auto px-6 py-20`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div>
                <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Il borgo</p>
                <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                  Castelfidardo: la città della fisarmonica e della memoria risorgimentale
                </h2>
                <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                  Castelfidardo è una città collinare dell&apos;entroterra anconetano, conosciuta in tutto il mondo come la patria della fisarmonica. Il suo nome è legato da oltre un secolo e mezzo alla produzione di questo strumento, ma il suo carattere non si esaurisce nella dimensione musicale: Castelfidardo unisce tradizione artigianale, memoria storica e paesaggio marchigiano in un equilibrio molto riconoscibile.
                </p>
                <div className="border-l-2 border-terracotta-200 pl-6 mb-8">
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">La fisarmonica: un&apos;identità unica</p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    La fisarmonica non nacque a Castelfidardo: la sua invenzione viene comunemente attribuita al viennese Cyrill Demian nel 1829. A Castelfidardo, però, si sviluppò una delle sue storie industriali più importanti, grazie a Paolo Soprani, ricordato come il fondatore dell&apos;industria della fisarmonica locale. La data chiave è il 1863, anno da cui si fa partire la prima grande esperienza produttiva italiana legata allo strumento.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Questa identità è raccontata dal Museo Internazionale della Fisarmonica, inaugurato il 9 maggio 1981 e ospitato nel Palazzo Municipale in Piazza della Repubblica. Il museo conserva strumenti rari, documenti, fotografie e registrazioni che illustrano l&apos;evoluzione tecnica ed estetica della fisarmonica e il rapporto fra ingegneria, artigianato e suono.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed">
                    Ogni settembre Castelfidardo ospita il Premio Internazionale della Fisarmonica (PIF), la competizione più importante al mondo dedicata allo strumento simbolo della città. Concerti, masterclass, incontri e mostre trasformano il centro in un palcoscenico internazionale.
                  </p>
                </div>
                <div className="border-l-2 border-terracotta-200 pl-6">
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">La Battaglia di Castelfidardo e i luoghi della memoria</p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Il 18 settembre 1860 Castelfidardo fu teatro di una delle battaglie decisive del Risorgimento. Le truppe sardo-piemontesi del generale Cialdini sconfissero le forze pontificie guidate dal generale de Lamoricière, in uno scontro che contribuì in modo decisivo all&apos;unione delle Marche al resto della penisola.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed">
                    Tra i luoghi più significativi della memoria storica c&apos;è l&apos;Ossario-Sacrario dei caduti della battaglia, eretto ai margini meridionali della Selva di Castelfidardo. Accanto a questo, la città conserva il Monumento Nazionale delle Marche &quot;Ai Vittoriosi&quot;, completato nel 1912, uno dei simboli monumentali più forti del paesaggio fidardense.
                  </p>
                </div>
              </div>

              {/* Photo grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 rounded-xl overflow-hidden aspect-[16/9]">
                  <img
                    src="https://static.readdy.ai/image/13e0fae89bbbd02d36954a6d83bda5fb/0956bcc31ad4283fcbb2df5ef0ab4903.png"
                    alt="Castelfidardo centro storico"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="rounded-xl overflow-hidden aspect-square">
                  <img
                    src="https://readdy.ai/api/search-image?query=accordion%20fisarmonica%20handcraft%20workshop%20artisan%20Castelfidardo%20Italy%20traditional%20instrument%20making%20warm%20light%20wooden%20details&width=400&height=400&seq=castGrid2&orientation=squarish"
                    alt="Castelfidardo fisarmonica artigianato"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="rounded-xl overflow-hidden aspect-square">
                  <img
                    src="https://static.readdy.ai/image/13e0fae89bbbd02d36954a6d83bda5fb/33a8656d90c8543e2c4a1d3aa1bf40af.png"
                    alt="Castelfidardo paesaggio collinare"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Vivere a Castelfidardo */}
          <section
            ref={castelfidardoLife.ref}
            className={`fade-in-up ${castelfidardoLife.isVisible ? 'visible' : ''} bg-ivory-50 py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div>
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Vivere a Castelfidardo</p>
                  <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                    Posizione, paesaggio e mercato immobiliare
                  </h2>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                    Castelfidardo gode di una posizione favorevole tra colline e costa. Si trova a circa 19 km da Ancona su strada e a circa 12-13 km da Sirolo, quindi in prossimità della Riviera del Conero, mantenendo però una dimensione più raccolta rispetto alle località costiere. Il portale turistico comunale richiama belvederi, parchi, la Selva di Castelfidardo e panorami che si aprono fino al mare.
                  </p>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed">
                    Sul piano immobiliare, Castelfidardo presenta valori medi inferiori alla media provinciale. A marzo 2026 il prezzo medio richiesto per gli immobili residenziali in vendita era di 1.395 €/m², contro una media della provincia di Ancona pari a 1.602 €/m². Una realtà interessante per chi cerca un contesto ben collegato con valori più contenuti rispetto ad altre zone della provincia.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-5">
                  {CASTELFIDARDO_STATS.map((stat) => (
                    <div key={stat.label} className="p-6 bg-white rounded-2xl border border-ivory-200 flex flex-col gap-3">
                      <div className="w-10 h-10 flex items-center justify-center text-terracotta-300">
                        <i className={`${stat.icon} text-2xl`}></i>
                      </div>
                      <p className="font-serif text-2xl text-anthracite-800 font-light">{stat.value}</p>
                      <p className="font-sans text-xs text-anthracite-400 tracking-wide uppercase">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Cosa vedere */}
          <section
            ref={castelfidardoAttractions.ref}
            className={`fade-in-up ${castelfidardoAttractions.isVisible ? 'visible' : ''} py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Esperienze</p>
              <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-12 leading-snug">
                Cosa vedere e vivere a Castelfidardo
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {CASTELFIDARDO_ATTRACTIONS.map((attr) => (
                  <article key={attr.title} className="bg-ivory-50 rounded-2xl p-7 border border-ivory-200 flex flex-col gap-4">
                    <div className="w-11 h-11 flex items-center justify-center text-terracotta-300 bg-white rounded-xl border border-ivory-200">
                      <i className={`${attr.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-serif text-lg text-anthracite-800 font-medium leading-snug">{attr.title}</h3>
                    <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{attr.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* Fonti Castelfidardo */}
          <section className="max-w-7xl mx-auto px-6 pb-2 pt-0">
            <div className="border-l-2 border-ivory-200 pl-6">
              <p className="font-sans text-xs tracking-widest uppercase text-anthracite-400 mb-4">Fonti</p>
              <ul className="flex flex-col gap-2">
                {[
                  { label: 'visitcastelfidardo.it', url: 'https://www.visitcastelfidardo.it/', sub: [
                    { label: 'La fisarmonica', url: 'https://www.visitcastelfidardo.it/fisarmonica/' },
                    { label: 'PIF', url: 'https://www.visitcastelfidardo.it/pif/' },
                    { label: 'Palazzo Municipale', url: 'https://www.visitcastelfidardo.it/palazzo-municipale/' },
                    { label: 'Busto a Paolo Soprani', url: 'https://www.visitcastelfidardo.it/busto-a-paolo-soprani/' },
                    { label: 'Monumento Nazionale delle Marche', url: 'https://www.visitcastelfidardo.it/monumento-nazionale-delle-marche/' },
                    { label: 'Sacrario-Ossario', url: 'https://www.visitcastelfidardo.it/sacrario-ossario/' },
                    { label: 'Chiesa di Sant\'Agostino', url: 'https://www.visitcastelfidardo.it/chiesa-sant-agostino/' },
                    { label: 'Belvederi', url: 'https://www.visitcastelfidardo.it/belvederi/' },
                  ]},
                  { label: 'museodellafisarmonica.it', url: 'https://www.museodellafisarmonica.it/' },
                  { label: 'museodelrisorgimentocastelfidardo.it — Ossario-Sacrario', url: 'https://www.museodelrisorgimentocastelfidardo.it/il-museo-diffuso/ossario-sacrario/' },
                  { label: 'comune.castelfidardo.an.it — Museo Internazionale della Fisarmonica', url: 'https://www.comune.castelfidardo.an.it/it/vivere/museo-internazionale-della-fisarmonica' },
                  { label: 'treccani.it — Fisarmonica', url: 'https://www.treccani.it/enciclopedia/fisarmonica/' },
                  { label: 'italia.it — Castelfidardo', url: 'https://www.italia.it/en/marche/ancona/castelfidardo/castelfidardo' },
                  { label: 'immobiliare.it — Mercato Castelfidardo', url: 'https://www.immobiliare.it/mercato-immobiliare/marche/castelfidardo/' },
                ].map((source) => (
                  <li key={source.url} className="flex flex-col gap-1">
                    <div className="flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <i className="ri-external-link-line text-xs text-terracotta-300"></i>
                      </div>
                      <a
                        href={source.url}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors leading-relaxed break-all cursor-pointer"
                      >
                        {source.label}
                      </a>
                    </div>
                    {'sub' in source && source.sub && (
                      <ul className="ml-6 flex flex-col gap-1">
                        {source.sub.map((sub) => (
                          <li key={sub.url} className="flex items-start gap-2">
                            <div className="w-3 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                              <i className="ri-corner-down-right-line text-xs text-anthracite-300"></i>
                            </div>
                            <a
                              href={sub.url}
                              target="_blank"
                              rel="nofollow noopener noreferrer"
                              className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors leading-relaxed break-all cursor-pointer"
                            >
                              {sub.label}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </>
      )}

      {/* ── SIROLO ONLY ── */}
      {isSirolo && (
        <>
          {/* Intro + storia + paesaggio */}
          <section
            ref={siroloParagraph.ref}
            className={`fade-in-up ${siroloParagraph.isVisible ? 'visible' : ''} max-w-7xl mx-auto px-6 py-20`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div>
                <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Il borgo</p>
                <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                  Sirolo: il gioiello del Conero tra mare e roccia
                </h2>
                <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                  Sirolo è uno dei luoghi più iconici della Riviera del Conero: un borgo storico affacciato sul mare Adriatico, immerso nel Parco Regionale del Conero, dove il paesaggio unisce falesie bianche, spiagge di ghiaia, macchia mediterranea e uno dei tratti costieri più spettacolari delle Marche.
                </p>
                <div className="border-l-2 border-terracotta-200 pl-6 mb-8">
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Il centro storico e il Parco del Conero</p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Il centro storico conserva il fascino del borgo antico, con vicoli, archi, scorci in pietra e una piazzetta panoramica che rappresenta il cuore del paese. Tra i riferimenti architettonici principali c&apos;è la Chiesa di San Nicola di Bari, situata nella piazza principale e riconosciuta come il luogo di culto più significativo del borgo. La sua architettura neoclassica e la posizione centrale la rendono uno dei simboli di Sirolo.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Sirolo si trova nel cuore del Parco del Conero, un&apos;area protetta dove la costa alta incontra il paesaggio collinare dell&apos;entroterra. Il promontorio del Conero, i sentieri immersi nella vegetazione mediterranea e le aperture panoramiche sul mare definiscono l&apos;identità naturale del luogo.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed">
                    Le spiagge più note sono Urbani, San Michele e Due Sorelle. La Spiaggia delle Due Sorelle, simbolo assoluto del Conero, è oggi raggiungibile solo via mare, mentre Spiaggia Urbani e San Michele rappresentano due degli arenili più amati del territorio.
                  </p>
                </div>
                <div className="border-l-2 border-terracotta-200 pl-6">
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Cosa fare a Sirolo</p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Il Parco del Conero offre una rete di 18 sentieri percorribili a piedi, a cavallo o in mountain bike, tra boschi, falesie e punti panoramici sul mare. Dal borgo si può raggiungere la costa per vivere il Conero da prospettive diverse: a piedi verso le spiagge più vicine, oppure via mare per scoprire la Spiaggia delle Due Sorelle e il profilo roccioso del promontorio dall&apos;acqua.
                  </p>
                </div>
              </div>

              {/* Photo grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 rounded-xl overflow-hidden aspect-[16/9]">
                  <img
                    src="https://public.readdy.ai/ai/img_res/edited_d188c810795cc5dc5ed6f10396948511_94bd5cbf.jpg"
                    alt="Sirolo vista sul mare del Conero"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="rounded-xl overflow-hidden aspect-square">
                  <img
                    src="https://public.readdy.ai/ai/img_res/edited_2e1c864f6d515838f178dc7ed5c54d60_6161e52c.jpg"
                    alt="Sirolo centro storico medievale"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="rounded-xl overflow-hidden aspect-square">
                  <img
                    src="https://static.readdy.ai/image/13e0fae89bbbd02d36954a6d83bda5fb/fa941b37bf3e2717d51df726f7f5b207.webp"
                    alt="Spiaggia delle Due Sorelle Sirolo"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Vivere a Sirolo */}
          <section
            ref={siroloLife.ref}
            className={`fade-in-up ${siroloLife.isVisible ? 'visible' : ''} bg-ivory-50 py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div>
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Vivere a Sirolo</p>
                  <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                    Posizione, mercato e qualità della vita
                  </h2>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                    Sirolo conta poco più di 4.000 abitanti e mantiene una dimensione raccolta pur essendo una delle località più riconoscibili del litorale marchigiano. La vicinanza ad Ancona, raggiungibile in circa 18 chilometri su strada, consente di vivere in un contesto paesaggisticamente straordinario senza rinunciare al collegamento con il capoluogo e con i principali servizi.
                  </p>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed">
                    Anche il profilo immobiliare conferma il posizionamento alto della località. A marzo 2026 il prezzo medio richiesto per gli immobili residenziali in vendita a Sirolo era di 2.890 €/m², ben al di sopra della media della provincia di Ancona, pari a 1.602 €/m². Questo rende Sirolo una delle realtà più forti e ricercate del mercato immobiliare marchigiano.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-5">
                  {SIROLO_STATS.map((stat) => (
                    <div key={stat.label} className="p-6 bg-white rounded-2xl border border-ivory-200 flex flex-col gap-3">
                      <div className="w-10 h-10 flex items-center justify-center text-terracotta-300">
                        <i className={`${stat.icon} text-2xl`}></i>
                      </div>
                      <p className="font-serif text-2xl text-anthracite-800 font-light">{stat.value}</p>
                      <p className="font-sans text-xs text-anthracite-400 tracking-wide uppercase">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Cosa vedere */}
          <section
            ref={siroloAttractions.ref}
            className={`fade-in-up ${siroloAttractions.isVisible ? 'visible' : ''} py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Esperienze</p>
              <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-12 leading-snug">
                Cosa vedere e vivere a Sirolo
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {SIROLO_ATTRACTIONS.map((attr) => (
                  <article key={attr.title} className="bg-ivory-50 rounded-2xl p-7 border border-ivory-200 flex flex-col gap-4">
                    <div className="w-11 h-11 flex items-center justify-center text-terracotta-300 bg-white rounded-xl border border-ivory-200">
                      <i className={`${attr.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-serif text-lg text-anthracite-800 font-medium leading-snug">{attr.title}</h3>
                    <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{attr.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* Fonti Sirolo */}
          <section className="max-w-7xl mx-auto px-6 pb-2 pt-0">
            <div className="border-l-2 border-ivory-200 pl-6">
              <p className="font-sans text-xs tracking-widest uppercase text-anthracite-400 mb-4">Fonti</p>
              <ul className="flex flex-col gap-2">
                {([
                  { label: 'turismosirolo.it', url: 'https://www.turismosirolo.it/', sub: [
                    { label: 'Centro storico', url: 'https://www.turismosirolo.it/scopri-sirolo/centro-storico' },
                    { label: 'Chiesa di San Nicola di Bari', url: 'https://www.turismosirolo.it/scopri-sirolo/chiesa-san-nicola-di-bari' },
                    { label: 'I sentieri del Conero', url: 'https://www.turismosirolo.it/scopri-sirolo/i-sentieri-del-conero' },
                    { label: 'Spiaggia delle Due Sorelle', url: 'https://www.turismosirolo.it/spiagge-sirolo/spiaggia-delle-due-sorelle' },
                  ]},
                  { label: 'parcodelconero.org — Sentieri', url: 'https://www.parcodelconero.org/sentieri/' },
                  { label: 'tuttitalia.it — Sirolo', url: 'https://www.tuttitalia.it/marche/57-sirolo/' },
                ] as Array<{ label: string; url: string; sub?: Array<{ label: string; url: string }> }>).map((source) => (
                  <li key={source.url} className="flex flex-col gap-1">
                    <div className="flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <i className="ri-external-link-line text-xs text-terracotta-300"></i>
                      </div>
                      <a
                        href={source.url}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors leading-relaxed break-all cursor-pointer"
                      >
                        {source.label}
                      </a>
                    </div>
                    {source.sub && (
                      <ul className="ml-6 flex flex-col gap-1">
                        {source.sub.map((sub) => (
                          <li key={sub.url} className="flex items-start gap-2">
                            <div className="w-3 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                              <i className="ri-corner-down-right-line text-xs text-anthracite-300"></i>
                            </div>
                            <a
                              href={sub.url}
                              target="_blank"
                              rel="nofollow noopener noreferrer"
                              className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors leading-relaxed break-all cursor-pointer"
                            >
                              {sub.label}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </>
      )}

      {/* ── VARANO ONLY ── */}
      {isVarano && (
        <>
          {/* Intro + paesaggio rurale */}
          <section
            ref={siroloParagraph.ref}
            className={`fade-in-up ${siroloParagraph.isVisible ? 'visible' : ''} max-w-7xl mx-auto px-6 py-20`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div>
                <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Il borgo</p>
                <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                  Varano: quiete rurale tra Ancona e il Conero
                </h2>
                <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                  Varano è una frazione del Comune di Ancona, inserita tra gli insediamenti storici collinari del territorio comunale e nel paesaggio del Conero. Più che un borgo isolato dell&apos;entroterra, è una località raccolta e silenziosa, dove la campagna resta protagonista pur trovandosi a pochi minuti dalla città e dalla costa.
                </p>
                <div className="border-l-2 border-terracotta-200 pl-6 mb-8">
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Un paesaggio rurale ancora vivo</p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Qui il paesaggio ha ancora un carattere rurale molto riconoscibile: strade di campagna, case coloniche, campi coltivati e una vocazione agricola che si legge ancora oggi nella presenza di aziende vitivinicole, oleifici e produzioni locali tra Varano e Angeli di Varano. È una parte del territorio anconetano in cui la dimensione agricola non è solo memoria, ma continua a essere un elemento vivo dell&apos;identità del luogo.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed">
                    Varano è adatta a chi cerca un contesto più appartato senza allontanarsi davvero dai servizi. La vicinanza ad Ancona e al sistema del Conero permette di vivere in una dimensione più lenta, ma con collegamenti rapidi verso il capoluogo, il mare e i principali percorsi naturalistici dell&apos;area protetta.
                  </p>
                </div>
                <div className="border-l-2 border-terracotta-200 pl-6">
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Posizione e mercato immobiliare</p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    La distanza da Ancona è di circa 6 km, mentre Portonovo si trova a circa 3 km. Varano non è una zona &quot;low cost&quot; della campagna marchigiana, ma un&apos;area residenziale e rurale ricercata. A marzo 2026 il prezzo medio richiesto per gli immobili residenziali in vendita nella zona Varano di Ancona era di 2.446 €/m², superiore sia alla media del Comune di Ancona (1.756 €/m²) sia a quella della provincia di Ancona (1.602 €/m²).
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed">
                    Questo riflette il valore della posizione, del verde circostante e della vicinanza al Conero. Il Parco del Conero, con i suoi 18 sentieri percorribili a piedi, a cavallo o in mountain bike, è parte integrante di questo equilibrio tra natura e accessibilità.
                  </p>
                </div>
              </div>

              {/* Photo grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 rounded-xl overflow-hidden aspect-[16/9]">
                  <img
                    src="https://static.readdy.ai/image/13e0fae89bbbd02d36954a6d83bda5fb/26e1184b1347038814c22418e63abccd.webp"
                    alt="Varano colline marchigiane"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Vivere a Varano */}
          <section
            ref={siroloLife.ref}
            className={`fade-in-up ${siroloLife.isVisible ? 'visible' : ''} bg-ivory-50 py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div>
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Vivere a Varano</p>
                  <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                    Posizione, accessibilità e qualità della vita
                  </h2>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                    Uno dei punti di forza di Varano è la posizione. La distanza da Ancona è di circa 6 km, mentre Portonovo si trova a circa 3 km: numeri che spiegano bene come questa frazione riesca a coniugare quiete rurale e prossimità alla città e al mare.
                  </p>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed">
                    Il contesto è ideale per chi cerca spazi aperti, verde e un rapporto più diretto con il paesaggio, senza rinunciare alla praticità. Il Parco del Conero, con i suoi 18 sentieri percorribili a piedi, a cavallo o in mountain bike, è parte integrante di questo equilibrio tra natura e accessibilità.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-5">
                  {[
                    { value: '6 km', label: 'da Ancona', icon: 'ri-road-map-line' },
                    { value: '3 km', label: 'da Portonovo', icon: 'ri-sailboat-line' },
                    { value: 'Parco Conero', label: 'area naturale di riferimento', icon: 'ri-leaf-line' },
                    { value: '2.446 €/m²', label: 'prezzo medio richiesto in vendita, marzo 2026', icon: 'ri-home-line' },
                  ].map((stat) => (
                    <div key={stat.label} className="p-6 bg-white rounded-2xl border border-ivory-200 flex flex-col gap-3">
                      <div className="w-10 h-10 flex items-center justify-center text-terracotta-300">
                        <i className={`${stat.icon} text-2xl`}></i>
                      </div>
                      <p className="font-serif text-2xl text-anthracite-800 font-light">{stat.value}</p>
                      <p className="font-sans text-xs text-anthracite-400 tracking-wide uppercase">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Esperienze Varano */}
          <section className="py-20 px-6">
            <div className="max-w-7xl mx-auto">
              <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Esperienze</p>
              <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-12 leading-snug">
                Cosa vedere e vivere a Varano
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
                {[
                  {
                    icon: 'ri-landscape-line',
                    title: 'Paesaggio rurale del Conero',
                    text: 'Varano conserva un rapporto diretto con la campagna, tra case sparse, coltivazioni e una dimensione più raccolta rispetto ai centri costieri e urbani.',
                  },
                  {
                    icon: 'ri-goblet-line',
                    title: 'Vino, olio e produzioni locali',
                    text: 'Tra Varano e Angeli di Varano sono presenti aziende agricole, cantine e oleifici che raccontano la continuità della tradizione produttiva locale.',
                  },
                  {
                    icon: 'ri-leaf-line',
                    title: 'Accesso rapido al Conero',
                    text: 'La vicinanza a Portonovo e ai sentieri del Parco rende Varano una base interessante per chi vuole vivere il versante più naturale della costa anconetana.',
                  },
                  {
                    icon: 'ri-building-line',
                    title: 'Vicinanza ad Ancona',
                    text: 'La frazione è abbastanza vicina al capoluogo da permettere una quotidianità comoda, pur mantenendo un\'atmosfera molto diversa da quella urbana.',
                  },
                ].map((attr) => (
                  <article key={attr.title} className="bg-ivory-50 rounded-2xl p-7 border border-ivory-200 flex flex-col gap-4">
                    <div className="w-11 h-11 flex items-center justify-center text-terracotta-300 bg-white rounded-xl border border-ivory-200">
                      <i className={`${attr.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-serif text-lg text-anthracite-800 font-medium leading-snug">{attr.title}</h3>
                    <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{attr.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* Fonti Varano */}
          <section className="max-w-7xl mx-auto px-6 pb-2 pt-0">
            <div className="border-l-2 border-ivory-200 pl-6">
              <p className="font-sans text-xs tracking-widest uppercase text-anthracite-400 mb-4">Fonti</p>
              <ul className="flex flex-col gap-2">
                {([
                  { label: 'comuneancona.it — Stradario Ufficiale', url: 'https://www.comuneancona.it/wp-content/uploads/2019/11/AllegatoA-Stradario-UfficialeA-GGIORNATO-01.10.2020.pdf' },
                  { label: 'comuneancona.it — Norme Tecniche di Attuazione', url: 'https://www.comuneancona.it/ankonline/urbanistica/wp-content/uploads/sites/7/2017/03/El.3_NTA.pdf' },
                  { label: 'parcodelconero.org', url: 'https://www.parcodelconero.org/', sub: [
                    { label: 'Sentieri', url: 'https://www.parcodelconero.org/sentieri/' },
                  ]},
                  { label: 'parks.it — Varano nel Parco del Conero', url: 'https://www.parks.it/parco.conero/fedenatura/varano.html' },
                  { label: 'parks.it — Prodotti tipici: Verdevita', url: 'https://www.parks.it/parco.conero/prodotti.tipici/agricoltura/verdevita.html' },
                  { label: 'rivieradelconero.info', url: 'https://www.rivieradelconero.info/', sub: [
                    { label: 'Gli oleifici della Riviera del Conero', url: 'https://www.rivieradelconero.info/it/gli-oleifici-della-riviera-del-conero/' },
                    { label: 'Le cantine vitivinicole della Riviera del Conero', url: 'https://www.rivieradelconero.info/it/le-cantine-vitivinicole-della-riviera-del-conero/' },
                  ]},
                  { label: 'rome2rio.com — Ancona → Varano', url: 'https://www.rome2rio.com/it/s/Ancona/Varano' },
                  { label: 'rome2rio.com — Varano → Portonovo', url: 'https://www.rome2rio.com/it/s/Varano/Portonovo-Marche-Italia' },
                  { label: 'immobiliare.it', url: 'https://www.immobiliare.it/', sub: [
                    { label: 'Mercato Varano (Ancona)', url: 'https://www.immobiliare.it/mercato-immobiliare/marche/ancona/varano/' },
                    { label: 'Mercato Comune di Ancona', url: 'https://www.immobiliare.it/mercato-immobiliare/marche/ancona/' },
                    { label: 'Mercato Provincia di Ancona', url: 'https://www.immobiliare.it/mercato-immobiliare/marche/ancona-provincia/' },
                  ]},
                ] as Array<{ label: string; url: string; sub?: Array<{ label: string; url: string }> }>).map((source) => (
                  <li key={source.url} className="flex flex-col gap-1">
                    <div className="flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <i className="ri-external-link-line text-xs text-terracotta-300"></i>
                      </div>
                      <a
                        href={source.url}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors leading-relaxed break-all cursor-pointer"
                      >
                        {source.label}
                      </a>
                    </div>
                    {source.sub && (
                      <ul className="ml-6 flex flex-col gap-1">
                        {source.sub.map((sub) => (
                          <li key={sub.url} className="flex items-start gap-2">
                            <div className="w-3 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                              <i className="ri-corner-down-right-line text-xs text-anthracite-300"></i>
                            </div>
                            <a
                              href={sub.url}
                              target="_blank"
                              rel="nofollow noopener noreferrer"
                              className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors leading-relaxed break-all cursor-pointer"
                            >
                              {sub.label}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </>
      )}

      {/* ── ALTIDONA ONLY ── */}
      {isAltidona && (
        <>
          {/* Intro + valle dell'Aso + Marina */}
          <section
            ref={altidonaParagraph.ref}
            className={`fade-in-up ${altidonaParagraph.isVisible ? 'visible' : ''} max-w-7xl mx-auto px-6 py-20`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div>
                <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Il borgo</p>
                <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                  Altidona: il borgo collinare tra la valle dell&apos;Aso e l&apos;Adriatico
                </h2>
                <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                  Altidona è un comune della provincia di Fermo che unisce due dimensioni molto diverse e complementari: il centro storico collinare, raccolto e panoramico, e la frazione Marina, più aperta verso la costa e la vita balneare. Le fonti istituzionali descrivono il territorio come un paese in collina che domina la porta d&apos;ingresso della valle dell&apos;Aso e che comprende circa 3 km di spiaggia, elemento che lo rende particolare rispetto a molti altri centri del fermano.
                </p>
                <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                  Il borgo storico conserva una dimensione tranquilla e poco spettacolarizzata, con un impianto semplice e una vita locale ancora leggibile. Più che per grandi attrazioni isolate, Altidona si apprezza per l&apos;equilibrio tra paesaggio, quiete e vicinanza al mare, in una posizione che consente di vivere la collina senza rinunciare alla costa.
                </p>
                <div className="border-l-2 border-terracotta-200 pl-6 mb-8">
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">La Chiesa dei Santi Maria e Ciriaco</p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed">
                    Nel centro del paese il riferimento principale è la Chiesa dei Santi Maria e Ciriaco, riedificata nel Settecento in stile neoclassico e sormontata da un campanile costruito nel 1886 su disegno di G.B. Alesiani. Le fonti turistiche regionali ricordano anche la presenza di opere d&apos;arte sacra di rilievo all&apos;interno dell&apos;edificio.
                  </p>
                </div>
                <div className="border-l-2 border-terracotta-200 pl-6">
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Marina di Altidona e Bandiera Blu</p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed">
                    Altidona non è un borgo distante dalla costa, ma un comune che comprende direttamente Marina di Altidona, con una spiaggia di circa 3 km e una chiara vocazione turistica balneare. La spiaggia del Lungomare Paolo Borsellino ha ottenuto la Bandiera Blu 2025.
                  </p>
                </div>
              </div>

              {/* Photo */}
              <div className="grid grid-cols-1 gap-4">
                <div className="rounded-xl overflow-hidden aspect-[3/4]">
                  <img
                    src="https://static.readdy.ai/image/13e0fae89bbbd02d36954a6d83bda5fb/34039321a5b9b61df602c2286b4cdcd4.png"
                    alt="Altidona borgo collinare e valle dell'Aso"
                    className="w-full h-full object-contain object-center"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Vivere ad Altidona */}
          <section
            ref={altidonaLife.ref}
            className={`fade-in-up ${altidonaLife.isVisible ? 'visible' : ''} bg-ivory-50 py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div>
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Vivere ad Altidona</p>
                  <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                    Posizione, mare e mercato immobiliare
                  </h2>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                    Altidona conta 3.748 abitanti al 1° gennaio 2025 e mantiene una dimensione contenuta, adatta a chi cerca un contesto residenziale più raccolto. Il municipio si trova a 224 metri sul livello del mare e Fermo è vicina: le fonti demografiche indicano una distanza in linea d&apos;aria di circa 8,7 km, mentre i collegamenti stradali la rendono facilmente raggiungibile.
                  </p>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                    Altidona non è un borgo distante dalla costa: comprende direttamente Marina di Altidona, con una spiaggia di circa 3 km e una chiara vocazione turistica balneare. La spiaggia del Lungomare Paolo Borsellino ha ottenuto la Bandiera Blu 2025.
                  </p>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed">
                    Sul piano immobiliare, Altidona presenta valori superiori alla media provinciale. A marzo 2026 il prezzo medio richiesto per gli immobili residenziali in vendita era di 1.983 €/m²; nella provincia di Fermo, nello stesso periodo, la media era di circa 1.270 €/m². È quindi più corretto descriverla come una località interessante e ben posizionata, non come una delle aree più economiche del territorio fermano.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-5">
                  {ALTIDONA_STATS.map((stat) => (
                    <div key={stat.label} className="p-6 bg-white rounded-2xl border border-ivory-200 flex flex-col gap-3">
                      <div className="w-10 h-10 flex items-center justify-center text-terracotta-300">
                        <i className={`${stat.icon} text-2xl`}></i>
                      </div>
                      <p className="font-serif text-2xl text-anthracite-800 font-light">{stat.value}</p>
                      <p className="font-sans text-xs text-anthracite-400 tracking-wide uppercase">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Fonti Altidona */}
          <section className="max-w-7xl mx-auto px-6 pb-2 pt-0">
            <div className="border-l-2 border-ivory-200 pl-6">
              <p className="font-sans text-xs tracking-widest uppercase text-anthracite-400 mb-4">Fonti</p>
              <ul className="flex flex-col gap-2">
                {([
                  { label: 'comune.altidona.fm.it', url: 'https://comune.altidona.fm.it/', sub: [
                    { label: 'Municipio di Altidona', url: 'https://comune.altidona.fm.it/luoghi/2064942/municipio-altidona' },
                  ]},
                  { label: 'tuttitalia.it — Altidona', url: 'https://www.tuttitalia.it/marche/18-altidona/' },
                  { label: 'marcheoutdoor.it', url: 'https://www.marcheoutdoor.it/Comuni/Dettagli/215', sub: [
                    { label: 'Chiesa di S. Maria e Sciriaco — Altidona', url: 'https://www.marcheoutdoor.it/PuntiDiInteresse/Dettagli/chiesa_di_s_maria_e_sciriacoaltidona' },
                  ]},
                  { label: 'immobiliare.it — Mercato Altidona', url: 'https://www.immobiliare.it/mercato-immobiliare/marche/altidona/', sub: [
                    { label: 'Mercato Provincia di Fermo', url: 'https://www.immobiliare.it/mercato-immobiliare/marche/fermo-provincia/' },
                  ]},
                  { label: 'bandierablu.org — Bandiera Blu 2025', url: 'https://www.bandierablu.org/common/blueflag.asp?anno=2025&tipo=bb' },
                ] as Array<{ label: string; url: string; sub?: Array<{ label: string; url: string }> }>).map((source) => (
                  <li key={source.url} className="flex flex-col gap-1">
                    <div className="flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <i className="ri-external-link-line text-xs text-terracotta-300"></i>
                      </div>
                      <a
                        href={source.url}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors leading-relaxed break-all cursor-pointer"
                      >
                        {source.label}
                      </a>
                    </div>
                    {source.sub && (
                      <ul className="ml-6 flex flex-col gap-1">
                        {source.sub.map((sub) => (
                          <li key={sub.url} className="flex items-start gap-2">
                            <div className="w-3 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                              <i className="ri-corner-down-right-line text-xs text-anthracite-300"></i>
                            </div>
                            <a
                              href={sub.url}
                              target="_blank"
                              rel="nofollow noopener noreferrer"
                              className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors leading-relaxed break-all cursor-pointer"
                            >
                              {sub.label}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Cosa vedere */}
          <section
            ref={altidonaAttractions.ref}
            className={`fade-in-up ${altidonaAttractions.isVisible ? 'visible' : ''} py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Esperienze</p>
              <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-12 leading-snug">
                Cosa vedere e vivere ad Altidona
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
                {ALTIDONA_ATTRACTIONS.map((attr) => (
                  <article key={attr.title} className="bg-ivory-50 rounded-2xl p-7 border border-ivory-200 flex flex-col gap-4">
                    <div className="w-11 h-11 flex items-center justify-center text-terracotta-300 bg-white rounded-xl border border-ivory-200">
                      <i className={`${attr.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-serif text-lg text-anthracite-800 font-medium leading-snug">{attr.title}</h3>
                    <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{attr.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* ── TORRE DI PALME ONLY ── */}
      {isTorreDiPalme && (
        <>
          {/* Intro + storia + patrimonio */}
          <section
            ref={torreParagraph.ref}
            className={`fade-in-up ${torreParagraph.isVisible ? 'visible' : ''} max-w-7xl mx-auto px-6 py-20`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div>
                <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Il borgo</p>
                <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                  Torre di Palme: il borgo panoramico tra mare, storia e macchia mediterranea
                </h2>
                <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                  Torre di Palme è una località del Comune di Fermo, posta su un colle che domina l&apos;Adriatico. Le fonti ufficiali la descrivono come un vero e proprio balcone panoramico sul mare, con un impianto storico murato e un tessuto urbano di rara uniformità architettonica, fatto di vicoli, case in cotto e scorci continui sulla costa e sulle colline fermane.
                </p>
                <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                  Il borgo ha origini antiche: l&apos;area era legata all&apos;Ager Palmensis citato da Plinio il Vecchio, mentre l&apos;abitato medievale si sviluppò come castello fortificato su uno sperone roccioso. La sua storia è collegata a Fermo, di cui oggi è una frazione, e il suo valore storico e paesaggistico è riconosciuto anche dal Club de I Borghi più Belli d&apos;Italia, dove compare come Borgo Ospite.
                </p>
                <div className="border-l-2 border-terracotta-200 pl-6 mb-8">
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Il patrimonio religioso e artistico</p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Nel centro storico spiccano alcuni edifici religiosi di grande rilievo. La Chiesa di San Giovanni Battista è indicata dalle fonti ufficiali come l&apos;edificio più antico del borgo, con origini probabilmente tra IX e X secolo. Lungo il corso si incontra poi il Tempio di Sant&apos;Agostino, edificio gotico che conserva il celebre polittico di Vittore Crivelli, mentre la Chiesa di Santa Maria a Mare è un altro dei luoghi simbolo di Torre di Palme, con impianto romanico-gotico, campanile medievale e importanti tracce pittoriche interne.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed">
                    Dal punto di vista culturale, Torre di Palme si è arricchita negli ultimi anni anche del Museo Archeologico, inaugurato nel 2019, dove sono esposti i corredi funerari di tombe picene rinvenute nella contrada Cugnolo. Questo aggiunge al borgo una lettura più ampia, che unisce medioevo, paesaggio costiero e radici protostoriche del territorio.
                  </p>
                </div>
                <div className="border-l-2 border-terracotta-200 pl-6">
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Il Bosco del Cugnolo e la natura</p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed">
                    Accanto al patrimonio storico, Torre di Palme offre anche una dimensione naturalistica molto forte. A pochi minuti dal borgo si raggiunge il Bosco del Cugnolo, area floristica protetta e uno dei pochi lembi ancora integri di macchia mediterranea del litorale marchigiano. Il percorso, breve e accessibile, conduce anche verso la Grotta degli Amanti, legata a una celebre leggenda locale.
                  </p>
                </div>
              </div>

              {/* Photo */}
              <div className="grid grid-cols-1 gap-4">
                <div className="rounded-xl overflow-hidden aspect-[16/9]">
                  <img
                    src="https://static.readdy.ai/image/13e0fae89bbbd02d36954a6d83bda5fb/9d9f6b096a2d84c2d0f466417e9fec39.png"
                    alt="Torre di Palme promontorio sul mare"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Vivere a Torre di Palme */}
          <section
            ref={torreLife.ref}
            className={`fade-in-up ${torreLife.isVisible ? 'visible' : ''} bg-ivory-50 py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div>
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Vivere a Torre di Palme</p>
                  <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                    Posizione, paesaggio e contesto immobiliare
                  </h2>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                    Torre di Palme ha una posizione molto favorevole tra collina e mare. La distanza stradale da Fermo è di circa 10,7–11,3 km, mentre Porto San Giorgio si raggiunge in circa 5–6,5 km. Più che un luogo isolato, è quindi un borgo raccolto ma ben collegato, adatto a chi cerca quiete e paesaggio senza rinunciare alla prossimità con la costa e con i servizi del capoluogo.
                  </p>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed">
                    Sul piano immobiliare è meglio mantenere una formulazione prudente. Non risultano quotazioni ufficiali autonome e strutturate per la sola frazione di Torre di Palme; il riferimento più solido è quello del Comune di Fermo, dove a marzo 2026 il prezzo medio richiesto per gli immobili residenziali in vendita era di 1.318 €/m², contro 1.270 €/m² della media provinciale. Per Torre di Palme si può parlare di contesto raro e ricercato.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-5">
                  {TORRE_DI_PALME_STATS.map((stat) => (
                    <div key={stat.label} className="p-6 bg-white rounded-2xl border border-ivory-200 flex flex-col gap-3">
                      <div className="w-10 h-10 flex items-center justify-center text-terracotta-300">
                        <i className={`${stat.icon} text-2xl`}></i>
                      </div>
                      <p className="font-serif text-2xl text-anthracite-800 font-light">{stat.value}</p>
                      <p className="font-sans text-xs text-anthracite-400 tracking-wide uppercase">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Fonti Torre di Palme */}
          <section className="max-w-7xl mx-auto px-6 pb-2 pt-0">
            <div className="border-l-2 border-ivory-200 pl-6">
              <p className="font-sans text-xs tracking-widest uppercase text-anthracite-400 mb-4">Fonti</p>
              <ul className="flex flex-col gap-2">
                {([
                  { label: 'italia.it — Torre di Palme', url: 'https://www.italia.it/it/marche/torre-di-palme' },
                  { label: 'visitfermo.it', url: 'https://www.visitfermo.it/en/poi/torre-di-palme', sub: [
                    { label: 'Chiesa di San Giovanni Battista', url: 'https://www.visitfermo.it/en/poi/the-church-of-san-giovanni-battista' },
                    { label: 'Tempio di Sant\'Agostino e polittico di Crivelli', url: 'https://www.visitfermo.it/en/poi/st-augustine-temple-and-the-lost-polyptych-of-crivelli' },
                    { label: 'Chiesa di Santa Maria a Mare', url: 'https://www.visitfermo.it/en/poi/the-church-of-santa-maria-a-mare' },
                    { label: 'Bosco del Cugnolo', url: 'https://www.visitfermo.it/en/poi/the-forest-of-cugnolo' },
                  ]},
                  { label: 'borghipiubelliditalia.it — Torre di Palme', url: 'https://borghipiubelliditalia.it/borgo/torre-di-palme-2/' },
                  { label: 'fermomusei.it — Museo Archeologico di Torre di Palme', url: 'https://www.fermomusei.it/en/museums/torre-di-palme-archaeological-museum/' },
                  { label: 'regione.marche.it — Museo Archeologico di Torre di Palme', url: 'https://www.regione.marche.it/Regione-Utile/Cultura/Ricerca-Musei/Id/486/FERMO-Museo-Archeologico-di-Torre-di-Palme' },
                  { label: 'rome2rio.com — Fermo → Torre di Palme', url: 'https://www.rome2rio.com/it/s/Fermo/Torre-di-Palme' },
                  { label: 'viamichelin.it — Torre di Palme → Porto San Giorgio', url: 'https://www.viamichelin.it/itinerari/risultati/torre_di_palme-63900-fermo-marche-repubblica_italiana-to-porto_san_giorgio-63822-fermo-marche-repubblica_italiana' },
                ] as Array<{ label: string; url: string; sub?: Array<{ label: string; url: string }> }>).map((source) => (
                  <li key={source.url} className="flex flex-col gap-1">
                    <div className="flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <i className="ri-external-link-line text-xs text-terracotta-300"></i>
                      </div>
                      <a
                        href={source.url}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors leading-relaxed break-all cursor-pointer"
                      >
                        {source.label}
                      </a>
                    </div>
                    {source.sub && (
                      <ul className="ml-6 flex flex-col gap-1">
                        {source.sub.map((sub) => (
                          <li key={sub.url} className="flex items-start gap-2">
                            <div className="w-3 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                              <i className="ri-corner-down-right-line text-xs text-anthracite-300"></i>
                            </div>
                            <a
                              href={sub.url}
                              target="_blank"
                              rel="nofollow noopener noreferrer"
                              className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors leading-relaxed break-all cursor-pointer"
                            >
                              {sub.label}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Cosa vedere */}
          <section
            ref={torreAttractions.ref}
            className={`fade-in-up ${torreAttractions.isVisible ? 'visible' : ''} py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Esperienze</p>
              <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-12 leading-snug">
                Cosa vedere e vivere a Torre di Palme
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {TORRE_DI_PALME_ATTRACTIONS.map((attr) => (
                  <article key={attr.title} className="bg-ivory-50 rounded-2xl p-7 border border-ivory-200 flex flex-col gap-4">
                    <div className="w-11 h-11 flex items-center justify-center text-terracotta-300 bg-white rounded-xl border border-ivory-200">
                      <i className={`${attr.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-serif text-lg text-anthracite-800 font-medium leading-snug">{attr.title}</h3>
                    <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{attr.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* ── FERMO ONLY ── */}
      {isFermo && (
        <>
          {/* Intro + cisterne + arte */}
          <section
            ref={fermoParagraph.ref}
            className={`fade-in-up ${fermoParagraph.isVisible ? 'visible' : ''} max-w-7xl mx-auto px-6 py-20`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div>
                <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">La città</p>
                <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                  Fermo: la città delle cisterne romane e del colle Girfalco
                </h2>
                <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                  Fermo è una città storica delle Marche meridionali e capoluogo di provincia, costruita sul colle Girfalco a 319 metri sul livello del mare. Più che per un singolo monumento, si distingue per la densità del suo patrimonio: piazze, musei, architetture religiose, resti archeologici e un rapporto molto forte con il paesaggio adriatico.
                </p>
                <div className="border-l-2 border-terracotta-200 pl-6 mb-8">
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Le Cisterne Romane</p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Uno dei luoghi simbolo di Fermo sono le Cisterne Romane, un grande complesso sotterraneo realizzato nel I secolo d.C. per la raccolta e la distribuzione dell&apos;acqua. Le fonti turistiche ufficiali parlano di 30 sale disposte su tre file per una superficie complessiva di circa 2.200 metri quadrati. Si tratta di uno dei più importanti impianti idrici romani conservati nelle Marche e di una delle visite più caratteristiche della città.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Il profilo monumentale di Fermo è dominato dalla Cattedrale di Santa Maria Assunta, che sorge sul Girfalco. La chiesa attuale fu completata nel 1227 dopo la distruzione dell&apos;edificio precedente nel 1176; conserva ancora la facciata originaria in pietra d&apos;Istria, mentre l&apos;interno è oggi prevalentemente neoclassico per effetto dei rifacimenti del Settecento.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed">
                    Accanto alla cattedrale si trova il Museo Diocesano, dove è conservata la celebre casula di San Thomas Becket, rarissimo tessuto di origine araba datato 1116. Nel cuore del centro storico, in Palazzo dei Priori, si trova invece la Pinacoteca Civica, che custodisce opere di grande rilievo: le pale di Rubens e Giovanni Lanfranco, il ciclo di dipinti murali di Andrea Boscoli e il celebre Mappamondo della biblioteca storica.
                  </p>
                </div>
              </div>

              {/* Photo */}
              <div className="grid grid-cols-1 gap-4">
                <div className="rounded-xl overflow-hidden aspect-[16/9]">
                  <img
                    src="https://static.readdy.ai/image/13e0fae89bbbd02d36954a6d83bda5fb/82d77455fc8a30c7975b5c64a0c626c2.png"
                    alt="Fermo colle Girfalco e panorama adriatico"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Vivere a Fermo */}
          <section
            ref={fermoLife.ref}
            className={`fade-in-up ${fermoLife.isVisible ? 'visible' : ''} bg-ivory-50 py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div>
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Vivere a Fermo</p>
                  <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                    Servizi, posizione e mercato immobiliare
                  </h2>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                    Fermo conta 35.727 abitanti ed è una città di dimensione intermedia, abbastanza strutturata da offrire servizi, scuole, ospedale, rete museale e sedi universitarie, ma ancora leggibile e vivibile come centro storico collinare. L&apos;Università Politecnica delle Marche ha qui una sede di corsi di laurea.
                  </p>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                    Il rapporto con il mare è uno degli aspetti più forti della città: Porto San Giorgio si trova a circa 5 km di distanza, mentre Porto Sant&apos;Elpidio dista circa 13,4 km su strada. Per la montagna, i Monti Sibillini si trovano a circa 106,7 km su strada, con un tempo di percorrenza vicino a 1 ora e 43 minuti.
                  </p>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed">
                    Anche il mercato immobiliare conferma un profilo interessante. A marzo 2026 il prezzo medio richiesto per gli immobili residenziali in vendita a Fermo era di 1.469 €/m², un valore superiore alla media della provincia fermana (1.323 €/m²) ma ancora lontano dalle quotazioni delle località costiere più esclusive.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-5">
                  {FERMO_STATS.map((stat) => (
                    <div key={stat.label} className="p-6 bg-white rounded-2xl border border-ivory-200 flex flex-col gap-3">
                      <div className="w-10 h-10 flex items-center justify-center text-terracotta-300">
                        <i className={`${stat.icon} text-2xl`}></i>
                      </div>
                      <p className="font-serif text-2xl text-anthracite-800 font-light">{stat.value}</p>
                      <p className="font-sans text-xs text-anthracite-400 tracking-wide uppercase">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Fonti Fermo */}
          <section className="max-w-7xl mx-auto px-6 pb-2 pt-0">
            <div className="border-l-2 border-ivory-200 pl-6">
              <p className="font-sans text-xs tracking-widest uppercase text-anthracite-400 mb-4">Fonti</p>
              <ul className="flex flex-col gap-2">
                {([
                  { label: 'visitfermo.it — Cisterne Romane', url: 'https://www.visitfermo.it/it/organizzazioni/cisterne-romane-di-fermo', sub: [
                    { label: 'Cattedrale di Fermo', url: 'https://www.visitfermo.it/en/poi/fermo-cathedral' },
                  ]},
                  { label: 'fermomusei.it — Cisterne Romane', url: 'https://www.fermomusei.it/musei/cisterne-romane/', sub: [
                    { label: 'Museo Diocesano', url: 'https://www.fermomusei.it/musei/museo-diocesano/' },
                    { label: 'Casula di San Thomas Becket', url: 'https://www.fermomusei.it/musei/museo-diocesano/approfondimenti/casula-di-san-thomas-becket/' },
                    { label: 'Palazzo dei Priori', url: 'https://www.fermomusei.it/musei/palazzo-dei-priori/' },
                    { label: 'Pinacoteca Civica', url: 'https://www.fermomusei.it/musei/palazzo-dei-priori/approfondimenti/la-pinacoteca-civica/' },
                  ]},
                  { label: 'regione.marche.it — Cisterne Romane di Fermo', url: 'https://www.regione.marche.it/Regione-Utile/Cultura/Ricerca-Musei/Id/275/FERMO-Cisterne-Romane' },
                  { label: 'museodiocesanofermo.it', url: 'https://www.museodiocesanofermo.it/' },
                  { label: 'comune.fermo.it — Musei civici', url: 'https://www.comune.fermo.it/Vivere-il-comune/Luoghi/Musei-civici' },
                  { label: 'univpm.it — Corsi di laurea sede di Fermo', url: 'https://www.univpm.it/Entra/Sedi_dei_Corsi_di_Studio/Corsi_di_laurea_Sede_di_Fermo', sub: [
                    { label: 'Fermo città universitaria', url: 'https://www.univpm.it/Entra/Magazine_online/Comunicati_stampa_1/Fermo_citta_universitaria' },
                  ]},
                  { label: 'tuttitalia.it — Fermo', url: 'https://www.tuttitalia.it/marche/82-fermo/' },
                  { label: 'rome2rio.com — Fermo → Porto San Giorgio', url: 'https://www.rome2rio.com/s/Fermo/Porto-San-Giorgio', sub: [
                    { label: 'Fermo → Porto Sant\'Elpidio', url: 'https://www.rome2rio.com/s/Fermo/Porto-Sant-Elpidio' },
                    { label: 'Fermo → Monti Sibillini', url: 'https://www.rome2rio.com/s/Fermo/Monti-Sibillini' },
                  ]},
                ] as Array<{ label: string; url: string; sub?: Array<{ label: string; url: string }> }>).map((source) => (
                  <li key={source.url} className="flex flex-col gap-1">
                    <div className="flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <i className="ri-external-link-line text-xs text-terracotta-300"></i>
                      </div>
                      <a
                        href={source.url}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors leading-relaxed break-all cursor-pointer"
                      >
                        {source.label}
                      </a>
                    </div>
                    {source.sub && (
                      <ul className="ml-6 flex flex-col gap-1">
                        {source.sub.map((sub) => (
                          <li key={sub.url} className="flex items-start gap-2">
                            <div className="w-3 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                              <i className="ri-corner-down-right-line text-xs text-anthracite-300"></i>
                            </div>
                            <a
                              href={sub.url}
                              target="_blank"
                              rel="nofollow noopener noreferrer"
                              className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors leading-relaxed break-all cursor-pointer"
                            >
                              {sub.label}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Cosa vedere */}
          <section
            ref={fermoAttractions.ref}
            className={`fade-in-up ${fermoAttractions.isVisible ? 'visible' : ''} py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Esperienze</p>
              <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-12 leading-snug">
                Cosa vedere e vivere a Fermo
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {FERMO_ATTRACTIONS.map((attr) => (
                  <article key={attr.title} className="bg-ivory-50 rounded-2xl p-7 border border-ivory-200 flex flex-col gap-4">
                    <div className="w-11 h-11 flex items-center justify-center text-terracotta-300 bg-white rounded-xl border border-ivory-200">
                      <i className={`${attr.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-serif text-lg text-anthracite-800 font-medium leading-snug">{attr.title}</h3>
                    <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{attr.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* ── MONTELUPONE ONLY ── */}
      {isMontelupone && (
        <>
          {/* Intro + mura + storia */}
          <section
            ref={monteluponeParagraph.ref}
            className={`fade-in-up ${monteluponeParagraph.isVisible ? 'visible' : ''} max-w-7xl mx-auto px-6 py-20`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div>
                <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Il borgo</p>
                <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                  Montelupone: il borgo murato tra Macerata e l&apos;Adriatico
                </h2>
                <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                  Montelupone è un borgo collinare della provincia di Macerata, posto in una posizione favorevole tra l&apos;entroterra e la costa. Il suo centro storico conserva ancora in modo molto leggibile l&apos;impianto medievale, con la cinta muraria, le quattro porte di accesso e un tessuto urbano compatto che gli ha fatto ottenere sia la Bandiera Arancione del Touring Club Italiano sia l&apos;ingresso nel circuito dei Borghi più Belli d&apos;Italia.
                </p>
                <div className="border-l-2 border-terracotta-200 pl-6 mb-8">
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Il centro storico</p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Il nucleo antico si sviluppa attorno a Piazza del Comune, dove si trovano il Palazzetto del Podestà o dei Priori e la Torre Civica, entrambi tra i simboli più riconoscibili del paese. Il Palazzetto è un edificio trecentesco, mentre la torre domina il profilo del borgo e apre visuali che arrivano fino ai Monti Sibillini e al mare Adriatico.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Tra i principali luoghi di interesse del centro c&apos;è anche la Chiesa di San Francesco, la cui costruzione risale al 1251 e la cui consacrazione avvenne nel 1397. L&apos;edificio ha subito importanti trasformazioni nei secoli successivi e oggi presenta un interno in larga parte tardo barocco, pur conservando elementi più antichi che ne raccontano la lunga storia.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed">
                    Il percorso dentro il borgo è completato da altri elementi di rilievo come il Palazzo Comunale e il Teatro storico Nicola Degli Angeli, inserito all&apos;interno del complesso civico. Il teatro, di impianto ottocentesco, arricchisce il profilo culturale di Montelupone e contribuisce a farne un centro storico piccolo ma molto articolato.
                  </p>
                </div>
              </div>

              {/* Photo */}
              <div className="grid grid-cols-1 gap-4">
                <div className="rounded-xl overflow-hidden aspect-[16/9]">
                  <img
                    src="https://static.readdy.ai/image/13e0fae89bbbd02d36954a6d83bda5fb/065f50add5004388c74dc2d8bc642d7d.png"
                    alt="Montelupone mura medievali e panorama"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Vivere a Montelupone */}
          <section
            ref={monteluponeLife.ref}
            className={`fade-in-up ${monteluponeLife.isVisible ? 'visible' : ''} bg-ivory-50 py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div>
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Vivere a Montelupone</p>
                  <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                    Dimensione, posizione e mercato immobiliare
                  </h2>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                    Montelupone conta 3.355 abitanti al 1° gennaio 2025 e mantiene una dimensione raccolta, adatta a chi cerca tranquillità senza isolamento. La distanza stradale da Macerata è di circa 13 km, mentre la costa più vicina, verso Porto Potenza Picena, si raggiunge in circa 13,6 km su strada. Questo rende il borgo ben collegato sia all&apos;entroterra sia al mare.
                  </p>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed">
                    Anche il mercato immobiliare conferma il profilo accessibile del paese. A marzo 2026 il prezzo medio richiesto per gli immobili residenziali in vendita a Montelupone era di 818 €/m², contro una media della provincia di Macerata di 1.397 €/m². È quindi corretto raccontarlo come un contesto storico di qualità con valori medi più contenuti rispetto al dato provinciale.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-5">
                  {MONTELUPONE_STATS.map((stat) => (
                    <div key={stat.label} className="p-6 bg-white rounded-2xl border border-ivory-200 flex flex-col gap-3">
                      <div className="w-10 h-10 flex items-center justify-center text-terracotta-300">
                        <i className={`${stat.icon} text-2xl`}></i>
                      </div>
                      <p className="font-serif text-2xl text-anthracite-800 font-light">{stat.value}</p>
                      <p className="font-sans text-xs text-anthracite-400 tracking-wide uppercase">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Fonti Montelupone */}
          <section className="max-w-7xl mx-auto px-6 pb-2 pt-0">
            <div className="border-l-2 border-ivory-200 pl-6">
              <p className="font-sans text-xs tracking-widest uppercase text-anthracite-400 mb-4">Fonti</p>
              <ul className="flex flex-col gap-2">
                {([
                  { label: 'borghipiubelliditalia.it — Montelupone', url: 'https://borghipiubelliditalia.it/borgo/montelupone/' },
                  { label: 'touringclub.it — Bandiere Arancioni', url: 'https://www.touringclub.it/cosa-facciamo/bandiere-arancioni', sub: [
                    { label: 'paesiarancioni.net — Montelupone', url: 'https://www.paesiarancioni.net/comune/montelupone' },
                  ]},
                  { label: 'italia.it — Montelupone', url: 'https://www.italia.it/en/marche/montelupone' },
                  { label: 'valledelpensare.it — Montelupone', url: 'https://www.valledelpensare.it/it/montelupone/', sub: [
                    { label: 'Palazzetto del Podestà e Torre Civica', url: 'https://www.valledelpensare.it/it/punto-di-interesse/poi/palazzetto-del-podest-o-priori-e-torre-civica-32/' },
                    { label: 'Palazzo Comunale e Teatro Nicola Degli Angeli', url: 'https://www.valledelpensare.it/it/punto-di-interesse/poi/palazzo-comunale-e-teatro-nicola-degli-angeli-10/' },
                    { label: 'Chiesa di San Francesco', url: 'https://www.valledelpensare.it/it/punto-di-interesse/poi/chiesa-di-san-francesco-1/' },
                    { label: 'Porta Cassero', url: 'https://www.valledelpensare.it/it/punto-di-interesse/poi/porta-cassero-143/' },
                    { label: 'Le mura e la torre (itinerario)', url: 'https://www.valledelpensare.it/it/scheda-itinerario/itinerario/le-mura-e-la-torre-11/' },
                  ]},
                  { label: 'artbonus.gov.it — Confessionali chiesa di San Francesco', url: 'https://artbonus.gov.it/117-9-confessionali-in-noce-lastronato-del-1700-della-chiesa-di-san-francesco.html' },
                  { label: 'tuttitalia.it — Montelupone', url: 'https://www.tuttitalia.it/marche/97-montelupone/' },
                  { label: 'rome2rio.com — Montelupone → Macerata', url: 'https://www.rome2rio.com/it/s/Montelupone/Macerata', sub: [
                    { label: 'Montelupone → Porto Potenza Picena', url: 'https://www.rome2rio.com/s/Montelupone/Porto-Potenza-Picena' },
                  ]},
                ] as Array<{ label: string; url: string; sub?: Array<{ label: string; url: string }> }>).map((source) => (
                  <li key={source.url} className="flex flex-col gap-1">
                    <div className="flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <i className="ri-external-link-line text-xs text-terracotta-300"></i>
                      </div>
                      <a
                        href={source.url}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors leading-relaxed break-all cursor-pointer"
                      >
                        {source.label}
                      </a>
                    </div>
                    {source.sub && (
                      <ul className="ml-6 flex flex-col gap-1">
                        {source.sub.map((sub) => (
                          <li key={sub.url} className="flex items-start gap-2">
                            <div className="w-3 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                              <i className="ri-corner-down-right-line text-xs text-anthracite-300"></i>
                            </div>
                            <a
                              href={sub.url}
                              target="_blank"
                              rel="nofollow noopener noreferrer"
                              className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors leading-relaxed break-all cursor-pointer"
                            >
                              {sub.label}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Cosa vedere */}
          <section
            ref={monteluponeAttractions.ref}
            className={`fade-in-up ${monteluponeAttractions.isVisible ? 'visible' : ''} py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Esperienze</p>
              <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-12 leading-snug">
                Cosa vedere e vivere a Montelupone
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {MONTELUPONE_ATTRACTIONS.map((attr) => (
                  <article key={attr.title} className="bg-ivory-50 rounded-2xl p-7 border border-ivory-200 flex flex-col gap-4">
                    <div className="w-11 h-11 flex items-center justify-center text-terracotta-300 bg-white rounded-xl border border-ivory-200">
                      <i className={`${attr.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-serif text-lg text-anthracite-800 font-medium leading-snug">{attr.title}</h3>
                    <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{attr.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* ── FIASTRA ONLY ── */}
      {isFiastra && (
        <>
          {/* Intro + Abbazia + lago */}
          <section
            ref={fiastraParagraph.ref}
            className={`fade-in-up ${fiastraParagraph.isVisible ? 'visible' : ''} max-w-7xl mx-auto px-6 py-20`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div>
                <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Il borgo</p>
                <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                  Fiastra: il lago, i Sibillini e la natura dell&apos;entroterra marchigiano
                </h2>
                <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                  Fiastra è un piccolo comune della provincia di Macerata, nel cuore del Parco Nazionale dei Monti Sibillini. Più che per un centro storico compatto, è conosciuto per il suo straordinario contesto naturale: il Lago di Fiastra, le montagne del parco, i boschi, i sentieri e alcuni degli itinerari più spettacolari dell&apos;Appennino marchigiano. Al 1° gennaio 2025 conta 607 abitanti, mantenendo una dimensione raccolta e fortemente legata al paesaggio.
                </p>
                <div className="border-l-2 border-terracotta-200 pl-6 mb-8">
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Il Lago di Fiastra e le Lame Rosse</p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Il Lago di Fiastra è il simbolo più noto del territorio. Si tratta di un lago artificiale creato nel 1955 sul fiume Fiastrone, inserito all&apos;interno del Parco dei Sibillini; le fonti turistiche ufficiali lo indicano come una meta popolare per nuoto e kayak, oltre che come uno dei luoghi più rappresentativi della zona. Le fonti regionali lo descrivono inoltre come il più grande bacino idroelettrico delle Marche.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed">
                    Tra le esperienze più caratteristiche di Fiastra c&apos;è il percorso verso le Lame Rosse, uno degli itinerari più famosi dei Sibillini. Il Parco lo presenta come un percorso immerso nella cornice del lago, celebre per le sue formazioni rocciose, i piccoli canyon e i pinnacoli che rendono questo paesaggio uno dei più riconoscibili delle Marche.
                  </p>
                </div>
                <div className="border-l-2 border-terracotta-200 pl-6">
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Natura, sentieri e patrimonio storico</p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Fiastra è uno dei comuni compresi nel Parco Nazionale dei Monti Sibillini, un&apos;area protetta di oltre 71.000 ettari distribuita tra Marche e Umbria. Nel territorio del parco si trovano il lago, vallate profonde, faggete, dorsali montane e un patrimonio naturalistico di grande rilievo.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed">
                    Accanto al lago, il territorio conserva anche l&apos;Abbazia di Santa Maria di Rio Sacro, nell&apos;area di Meriggio lungo la sponda sinistra del Fiastrone. L&apos;attuale struttura in stile romanico locale ha origini antiche e conserva una cripta romanica del IX secolo; dopo il sisma del 2016, però, l&apos;abbazia risulta inagibile.
                  </p>
                </div>
              </div>

              {/* Photo grid */}
              <div className="grid grid-cols-1 gap-4">
                <div className="rounded-xl overflow-hidden aspect-[16/9]">
                  <img
                    src="https://static.readdy.ai/image/13e0fae89bbbd02d36954a6d83bda5fb/e6dab25a4bd5135d5076f0c4abb09e0a.png"
                    alt="Lago di Fiastra e Monti Sibillini"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Vivere a Fiastra */}
          <section
            ref={fiastraLife.ref}
            className={`fade-in-up ${fiastraLife.isVisible ? 'visible' : ''} bg-ivory-50 py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div>
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Vivere a Fiastra</p>
                  <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                    Natura, quiete e paesaggio
                  </h2>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                    Fiastra è adatta a chi cerca un contesto di quiete, spazio e paesaggio. La sua forza non sta nella quantità di servizi urbani, ma nella qualità ambientale: lago, montagne, sentieri e piccoli nuclei abitati definiscono uno stile di vita molto più lento rispetto alle località costiere o ai centri maggiori della provincia.
                  </p>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed">
                    Anche il profilo immobiliare conferma questa dimensione periferica ma interessante. A marzo 2026 il prezzo medio richiesto per gli immobili residenziali in vendita a Fiastra era di 583 €/m², contro una media della provincia di Macerata pari a 1.397 €/m². Valori sensibilmente più bassi della media provinciale, in un contesto naturale di grande qualità.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-5">
                  {FIASTRA_STATS.map((stat) => (
                    <div key={stat.label} className="p-6 bg-white rounded-2xl border border-ivory-200 flex flex-col gap-3">
                      <div className="w-10 h-10 flex items-center justify-center text-terracotta-300">
                        <i className={`${stat.icon} text-2xl`}></i>
                      </div>
                      <p className="font-serif text-2xl text-anthracite-800 font-light">{stat.value}</p>
                      <p className="font-sans text-xs text-anthracite-400 tracking-wide uppercase">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Fonti Fiastra */}
          <section className="max-w-7xl mx-auto px-6 pb-2 pt-0">
            <div className="border-l-2 border-ivory-200 pl-6">
              <p className="font-sans text-xs tracking-widest uppercase text-anthracite-400 mb-4">Fonti</p>
              <ul className="flex flex-col gap-2">
                {([
                  { label: 'comune.fiastra.mc.it', url: 'https://www.comune.fiastra.mc.it/' },
                  { label: 'tuttitalia.it — Fiastra', url: 'https://www.tuttitalia.it/marche/84-fiastra/' },
                  { label: 'sibillini.net', url: 'https://www.sibillini.net/', sub: [
                    { label: 'Parco Nazionale dei Monti Sibillini', url: 'https://www.parks.it/parco.nazionale.monti.sibillini/par.php' },
                    { label: 'Itinerario Lame Rosse', url: 'https://www.parks.it/parco.nazionale.monti.sibillini/iti_dettaglio.php?id_iti=4080' },
                    { label: 'Lago di Fiastra (scheda parco)', url: 'https://www.parks.it/parco.nazionale.monti.sibillini/man_dettaglio.php?id=121392' },
                  ]},
                  { label: 'lagodifiastra.it', url: 'https://www.lagodifiastra.it/', sub: [
                    { label: 'About — Lago di Fiastra', url: 'https://www.lagodifiastra.it/it/about' },
                  ]},
                  { label: 'italia.it — Lago di Fiastra', url: 'https://www.italia.it/it/marche/macerata/lago-di-fiastra' },
                  { label: 'marcheoutdoor.it — Abbazia di Santa Maria in Rio Sacro', url: 'https://www.marcheoutdoor.it/PuntiDiInteresse/Dettagli/abbazia_di_santa_maria_in_rio_sacro_' },
                ] as Array<{ label: string; url: string; sub?: Array<{ label: string; url: string }> }>).map((source) => (
                  <li key={source.url} className="flex flex-col gap-1">
                    <div className="flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <i className="ri-external-link-line text-xs text-terracotta-300"></i>
                      </div>
                      <a
                        href={source.url}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors leading-relaxed break-all cursor-pointer"
                      >
                        {source.label}
                      </a>
                    </div>
                    {source.sub && (
                      <ul className="ml-6 flex flex-col gap-1">
                        {source.sub.map((sub) => (
                          <li key={sub.url} className="flex items-start gap-2">
                            <div className="w-3 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                              <i className="ri-corner-down-right-line text-xs text-anthracite-300"></i>
                            </div>
                            <a
                              href={sub.url}
                              target="_blank"
                              rel="nofollow noopener noreferrer"
                              className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors leading-relaxed break-all cursor-pointer"
                            >
                              {sub.label}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Cosa vedere */}
          <section
            ref={fiastraAttractions.ref}
            className={`fade-in-up ${fiastraAttractions.isVisible ? 'visible' : ''} py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Esperienze</p>
              <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-12 leading-snug">
                Cosa vedere e vivere a Fiastra
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
                {FIASTRA_ATTRACTIONS.map((attr) => (
                  <article key={attr.title} className="bg-ivory-50 rounded-2xl p-7 border border-ivory-200 flex flex-col gap-4">
                    <div className="w-11 h-11 flex items-center justify-center text-terracotta-300 bg-white rounded-xl border border-ivory-200">
                      <i className={`${attr.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-serif text-lg text-anthracite-800 font-medium leading-snug">{attr.title}</h3>
                    <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{attr.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* ── TOLENTINO ONLY ── */}
      {isTolentino && (
        <>
          {/* Intro + Basilica + storia */}
          <section
            ref={tolentinoParagraph.ref}
            className={`fade-in-up ${tolentinoParagraph.isVisible ? 'visible' : ''} max-w-7xl mx-auto px-6 py-20`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div>
                <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Il borgo</p>
                <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                  Tolentino: la città di San Nicola e della valle del Chienti
                </h2>
                <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                  Tolentino è una città storica della provincia di Macerata, situata nella valle del Chienti, dove arte, spiritualità e memoria civile convivono in un tessuto urbano ricco di stratificazioni. Più che un borgo in senso stretto, è un centro marchigiano di dimensione intermedia, riconoscibile per il profilo della Basilica di San Nicola, per il patrimonio monumentale del centro e per la presenza di luoghi simbolici come il Ponte del Diavolo e il Castello della Rancia.
                </p>
                <div className="border-l-2 border-terracotta-200 pl-6 mb-8">
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">La Basilica di San Nicola e il Cappellone</p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Il cuore identitario della città è la Basilica di San Nicola da Tolentino, edificata sull&apos;antica chiesa di Sant&apos;Agostino. L&apos;impianto originario era a capanna e venne arricchito nel Quattrocento dallo splendido portale marmoreo di Nanni di Bartolo. All&apos;interno, il Cappellone conserva un importantissimo ciclo di affreschi trecenteschi attribuiti a Pietro da Rimini, articolati nelle storie della Vergine, di Cristo e di San Nicola. Il complesso comprende anche il chiostro e il Museo del Santuario, con otto sale che raccolgono opere, ex voto, ceramiche, sculture, dipinti e reperti dal IV-V secolo al Novecento.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed">
                    Accanto alla dimensione religiosa, Tolentino ha un profilo culturale molto originale grazie al MIUMOR – Museo Internazionale dell&apos;Umorismo nell&apos;Arte, ospitato al primo piano di Palazzo Sangallo. Il museo nasce dalla Biennale Internazionale dell&apos;Umorismo e conserva oltre 5.000 opere.
                  </p>
                </div>
                <div className="border-l-2 border-terracotta-200 pl-6">
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Il centro storico e gli altri luoghi di interesse</p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Il centro storico e il territorio attorno alla città offrono altri luoghi di grande interesse: il Ponte del Diavolo, ponte fortificato del 1268 con cinque arcate sul Chienti; il Castello della Rancia, oggi parte del sistema museale cittadino; e la Concattedrale di San Catervo, che custodisce una storia architettonica complessa e molto antica.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed">
                    In questo modo Tolentino si presenta come una città d&apos;arte completa, non concentrata in un solo monumento ma diffusa in più nuclei di visita.
                  </p>
                </div>
              </div>

              {/* Photo */}
              <div className="grid grid-cols-1 gap-4">
                <div className="rounded-xl overflow-hidden aspect-[16/9]">
                  <img
                    src="https://static.readdy.ai/image/13e0fae89bbbd02d36954a6d83bda5fb/bb940abd3c6b40a9f18c93839417a39a.png"
                    alt="Tolentino Basilica di San Nicola"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Vivere a Tolentino */}
          <section
            ref={tolentinoLife.ref}
            className={`fade-in-up ${tolentinoLife.isVisible ? 'visible' : ''} bg-ivory-50 py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div>
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Vivere a Tolentino</p>
                  <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                    Servizi, posizione e mercato immobiliare
                  </h2>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                    Tolentino conta 17.557 abitanti al 31 dicembre 2024 e mantiene il profilo di una città strutturata, con una dimensione abbastanza ampia da garantire una buona quotidianità, ma ancora leggibile e vivibile come centro dell&apos;entroterra marchigiano. La costa adriatica più vicina, verso Civitanova Marche, dista circa 40 km su strada, mentre i Monti Sibillini si trovano a circa 58,8 km di distanza stradale.
                  </p>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed">
                    Anche il mercato immobiliare restituisce l&apos;immagine di una città accessibile. A marzo 2026 il prezzo medio richiesto per gli immobili residenziali in vendita a Tolentino era di 1.212 €/m², inferiore alla media della provincia di Macerata, pari a 1.397 €/m². È quindi corretto presentare Tolentino come una realtà interessante per chi cerca un centro storico e urbano ben definito, con valori medi più contenuti rispetto al contesto provinciale.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-5">
                  {TOLENTINO_STATS.map((stat) => (
                    <div key={stat.label} className="p-6 bg-white rounded-2xl border border-ivory-200 flex flex-col gap-3">
                      <div className="w-10 h-10 flex items-center justify-center text-terracotta-300">
                        <i className={`${stat.icon} text-2xl`}></i>
                      </div>
                      <p className="font-serif text-2xl text-anthracite-800 font-light">{stat.value}</p>
                      <p className="font-sans text-xs text-anthracite-400 tracking-wide uppercase">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Fonti Tolentino */}
          <section className="max-w-7xl mx-auto px-6 pb-2 pt-0">
            <div className="border-l-2 border-ivory-200 pl-6">
              <p className="font-sans text-xs tracking-widest uppercase text-anthracite-400 mb-4">Fonti</p>
              <ul className="flex flex-col gap-2">
                {([
                  { label: 'sannicoladatolentino.org', url: 'https://www.sannicoladatolentino.org/', sub: [
                    { label: 'La facciata', url: 'https://www.sannicoladatolentino.org/la-facciata/' },
                    { label: 'La storia', url: 'https://www.sannicoladatolentino.org/la-storia/' },
                  ]},
                  { label: 'tolentinomuseicivici.it', url: 'https://www.tolentinomuseicivici.it/', sub: [
                    { label: 'Complesso monumentale Basilica di San Nicola', url: 'https://www.tolentinomuseicivici.it/project/complesso-monumentale-basilica-di-san-nicola/' },
                    { label: 'Museo Santuario San Nicola', url: 'https://www.tolentinomuseicivici.it/museo-santuario-san-nicola/' },
                    { label: 'MIUMOR – Museo Internazionale Umorismo nell\'Arte', url: 'https://www.tolentinomuseicivici.it/miumor-museo-internazionale-umorismo-arte-tolentino-macerata/' },
                    { label: 'Ponte del Diavolo', url: 'https://www.tolentinomuseicivici.it/project/ponte-del-diavolo/' },
                    { label: 'Castello della Rancia', url: 'https://www.tolentinomuseicivici.it/castello-della-rancia-tolentino-2/' },
                    { label: 'Concattedrale di San Catervo', url: 'https://www.tolentinomuseicivici.it/project/concattedrale-di-san-catervo/' },
                  ]},
                  { label: 'tuttitalia.it — Tolentino, andamento demografico', url: 'https://www.tuttitalia.it/marche/85-tolentino/statistiche/popolazione-andamento-demografico/' },
                ] as Array<{ label: string; url: string; sub?: Array<{ label: string; url: string }> }>).map((source) => (
                  <li key={source.url} className="flex flex-col gap-1">
                    <div className="flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <i className="ri-external-link-line text-xs text-terracotta-300"></i>
                      </div>
                      <a
                        href={source.url}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors leading-relaxed break-all cursor-pointer"
                      >
                        {source.label}
                      </a>
                    </div>
                    {source.sub && (
                      <ul className="ml-6 flex flex-col gap-1">
                        {source.sub.map((sub) => (
                          <li key={sub.url} className="flex items-start gap-2">
                            <div className="w-3 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                              <i className="ri-corner-down-right-line text-xs text-anthracite-300"></i>
                            </div>
                            <a
                              href={sub.url}
                              target="_blank"
                              rel="nofollow noopener noreferrer"
                              className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors leading-relaxed break-all cursor-pointer"
                            >
                              {sub.label}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Cosa vedere */}
          <section
            ref={tolentinoAttractions.ref}
            className={`fade-in-up ${tolentinoAttractions.isVisible ? 'visible' : ''} py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Esperienze</p>
              <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-12 leading-snug">
                Cosa vedere e vivere a Tolentino
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {TOLENTINO_ATTRACTIONS.map((attr) => (
                  <article key={attr.title} className="bg-ivory-50 rounded-2xl p-7 border border-ivory-200 flex flex-col gap-4">
                    <div className="w-11 h-11 flex items-center justify-center text-terracotta-300 bg-white rounded-xl border border-ivory-200">
                      <i className={`${attr.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-serif text-lg text-anthracite-800 font-medium leading-snug">{attr.title}</h3>
                    <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{attr.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* ── SAN SEVERINO ONLY ── */}
      {isSanSeverino && (
        <>
          {/* Intro + arte + storia */}
          <section
            ref={sanSeverinoParagraph.ref}
            className={`fade-in-up ${sanSeverinoParagraph.isVisible ? 'visible' : ''} max-w-7xl mx-auto px-6 py-20`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div>
                <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Il borgo</p>
                <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                  San Severino Marche: l&apos;antica Septempeda tra Piazza del Popolo e Castello al Monte
                </h2>
                <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                  San Severino Marche è una città d&apos;arte dell&apos;entroterra maceratese, attraversata dal fiume Potenza e posta in equilibrio tra Adriatico e monti Sibillini. Il suo patrimonio storico e paesaggistico è insolitamente ricco: dal centro storico raccolto attorno a Piazza del Popolo, con la sua caratteristica forma ellittica, fino a Castello al Monte, che domina dall&apos;alto con la Torre degli Smeducci, le fortificazioni e il museo archeologico.
                </p>
                <div className="border-l-2 border-terracotta-200 pl-6 mb-8">
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">L&apos;arte e la storia: una città museo</p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    San Severino Marche è legata in modo profondo ai fratelli Lorenzo e Jacopo Salimbeni, artisti settempedani tra la fine del Trecento e il primo Quattrocento, a cui la città dedica uno spazio importante all&apos;interno della Pinacoteca civica &quot;P. Tacchi Venturi&quot;. La raccolta conserva opere che vanno dal XIV al XVII secolo ed è considerata una delle raccolte d&apos;arte antica più preziose delle Marche.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    A questa stratificazione artistica si aggiunge quella archeologica. L&apos;antica Septempeda, da cui deriva l&apos;identità storica della città, si estende a est dell&apos;attuale centro abitato: nell&apos;area archeologica sono visibili la porta urbica, tratti delle mura, il complesso termale e le fornaci per la produzione di ceramica. San Severino conserva così un dialogo raro tra città medievale e radici romane.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed">
                    Tra i luoghi più suggestivi del territorio rientrano anche Sant&apos;Eustachio, complesso monastico di origine antica incastonato nella roccia, e l&apos;insieme monumentale di Castello al Monte, che contribuisce a definire il profilo storico della città.
                  </p>
                </div>
                <div className="border-l-2 border-terracotta-200 pl-6">
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Un nodo strategico nell&apos;entroterra marchigiano</p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    San Severino Marche si trova nella valle del Potenza ed è ben collegata con il resto della provincia. È una base interessante per vivere l&apos;entroterra marchigiano, tra paesaggio collinare, rilievi montani e itinerari naturalistici. Più che su distanze rigidamente numeriche, conviene puntare sul suo ruolo di cerniera tra costa e interno, che è anche il modo in cui viene descritta nelle fonti turistiche ufficiali.
                  </p>
                </div>
              </div>

              {/* Photo */}
              <div className="grid grid-cols-1 gap-4">
                <div className="rounded-xl overflow-hidden aspect-[16/9]">
                  <img
                    src="https://static.readdy.ai/image/13e0fae89bbbd02d36954a6d83bda5fb/608aac6aec614bbd2bd802178ebacdd7.png"
                    alt="San Severino Marche piazza medievale"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Vivere a San Severino */}
          <section
            ref={sanSeverinoLife.ref}
            className={`fade-in-up ${sanSeverinoLife.isVisible ? 'visible' : ''} bg-ivory-50 py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div>
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Vivere a San Severino</p>
                  <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                    Posizione, mercato e qualità della vita
                  </h2>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                    San Severino Marche si trova nella valle del Potenza ed è ben collegata con il resto della provincia di Macerata. È una base interessante per vivere l&apos;entroterra marchigiano, tra paesaggio collinare, rilievi montani e itinerari naturalistici verso i Sibillini.
                  </p>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed">
                    Dal punto di vista immobiliare, San Severino Marche presenta valori medi inferiori alla media provinciale. A marzo 2026 il prezzo medio richiesto per gli immobili residenziali in vendita era di 972 €/m², contro una media provinciale di 1.397 €/m². Un mercato accessibile, con palazzi storici nel centro, case in collina con vista sulle vallate e rustici nelle campagne circostanti.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-5">
                  {SAN_SEVERINO_STATS.map((stat) => (
                    <div key={stat.label} className="p-6 bg-white rounded-2xl border border-ivory-200 flex flex-col gap-3">
                      <div className="w-10 h-10 flex items-center justify-center text-terracotta-300">
                        <i className={`${stat.icon} text-2xl`}></i>
                      </div>
                      <p className="font-serif text-2xl text-anthracite-800 font-light">{stat.value}</p>
                      <p className="font-sans text-xs text-anthracite-400 tracking-wide uppercase">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Fonti */}
          <section className="max-w-7xl mx-auto px-6 pb-2 pt-0">
            <div className="border-l-2 border-ivory-200 pl-6">
              <p className="font-sans text-xs tracking-widest uppercase text-anthracite-400 mb-4">Fonti</p>
              <ul className="flex flex-col gap-2">
                {[
                  { label: 'italia.it — San Severino Marche', url: 'https://www.italia.it/it/marche/san-severino-marche' },
                  { label: 'Comune di San Severino Marche — Pinacoteca civica "P. Tacchi Venturi"', url: 'https://www.comune.sanseverinomarche.mc.it/vivere-il-comune/luoghi/pinacoteca-civica-p-tacchi-venturi/' },
                  { label: 'SABAP Ascoli Piceno — Apertura area archeologica di Septempeda', url: 'https://sabapascoli.cultura.gov.it/apertura-dellarea-archeologica-di-septempeda-san-severino-marche-mc-sabato-27-settembre-2025/' },
                  { label: 'cultura.gov.it — Pinacoteca comunale "P. Tacchi Venturi"', url: 'https://cultura.gov.it/luogo/pinacoteca-comunale-p-tacchi-venturi' },
                ].map((source) => (
                  <li key={source.url} className="flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-external-link-line text-xs text-terracotta-300"></i>
                    </div>
                    <a
                      href={source.url}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors leading-relaxed break-all cursor-pointer"
                    >
                      {source.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Cosa vedere */}
          <section
            ref={sanSeverinoAttractions.ref}
            className={`fade-in-up ${sanSeverinoAttractions.isVisible ? 'visible' : ''} py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Esperienze</p>
              <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-12 leading-snug">
                Cosa vedere e vivere a San Severino Marche
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {SAN_SEVERINO_ATTRACTIONS.map((attr) => (
                  <article key={attr.title} className="bg-ivory-50 rounded-2xl p-7 border border-ivory-200 flex flex-col gap-4">
                    <div className="w-11 h-11 flex items-center justify-center text-terracotta-300 bg-white rounded-xl border border-ivory-200">
                      <i className={`${attr.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-serif text-lg text-anthracite-800 font-medium leading-snug">{attr.title}</h3>
                    <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{attr.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* ── CAMPOCAVALLO ONLY ── */}
      {isCampocavallo && (
        <>
          {/* Intro + Santuario */}
          <section
            ref={campocavalloParagraph.ref}
            className={`fade-in-up ${campocavalloParagraph.isVisible ? 'visible' : ''} max-w-7xl mx-auto px-6 py-20`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div>
                <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Il borgo</p>
                <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                  Campocavallo: santuario, campagna e tradizione nelle colline osimane
                </h2>
                <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                  Campocavallo è una frazione del Comune di Osimo, nella vallata del Musone, e conserva un&apos;identità molto riconoscibile: quella di un luogo in cui la devozione popolare, la memoria rurale e la vita di comunità si intrecciano in modo naturale. Ancora oggi il suo nome è legato soprattutto al Santuario della Beata Vergine Addolorata, uno dei luoghi religiosi più importanti del territorio osimano.
                </p>
                <div className="border-l-2 border-terracotta-200 pl-6 mb-8">
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Il Santuario della Beata Vergine Addolorata</p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Alla fine dell&apos;Ottocento, Campocavallo era una contrada di campagna fatta di case coloniche sparse, campi coltivati e un semplice crocevia. Nazzareno Taddioli fece costruire la piccola chiesa che sarebbe poi diventata la Cappella del Prodigio, dove don Giovanni Sorbellini collocò sopra l&apos;altare un&apos;oleografia della Madonna Addolorata. Secondo la tradizione e la documentazione custodita dal santuario, il 16 e 17 giugno 1892 quell&apos;immagine fu vista lacrimare e muovere gli occhi da numerosi testimoni, dando origine a un&apos;imponente ondata di devozione popolare.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    La prima pietra fu posta il 10 dicembre 1892; i lavori iniziarono nel 1893, la consacrazione avvenne nel 1905 e il campanile fu completato nel 1913. Progettato dall&apos;architetto osimano Costantino Costantini in stile neogotico con influenze lombarde, l&apos;edificio colpisce per la mole, il laterizio a vista, la cupola e le ricche decorazioni in terracotta. All&apos;interno si trovano sette altari, vetrate istoriate e la tomba di don Giovanni Sorbellini.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed">
                    Campocavallo conserva una natura doppia: da una parte la campagna marchigiana con la sua memoria contadina; dall&apos;altra un santuario monumentale nato dalla devozione popolare. La Festa del Covo, ogni prima domenica di agosto, ne esprime la dimensione più comunitaria: un grande monumento votivo intrecciato a mano con 2-3 milioni di spighe di grano, portato in processione per le vie del paese. La tradizione nasce nel 1939 ed è il gesto identitario più forte della frazione.
                  </p>
                </div>
                <div className="border-l-2 border-terracotta-200 pl-6">
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Vivere a Campocavallo</p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Il santuario si trova a circa 2,5 km dal centro di Osimo, mentre la distanza stradale da Ancona è di circa 18 km. Campocavallo si colloca in una posizione intermedia tra la campagna osimana e la costa, permettendo di vivere un contesto più silenzioso pur restando a breve distanza dal mare e dalle località del Conero.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed">
                    Sul piano immobiliare, Campocavallo si inserisce nel mercato di Osimo. A marzo 2026 il prezzo medio richiesto per gli immobili residenziali in vendita nel comune era di 1.344 €/m², inferiore alla media della provincia di Ancona, pari a 1.602 €/m². Un contesto interessante per chi cerca un&apos;area residenziale più tranquilla, ben collegata e con una forte identità locale.
                  </p>
                </div>
              </div>

              {/* Photo grid */}
              <div className="grid grid-cols-1 gap-4">
                <div className="rounded-xl overflow-hidden aspect-[16/9]">
                  <img
                    src="https://static.readdy.ai/image/13e0fae89bbbd02d36954a6d83bda5fb/1987ae7006ec192eee5362e3843feea6.png"
                    alt="Santuario di Campocavallo"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Vivere a Campocavallo */}
          <section
            ref={campocavalloLife.ref}
            className={`fade-in-up ${campocavalloLife.isVisible ? 'visible' : ''} bg-ivory-50 py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div>
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Vivere a Campocavallo</p>
                  <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                    Posizione, accessibilità e mercato immobiliare
                  </h2>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                    Il santuario si trova a circa 2,5 km dal centro di Osimo, mentre la distanza stradale da Ancona è di circa 18 km. Questo rende la frazione adatta a chi cerca una dimensione più tranquilla, senza rinunciare alla vicinanza con la città, i servizi e i principali collegamenti.
                  </p>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                    Anche la Riviera del Conero resta facilmente raggiungibile. Campocavallo si colloca in una posizione intermedia tra la campagna osimana e la costa, e permette di vivere un contesto più silenzioso pur restando a breve distanza dal mare e dalle località del Conero.
                  </p>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed">
                    Sul piano immobiliare, Campocavallo si inserisce nel mercato di Osimo. A marzo 2026 il prezzo medio richiesto per gli immobili residenziali in vendita nel comune era di 1.344 €/m², inferiore alla media della provincia di Ancona, pari a 1.602 €/m². È quindi un contesto interessante per chi cerca un&apos;area residenziale più tranquilla, ben collegata e con una forte identità locale.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-5">
                  {CAMPOCAVALLO_STATS.map((stat) => (
                    <div key={stat.label} className="p-6 bg-white rounded-2xl border border-ivory-200 flex flex-col gap-3">
                      <div className="w-10 h-10 flex items-center justify-center text-terracotta-300">
                        <i className={`${stat.icon} text-2xl`}></i>
                      </div>
                      <p className="font-serif text-2xl text-anthracite-800 font-light">{stat.value}</p>
                      <p className="font-sans text-xs text-anthracite-400 tracking-wide uppercase">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Fonti Campocavallo */}
          <section className="max-w-7xl mx-auto px-6 pb-2 pt-0">
            <div className="border-l-2 border-ivory-200 pl-6">
              <p className="font-sans text-xs tracking-widest uppercase text-anthracite-400 mb-4">Fonti</p>
              <ul className="flex flex-col gap-2">
                {([
                  { label: 'santuariocampocavallo.it', url: 'https://www.santuariocampocavallo.it/', sub: [
                    { label: 'Storia del Santuario', url: 'https://www.santuariocampocavallo.it/storia/' },
                    { label: 'Il Prodigio del 1892', url: 'https://www.santuariocampocavallo.it/il-prodigio/' },
                    { label: 'La Festa del Covo', url: 'https://www.santuariocampocavallo.it/festa-del-covo/' },
                  ]},
                  { label: 'comune.osimo.an.it', url: 'https://www.comune.osimo.an.it/' },
                  { label: 'immobiliare.it — Mercato Osimo', url: 'https://www.immobiliare.it/mercato-immobiliare/marche/osimo/' },
                  { label: 'immobiliare.it — Mercato Provincia di Ancona', url: 'https://www.immobiliare.it/mercato-immobiliare/marche/ancona-provincia/' },
                ] as Array<{ label: string; url: string; sub?: Array<{ label: string; url: string }> }>).map((source) => (
                  <li key={source.url} className="flex flex-col gap-1">
                    <div className="flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <i className="ri-external-link-line text-xs text-terracotta-300"></i>
                      </div>
                      <a
                        href={source.url}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors leading-relaxed break-all cursor-pointer"
                      >
                        {source.label}
                      </a>
                    </div>
                    {source.sub && (
                      <ul className="ml-6 flex flex-col gap-1">
                        {source.sub.map((sub) => (
                          <li key={sub.url} className="flex items-start gap-2">
                            <div className="w-3 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                              <i className="ri-corner-down-right-line text-xs text-anthracite-300"></i>
                            </div>
                            <a
                              href={sub.url}
                              target="_blank"
                              rel="nofollow noopener noreferrer"
                              className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors leading-relaxed break-all cursor-pointer"
                            >
                              {sub.label}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Cosa vedere */}
          <section
            ref={campocavalloAttractions.ref}
            className={`fade-in-up ${campocavalloAttractions.isVisible ? 'visible' : ''} py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Esperienze</p>
              <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-12 leading-snug">
                Cosa vedere e vivere a Campocavallo
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
                {CAMPOCAVALLO_ATTRACTIONS.map((attr) => (
                  <article key={attr.title} className="bg-ivory-50 rounded-2xl p-7 border border-ivory-200 flex flex-col gap-4">
                    <div className="w-11 h-11 flex items-center justify-center text-terracotta-300 bg-white rounded-xl border border-ivory-200">
                      <i className={`${attr.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-serif text-lg text-anthracite-800 font-medium leading-snug">{attr.title}</h3>
                    <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{attr.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* Video Campocavallo */}
          <section className="max-w-7xl mx-auto px-6 pb-20">
            <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Video</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="w-full rounded-2xl overflow-hidden border border-ivory-200 aspect-[9/16]">
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/sZA9fiGsYbM"
                  title="Video Campocavallo 1"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
              <div className="w-full rounded-2xl overflow-hidden border border-ivory-200 aspect-[9/16]">
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/7_f15QwBrAQ"
                  title="Video Campocavallo 2"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
              <div className="w-full rounded-2xl overflow-hidden border border-ivory-200 aspect-[9/16]">
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/J_kD2WfRzRs"
                  title="Video Campocavallo 3"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
            </div>
          </section>
        </>
      )}

      {/* ── STAFFOLO ONLY ── */}
      {isStaffolo && (
        <>
          {/* Intro + storia + Verdicchio */}
          <section
            ref={staffoloParagraph.ref}
            className={`fade-in-up ${staffoloParagraph.isVisible ? 'visible' : ''} max-w-7xl mx-auto px-6 py-20`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div>
                <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Il borgo</p>
                <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                  Staffolo: il balcone della Vallesina tra Verdicchio e mura medievali
                </h2>
                <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                  Staffolo è un comune collinare della provincia di Ancona, posto tra il corso del Musone e quello dell&apos;Esino. Il nucleo più antico sorge a circa 450 metri di altitudine, con un impianto medievale compatto e panoramico che gli ha fatto guadagnare il nome di &quot;balcone della Vallesina&quot;. Da qui lo sguardo si apre sulle colline coltivate, sui vigneti e, nelle giornate più limpide, fino all&apos;Adriatico.
                </p>
                <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                  Il legame con il vino è uno dei tratti più forti dell&apos;identità locale. Nel territorio di Staffolo prevale la coltivazione di vitigni come Verdicchio, Montepulciano e Sangiovese, e il borgo rientra pienamente nel sistema dei Castelli del Verdicchio, uno dei paesaggi enologici più rappresentativi delle Marche.
                </p>
                <div className="border-l-2 border-terracotta-200 pl-6 mb-8">
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Il Museo del Vino e dell&apos;Arte Contadina</p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Nel centro storico si trova il Museo del Vino e dell&apos;Arte Contadina, con annessa Enoteca Comunale. Il percorso espositivo racconta la tradizione vitivinicola del territorio attraverso strumenti storici di cantina, attrezzi da lavoro e materiali legati alla produzione del vino; tra i pezzi più noti viene ricordato anche un antico torchio del 1695.
                  </p>
                </div>
                <div className="border-l-2 border-terracotta-200 pl-6">
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Il borgo medievale e il suo patrimonio</p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Staffolo conserva un impianto medievale ben leggibile: il centro storico ha una forma ellittica ed è ancora cinto da mura in parte originali, aperte in due accessi contrapposti, Porta Venezia a nord e Porta San Martino a sud. Sulla cinta muraria si distingue il torrione detto dell&apos;Albornoz, risalente al XIV secolo.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed">
                    All&apos;ingresso del paese si apre Piazza Leopardi, affiancata dalle chiese di Sant&apos;Egidio Abate e San Francesco. Staffolo è inoltre Bandiera Arancione del Touring Club Italiano, riconoscimento che valorizza la qualità del centro storico, dell&apos;accoglienza e del contesto paesaggistico.
                  </p>
                </div>
              </div>

              {/* Photo grid */}
              <div className="grid grid-cols-1 gap-4">
                <div className="rounded-xl overflow-hidden aspect-[16/9]">
                  <img
                    src="https://static.readdy.ai/image/13e0fae89bbbd02d36954a6d83bda5fb/f925a754683135bbf59140192f7d9d62.png"
                    alt="Staffolo borgo medievale e vigneti"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Vivere a Staffolo */}
          <section
            ref={staffoloLife.ref}
            className={`fade-in-up ${staffoloLife.isVisible ? 'visible' : ''} bg-ivory-50 py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div>
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Vivere a Staffolo</p>
                  <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                    Pace, paesaggio e accessibilità
                  </h2>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                    Staffolo conta 2.168 abitanti al 1° gennaio 2025 e mantiene una dimensione raccolta, adatta a chi cerca tranquillità senza isolamento. Jesi si raggiunge in circa 16-18 km su strada, mentre Ancona è raggiungibile in auto in circa 37 minuti. Il borgo resta quindi ben collegato, pur conservando un ritmo di vita lento e fortemente legato al paesaggio rurale.
                  </p>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed">
                    Anche il mercato immobiliare conferma il profilo di Staffolo come località accessibile. A marzo 2026 il prezzo medio richiesto per gli immobili residenziali in vendita era di 773 €/m², nettamente inferiore alla media della provincia di Ancona, pari a 1.602 €/m². È quindi una realtà interessante per chi cerca un contesto storico e paesaggistico autentico, con valori ancora contenuti rispetto ad altre aree della provincia.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-5">
                  {STAFFOLO_STATS.map((stat) => (
                    <div key={stat.label} className="p-6 bg-white rounded-2xl border border-ivory-200 flex flex-col gap-3">
                      <div className="w-10 h-10 flex items-center justify-center text-terracotta-300">
                        <i className={`${stat.icon} text-2xl`}></i>
                      </div>
                      <p className="font-serif text-2xl text-anthracite-800 font-light">{stat.value}</p>
                      <p className="font-sans text-xs text-anthracite-400 tracking-wide uppercase">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Fonti Staffolo */}
          <section className="max-w-7xl mx-auto px-6 pb-2 pt-0">
            <div className="border-l-2 border-ivory-200 pl-6">
              <p className="font-sans text-xs tracking-widest uppercase text-anthracite-400 mb-4">Fonti</p>
              <ul className="flex flex-col gap-2">
                {([
                  { label: 'castellidelverdicchio.it — Staffolo', url: 'https://www.castellidelverdicchio.it/it/comune/comune/11/', sub: [
                    { label: 'Struttura ricettiva (1)', url: 'https://www.castellidelverdicchio.it/it/struttura/ric/154/' },
                    { label: 'Struttura ricettiva (2)', url: 'https://www.castellidelverdicchio.it/it/struttura/ric/440/' },
                  ]},
                  { label: 'touringclub.it — Staffolo Bandiera Arancione', url: 'https://www.touringclub.it/destinazioni/staffolo' },
                  { label: 'regione.marche.it — Museo del Vino ed Enoteca Comunale', url: 'https://www.regione.marche.it/Regione-Utile/Cultura/Ricerca-Musei/Id/56/STAFFOLO-Museo-del-Vino-Enoteca-Comunale' },
                  { label: 'catalogoviti.politicheagricole.it — Verdicchio dei Castelli di Jesi DOC', url: 'https://catalogoviti.politicheagricole.it/scheda_denom.php?q=2320&t=dsc' },
                  { label: 'tuttitalia.it — Staffolo', url: 'https://www.tuttitalia.it/marche/37-staffolo/' },
                  { label: 'rome2rio.com — Staffolo → Jesi', url: 'https://www.rome2rio.com/it/s/Staffolo/Jesi' },
                  { label: 'rome2rio.com — Staffolo → Ancona', url: 'https://www.rome2rio.com/it/s/Staffolo/Ancona' },
                  { label: 'viamichelin.it — Staffolo → Jesi (itinerario)', url: 'https://www.viamichelin.it/itinerari/risultati/staffolo-60039-ancona-marche-repubblica_italiana-to-jesi-60035-ancona-marche-repubblica_italiana' },
                ] as Array<{ label: string; url: string; sub?: Array<{ label: string; url: string }> }>).map((source) => (
                  <li key={source.url} className="flex flex-col gap-1">
                    <div className="flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <i className="ri-external-link-line text-xs text-terracotta-300"></i>
                      </div>
                      <a
                        href={source.url}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors leading-relaxed break-all cursor-pointer"
                      >
                        {source.label}
                      </a>
                    </div>
                    {source.sub && (
                      <ul className="ml-6 flex flex-col gap-1">
                        {source.sub.map((sub) => (
                          <li key={sub.url} className="flex items-start gap-2">
                            <div className="w-3 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                              <i className="ri-corner-down-right-line text-xs text-anthracite-300"></i>
                            </div>
                            <a
                              href={sub.url}
                              target="_blank"
                              rel="nofollow noopener noreferrer"
                              className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors leading-relaxed break-all cursor-pointer"
                            >
                              {sub.label}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Cosa vedere */}
          <section
            ref={staffoloAttractions.ref}
            className={`fade-in-up ${staffoloAttractions.isVisible ? 'visible' : ''} py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Esperienze</p>
              <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-12 leading-snug">
                Cosa vedere e vivere a Staffolo
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {STAFFOLO_ATTRACTIONS.map((attr) => (
                  <article key={attr.title} className="bg-ivory-50 rounded-2xl p-7 border border-ivory-200 flex flex-col gap-4">
                    <div className="w-11 h-11 flex items-center justify-center text-terracotta-300 bg-white rounded-xl border border-ivory-200">
                      <i className={`${attr.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-serif text-lg text-anthracite-800 font-medium leading-snug">{attr.title}</h3>
                    <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{attr.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* Video Staffolo */}
          <section className="max-w-7xl mx-auto px-6 pt-10 pb-20">
            <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Video</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="max-w-sm rounded-2xl overflow-hidden border border-ivory-200 aspect-[9/16]">
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/lUjZw5d6qtk"
                  title="Video Staffolo 1"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
              <div className="max-w-sm rounded-2xl overflow-hidden border border-ivory-200 aspect-[9/16]">
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/rX-rvFCFrO0"
                  title="Video Staffolo 2"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
              <div className="max-w-sm rounded-2xl overflow-hidden border border-ivory-200 aspect-[9/16]">
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/fmAzEPCSAm4"
                  title="Video Staffolo 3"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
              <div className="max-w-sm rounded-2xl overflow-hidden border border-ivory-200 aspect-[9/16]">
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/448PaZ482Yc"
                  title="Video Staffolo 4"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
              <div className="max-w-sm rounded-2xl overflow-hidden border border-ivory-200 aspect-[9/16]">
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/JIIWNNu5_6w"
                  title="Video Staffolo 5"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
            </div>
          </section>

          {/* Link immobile Staffolo */}
          <section className="max-w-7xl mx-auto px-6 pb-20">
            <div className="border-l-2 border-terracotta-200 pl-6">
              <p className="font-sans text-sm font-medium tracking-widest uppercase text-terracotta-300 mb-4">Proprietà in vendita</p>
              <div className="flex flex-col md:flex-row gap-6 items-start">
                <div className="w-full md:w-72 flex-shrink-0 rounded-xl overflow-hidden border border-ivory-200">
                  <img
                    src="https://storage.readdy-site.link/project_files/eb454c64-f721-4c2b-844b-992754102f07/8f20012a-b277-475c-ac52-381fa72ee2e1_facciata-1.1.png?v=1576752e4a7af256f18555e5329f6dab"
                    alt="Facciata dell'appartamento in vendita a Staffolo"
                    className="w-full h-48 object-cover object-center"
                  />
                </div>
                <div className="flex flex-col gap-3">
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed">
                    Appartamento di 9 locali e 207 m² in vendita a Staffolo, nel cuore delle colline marchigiane.
                  </p>
                  <a
                    href="https://www.iad-italia.it/annuncio/appartamento-vendita-9-locali-staffolo-207m2/r163243"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="font-sans text-sm text-terracotta-300 hover:text-terracotta-400 transition-colors leading-relaxed break-all cursor-pointer inline-flex items-center gap-2"
                  >
                    Vedi annuncio su IAD Italia
                    <i className="ri-external-link-line"></i>
                  </a>
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* ── PETRITOLI ONLY ── */}
      {isPetritoli && (
        <>
          {/* Intro + ceramica + paesaggio */}
          <section
            ref={petritoloParagraph.ref}
            className={`fade-in-up ${petritoloParagraph.isVisible ? 'visible' : ''} max-w-7xl mx-auto px-6 py-20`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div>
                <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Il borgo</p>
                <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                  Petritoli: il borgo panoramico della Valdaso tra teatro, storia e colline
                </h2>
                <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                  Petritoli è un borgo collinare della provincia di Fermo, affacciato sulla valle dell&apos;Aso e posto a 358 metri sul livello del mare. Le fonti ufficiali lo descrivono come un centro panoramico da cui lo sguardo arriva dai Monti Sibillini fino alla costa adriatica, con un impianto storico ancora molto leggibile e un accesso monumentale tra i più riconoscibili del territorio.
                </p>
                <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                  L&apos;ingresso al borgo è uno dei suoi segni distintivi: una porta scenografica formata da tre archi gotici racchiusi tra due torri cilindriche del Quattrocento. All&apos;interno, il centro storico conserva edifici civili e religiosi di rilievo, in un insieme compatto che rende Petritoli uno dei borghi più interessanti del fermano. Il paese fa inoltre parte del circuito dei Borghi più Belli d&apos;Italia.
                </p>
                <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                  Più che alla ceramica, l&apos;identità documentata di Petritoli oggi è legata soprattutto al suo patrimonio architettonico, al Teatro dell&apos;Iride, alla dimensione culturale del borgo e alla sua notorietà recente come &quot;borgo dei matrimoni&quot;, espressione usata anche dal Comune nella promozione territoriale.
                </p>
                <div className="border-l-2 border-terracotta-200 pl-6 mb-8">
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Il borgo storico e i suoi luoghi principali</p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Tra gli edifici più importanti c&apos;è Santa Maria in Piazza, ricostruita nel Cinquecento su un edificio più antico. Accanto a questa emergono anche la Chiesa di Sant&apos;Andrea, nota per gli stucchi settecenteschi, e la Chiesa di San Prospero, indicata dalle fonti de I Borghi più Belli d&apos;Italia come l&apos;edificio religioso più antico del paese, risalente al XIII secolo. Poco fuori dal centro storico si trova inoltre il Santuario della Madonna della Liberata, le cui origini vengono fatte risalire attorno all&apos;anno Mille.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Un altro luogo simbolo del borgo è il Teatro dell&apos;Iride, piccolo teatro storico della seconda metà dell&apos;Ottocento progettato dall&apos;ingegner Giuseppe Sabbatini. Oggi rappresenta uno dei poli culturali più identitari di Petritoli e continua a ospitare stagioni teatrali e musicali.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed">
                    Nel centro storico trovano spazio anche la Torre Civica, completata nel 1832, l&apos;Antica Stamperia Fabiani, la Biblioteca Comunale Luigi Mannocchi e diversi edifici storici che rafforzano il profilo culturale del paese.
                  </p>
                </div>
              </div>

              {/* Photo */}
              <div className="grid grid-cols-1 gap-4">
                <div className="rounded-xl overflow-hidden aspect-[16/9]">
                  <img
                    src="https://static.readdy.ai/image/13e0fae89bbbd02d36954a6d83bda5fb/4a64cb3c68db28d277e1aa719dd7dff1.png"
                    alt="Petritoli borgo medievale e Valdaso"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Vivere a Petritoli */}
          <section
            ref={petritoloLife.ref}
            className={`fade-in-up ${petritoloLife.isVisible ? 'visible' : ''} bg-ivory-50 py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div>
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Vivere a Petritoli</p>
                  <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                    Posizione, paesaggio e mercato immobiliare
                  </h2>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                    Petritoli conta 2.115 abitanti al 1° gennaio 2025 e conserva una dimensione raccolta. La distanza da Fermo è di circa 20 km in linea d&apos;aria e 15,9 km su strada, con un tempo di percorrenza di circa 19 minuti in auto. Anche il mare è più vicino di quanto spesso si pensi: Porto San Giorgio dista circa 20,9 km su strada.
                  </p>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                    Il paesaggio resta uno dei suoi punti di forza: Petritoli domina la Valdaso e mantiene un rapporto molto diretto con le colline coltivate, gli oliveti e il mosaico agrario marchigiano. In questo senso il borgo è adatto a chi cerca un contesto storico e paesaggistico autentico, ma ben collegato con la costa e con i servizi del fermano.
                  </p>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed">
                    Sul piano immobiliare, Petritoli rientra tra i comuni più accessibili della provincia. A marzo 2026 il prezzo medio richiesto per gli immobili residenziali in vendita era di 560 €/m², contro una media provinciale di 1.270 €/m². È quindi corretto descriverlo come un borgo con valori medi contenuti, pur evitando promesse troppo spinte sul rendimento o sulla rivalutazione futura.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-5">
                  {PETRITOLI_STATS.map((stat) => (
                    <div key={stat.label} className="p-6 bg-white rounded-2xl border border-ivory-200 flex flex-col gap-3">
                      <div className="w-10 h-10 flex items-center justify-center text-terracotta-300">
                        <i className={`${stat.icon} text-2xl`}></i>
                      </div>
                      <p className="font-serif text-2xl text-anthracite-800 font-light">{stat.value}</p>
                      <p className="font-sans text-xs text-anthracite-400 tracking-wide uppercase">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Fonti Petritoli */}
          <section className="max-w-7xl mx-auto px-6 pb-2 pt-0">
            <div className="border-l-2 border-ivory-200 pl-6">
              <p className="font-sans text-xs tracking-widest uppercase text-anthracite-400 mb-4">Fonti</p>
              <ul className="flex flex-col gap-2">
                {([
                  { label: 'borghipiubelliditalia.it — Petritoli', url: 'https://borghipiubelliditalia.it/borgo/petritoli/' },
                  { label: 'italia.it — Petritoli', url: 'https://www.italia.it/en/marche/petritoli' },
                  { label: 'comune.petritoli.fm.it', url: 'https://www.comune.petritoli.fm.it/c109031/zf/index.php/servizi-aggiuntivi/index/index/idtesto/12', sub: [
                    { label: 'Scheda territorio (2)', url: 'https://www.comune.petritoli.fm.it/c109031/zf/index.php/servizi-aggiuntivi/index/index/idtesto/36' },
                    { label: 'Scheda territorio (3)', url: 'https://www.comune.petritoli.fm.it/c109031/zf/index.php/servizi-aggiuntivi/index/index/idtesto/112' },
                  ]},
                  { label: 'visitpetritoli.it', url: 'https://www.visitpetritoli.it/', sub: [
                    { label: 'Punti di interesse', url: 'https://www.visitpetritoli.it/poi/' },
                    { label: 'Chiesa di Sant\'Andrea', url: 'https://www.visitpetritoli.it/poi/chiesa-di-sant-andrea/' },
                    { label: 'Santuario della Liberata', url: 'https://www.visitpetritoli.it/poi/santuario-liberata/' },
                  ]},
                  { label: 'touringclub.it — Teatro dell\'Iride', url: 'https://www.touringclub.it/destinazioni/petritoli/fare/310310-teatro-delliride' },
                  { label: 'tuttitalia.it — Petritoli', url: 'https://www.tuttitalia.it/marche/20-petritoli/' },
                  { label: 'rome2rio.com — Petritoli', url: 'https://www.rome2rio.com/it/s/Petritoli' },
                ] as Array<{ label: string; url: string; sub?: Array<{ label: string; url: string }> }>).map((source) => (
                  <li key={source.url} className="flex flex-col gap-1">
                    <div className="flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <i className="ri-external-link-line text-xs text-terracotta-300"></i>
                      </div>
                      <a
                        href={source.url}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors leading-relaxed break-all cursor-pointer"
                      >
                        {source.label}
                      </a>
                    </div>
                    {source.sub && (
                      <ul className="ml-6 flex flex-col gap-1">
                        {source.sub.map((sub) => (
                          <li key={sub.url} className="flex items-start gap-2">
                            <div className="w-3 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                              <i className="ri-corner-down-right-line text-xs text-anthracite-300"></i>
                            </div>
                            <a
                              href={sub.url}
                              target="_blank"
                              rel="nofollow noopener noreferrer"
                              className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors leading-relaxed break-all cursor-pointer"
                            >
                              {sub.label}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Cosa vedere */}
          <section
            ref={petritoloAttractions.ref}
            className={`fade-in-up ${petritoloAttractions.isVisible ? 'visible' : ''} py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Esperienze</p>
              <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-12 leading-snug">
                Cosa vedere e vivere a Petritoli
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {PETRITOLI_ATTRACTIONS.map((attr) => (
                  <article key={attr.title} className="bg-ivory-50 rounded-2xl p-7 border border-ivory-200 flex flex-col gap-4">
                    <div className="w-11 h-11 flex items-center justify-center text-terracotta-300 bg-white rounded-xl border border-ivory-200">
                      <i className={`${attr.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-serif text-lg text-anthracite-800 font-medium leading-snug">{attr.title}</h3>
                    <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{attr.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* ── SERVIGLIANO ONLY ── */}
      {isServigliano && (
        <>
          {/* Intro + impianto urbano */}
          <section
            ref={serviglianoP.ref}
            className={`fade-in-up ${serviglianoP.isVisible ? 'visible' : ''} max-w-7xl mx-auto px-6 py-20`}
          >
            <div className="flex flex-col gap-12">
              {/* Riga 1: intro + immagine */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
                <div>
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Il borgo</p>
                  <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                    Servigliano: la città ideale tra piazza perfetta, memoria e colline fermane
                  </h2>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                    Servigliano è uno dei casi urbanistici più particolari delle Marche. Oggi fa parte del circuito dei Borghi più Belli d&apos;Italia ed è noto per il suo impianto regolare, quasi geometrico, che gli ha fatto guadagnare la definizione di &quot;città ideale&quot;. Più che un borgo medievale cresciuto per stratificazioni, Servigliano è un centro pianificato, compatto e riconoscibile, costruito nella media valle del Tenna e ancora oggi leggibile nella sua forma originaria.
                  </p>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed">
                    La sua storia ha un momento decisivo nel 1771, quando il vecchio abitato franò e il paese venne ricostruito per volontà di Papa Clemente XIV. Il nuovo centro prese il nome di Castel Clementino e mantenne questa denominazione fino al 1863, quando recuperò il nome di Servigliano. Le fonti del Club dei Borghi più Belli d&apos;Italia attribuiscono il nuovo impianto a Virginio Bracci e ricordano che il borgo fu poi completato sotto Pio VI.
                  </p>
                </div>
                <div className="rounded-xl overflow-hidden aspect-[16/9]">
                  <img
                    src="https://static.readdy.ai/image/13e0fae89bbbd02d36954a6d83bda5fb/43227bf7322bf990c13fdcee9de16932.png"
                    alt="Servigliano la città ideale delle Marche"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>

              {/* Riga 2: impianto urbano + memoria civile affiancati */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
                <div className="border-l-2 border-terracotta-200 pl-6">
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Un impianto urbano unico nelle Marche</p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Il centro storico di Servigliano ha una struttura quadrangolare chiusa, di circa 144 per 137 metri, con tre porte monumentali e due assi principali che si incrociano secondo un disegno ortogonale. Le fonti del Club dei Borghi più Belli d&apos;Italia sottolineano come questa impostazione architettonica richiami il simbolo cristiano della croce e rappresenti in Europa un esempio quasi unico.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Il cuore del borgo è Piazza Roma, su cui si affacciano la Collegiata di San Marco e il Palazzo Comunale. La Collegiata conserva un organo settecentesco Fedeli e le reliquie di San Servigliano e San Gualtiero; poco distante si trova anche Palazzo Vecchiotti, edificio signorile di tre piani con altana.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed">
                    Tra i luoghi da vedere c&apos;è anche il recuperato complesso dei Frati Minori Osservanti con il Santuario di Santa Maria del Piano, che le fonti del Club dei Borghi più Belli d&apos;Italia indicano come costruito su resti di una villa romana del I secolo.
                  </p>
                </div>
                <div className="border-l-2 border-terracotta-200 pl-6">
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Memoria civile e identità contemporanea</p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Servigliano non è solo architettura ideale. Nel Novecento il paese è stato segnato in modo profondo dalla presenza dell&apos;ex campo di prigionia, oggi Parco della Pace, attivo in momenti diversi della prima e della seconda guerra mondiale e poi come centro per profughi.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed">
                    La Casa della Memoria, ospitata nell&apos;ex stazione ferroviaria, conserva oggi un museo e una mostra fotografica permanente dedicata alla storia del campo e dei suoi prigionieri. È uno degli aspetti più forti e meno scontati dell&apos;identità di Servigliano.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Vivere a Servigliano */}
          <section
            ref={serviglianoLife.ref}
            className={`fade-in-up ${serviglianoLife.isVisible ? 'visible' : ''} bg-ivory-50 py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div>
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Vivere a Servigliano</p>
                  <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                    Posizione, paesaggio e mercato immobiliare
                  </h2>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                    Servigliano conta 2.169 abitanti al 1° gennaio 2025 e si trova a 215 metri sul livello del mare. È un comune raccolto ma ben posizionato nella provincia di Fermo, tra collina, valle e collegamenti con la costa e con l&apos;entroterra. Il collegamento con Fermo richiede circa 26-27 minuti in auto, mentre Porto San Giorgio si raggiunge in circa 33 minuti.
                  </p>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed">
                    Anche il mercato immobiliare mantiene un profilo accessibile. A marzo 2026 il prezzo medio richiesto per gli immobili residenziali in vendita era di 902 €/m², contro una media provinciale di 1.270 €/m². È quindi corretto descrivere Servigliano come una realtà storica e architettonica di qualità, con valori medi inferiori alla media della provincia di Fermo.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-5">
                  {SERVIGLIANO_STATS.map((stat) => (
                    <div key={stat.label} className="p-6 bg-white rounded-2xl border border-ivory-200 flex flex-col gap-3">
                      <div className="w-10 h-10 flex items-center justify-center text-terracotta-300">
                        <i className={`${stat.icon} text-2xl`}></i>
                      </div>
                      <p className="font-serif text-2xl text-anthracite-800 font-light">{stat.value}</p>
                      <p className="font-sans text-xs text-anthracite-400 tracking-wide uppercase">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Cosa vedere */}
          <section
            ref={serviglianoAttractions.ref}
            className={`fade-in-up ${serviglianoAttractions.isVisible ? 'visible' : ''} py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Esperienze</p>
              <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-12 leading-snug">
                Cosa vedere e vivere a Servigliano
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {SERVIGLIANO_ATTRACTIONS.map((attr) => (
                  <article key={attr.title} className="bg-ivory-50 rounded-2xl p-7 border border-ivory-200 flex flex-col gap-4">
                    <div className="w-11 h-11 flex items-center justify-center text-terracotta-300 bg-white rounded-xl border border-ivory-200">
                      <i className={`${attr.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-serif text-lg text-anthracite-800 font-medium leading-snug">{attr.title}</h3>
                    <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{attr.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* Fonti Servigliano */}
          <section className="max-w-7xl mx-auto px-6 pb-2 pt-0">
            <div className="border-l-2 border-ivory-200 pl-6">
              <p className="font-sans text-xs tracking-widest uppercase text-anthracite-400 mb-4">Fonti</p>
              <ul className="flex flex-col gap-2">
                {([
                  {
                    label: 'borghipiubelliditalia.it — Servigliano',
                    url: 'https://borghipiubelliditalia.it/borgo/servigliano/',
                  },
                  {
                    label: 'italia.it — Servigliano',
                    url: 'https://www.italia.it/it/marche/servigliano',
                  },
                  {
                    label: 'comune.servigliano.fm.it',
                    url: 'https://comune.servigliano.fm.it/',
                    sub: [
                      { label: 'Scheda territorio', url: 'https://www.halleyweb.com/c044069/zf/index.php/servizi-aggiuntivi/index/index/idtesto/17' },
                    ],
                  },
                  {
                    label: 'tuttitalia.it — Servigliano',
                    url: 'https://www.tuttitalia.it/marche/81-servigliano/',
                  },
                  {
                    label: 'fondoambiente.it — Collegiata di San Marco',
                    url: 'https://fondoambiente.it/luoghi/collegiata-san-marco',
                  },
                  {
                    label: 'lacasadellamemoria.com',
                    url: 'https://www.lacasadellamemoria.com/',
                    sub: [
                      { label: 'Archivio storico', url: 'https://www.lacasadellamemoria.com/archivio/' },
                    ],
                  },
                  {
                    label: 'rome2rio.com — Servigliano → Fermo',
                    url: 'https://www.rome2rio.com/it/s/Servigliano/Fermo',
                    sub: [
                      { label: 'Servigliano → Porto San Giorgio', url: 'https://www.rome2rio.com/it/s/Servigliano/Porto-San-Giorgio' },
                    ],
                  },
                  {
                    label: 'immobiliare.it — Mercato Servigliano',
                    url: 'https://www.immobiliare.it/mercato-immobiliare/marche/servigliano/',
                    sub: [
                      { label: 'Mercato Provincia di Fermo', url: 'https://www.immobiliare.it/mercato-immobiliare/marche/fermo-provincia/' },
                    ],
                  },
                ] as Array<{ label: string; url: string; sub?: Array<{ label: string; url: string }> }>).map((source) => (
                  <li key={source.url} className="flex flex-col gap-1">
                    <div className="flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <i className="ri-external-link-line text-xs text-terracotta-300"></i>
                      </div>
                      <a
                        href={source.url}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors leading-relaxed break-all cursor-pointer"
                      >
                        {source.label}
                      </a>
                    </div>
                    {source.sub && (
                      <ul className="ml-6 flex flex-col gap-1">
                        {source.sub.map((sub) => (
                          <li key={sub.url} className="flex items-start gap-2">
                            <div className="w-3 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                              <i className="ri-corner-down-right-line text-xs text-anthracite-300"></i>
                            </div>
                            <a
                              href={sub.url}
                              target="_blank"
                              rel="nofollow noopener noreferrer"
                              className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors leading-relaxed break-all cursor-pointer"
                            >
                              {sub.label}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </>
      )}

      {/* ── ASPIO ONLY ── */}
      {isAspio && (
        <>
          {/* Intro + storia */}
          <section
            ref={aspioParagraph.ref}
            className={`fade-in-up ${aspioParagraph.isVisible ? 'visible' : ''} max-w-7xl mx-auto px-6 py-20`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div>
                <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Il territorio</p>
                <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-6 leading-snug">
                  Aspio: la località di cerniera tra Osimo, Ancona e il Conero
                </h2>
                <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                  Aspio non è un borgo monumentale nel senso classico del termine, ma una località dell&apos;area osimana che vive in una posizione strategica tra il capoluogo, la costa del Conero e il sistema dei quartieri sud di Osimo. Il Comune di Osimo la considera un ambito specifico del proprio territorio urbanistico e la include nel consiglio di quartiere San Biagio – Santo Stefano – Aspio, segno di un&apos;identità locale ben riconoscibile.
                </p>
                <p className="font-sans text-base text-anthracite-500 leading-relaxed">
                  Il carattere di Aspio è soprattutto quello di una zona di fondovalle e collegamento, attraversata dal torrente omonimo e vicina agli assi infrastrutturali principali. Più che per un centro storico compatto, Aspio si distingue per la praticità della posizione: da qui Osimo, Ancona e Camerano sono tutti facilmente raggiungibili, e anche la Riviera del Conero resta vicina.
                </p>
              </div>
              <div className="w-full h-[420px] rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=gentle%20rolling%20hills%20of%20Marche%20region%20Italy%20near%20Conero%20coast%20with%20green%20valleys%20and%20small%20rural%20roads%20connecting%20villages%20soft%20morning%20light%20warm%20earthy%20tones%20countryside%20landscape&width=800&height=420&seq=aspio-hero-01&orientation=landscape"
                  alt="Paesaggio collinare dell'area dell'Aspio tra Osimo e il Conero"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </section>

          {/* Territorio e servizi | Vivere ad Aspio */}
          <section className="max-w-7xl mx-auto px-6 pb-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Territorio, servizi e identità locale */}
              <div className="bg-stone-50 rounded-lg p-8">
                <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Territorio e identità</p>
                <h3 className="font-serif text-2xl text-anthracite-800 font-light mb-5 leading-snug">
                  Territorio, servizi e identità locale
                </h3>
                <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                  Uno dei riferimenti più riconoscibili della zona è la <strong className="text-anthracite-700">Parrocchia di San Giuseppe Lavoratore all&apos;Aspio</strong>, che appartiene alla diocesi di Ancona-Osimo. L&apos;edificio fu aperto ai fedeli il 1° maggio 1966, a poco più di un anno dall&apos;inizio dei lavori: un dato che racconta bene il carattere relativamente recente e residenziale di questa località rispetto ai borghi storici dell&apos;entroterra.
                </p>
                <p className="font-sans text-sm text-anthracite-500 leading-relaxed">
                  A rendere riconoscibile l&apos;area contribuisce anche la vicinanza con le <strong className="text-anthracite-700">Terme dell&apos;Aspio</strong>, nel territorio di Camerano. Le acque salsobromoiodiche delle sorgenti vicine al torrente Aspio erano conosciute già dalla fine dell&apos;Ottocento, mentre il 1930, anno della concessione mineraria, segna la nascita dello stabilimento termale moderno.
                </p>
              </div>

              {/* Vivere ad Aspio */}
              <div className="bg-stone-50 rounded-lg p-8">
                <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Vivere qui</p>
                <h3 className="font-serif text-2xl text-anthracite-800 font-light mb-5 leading-snug">
                  Vivere ad Aspio
                </h3>
                <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-5">
                  Aspio può essere interessante per chi cerca una zona ben collegata, più pratica che scenografica, ma comunque inserita nel paesaggio collinare che introduce al Conero. Aspio Terme dista circa 12 km da Osimo, con autobus diretti in media di 19 minuti, mentre Ancona si trova a circa 8 km, raggiungibile in autobus in circa 11 minuti o in treno da Camerano Aspio in circa 13 minuti.
                </p>
                <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-6">
                  Sul piano immobiliare, a marzo 2026 il prezzo medio richiesto per gli immobili residenziali in vendita a Osimo era di <strong className="text-anthracite-700">1.344 €/m²</strong>, inferiore alla media della provincia di Ancona pari a 1.602 €/m². Questo consente di raccontare Aspio come un&apos;area interessante per chi vuole restare nell&apos;orbita di Osimo e Ancona, con valori medi ancora sotto la media provinciale.
                </p>
                {/* Dati rapidi */}
                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-stone-200">
                  <div>
                    <p className="font-sans text-xs text-anthracite-400 mb-1">Quartiere / area</p>
                    <p className="font-serif text-sm text-anthracite-700 leading-snug">San Biagio – Santo Stefano – Aspio, Comune di Osimo</p>
                  </div>
                  <div>
                    <p className="font-sans text-xs text-anthracite-400 mb-1">Distanza da Osimo</p>
                    <p className="font-serif text-xl text-terracotta-400 font-light">~12 km</p>
                  </div>
                  <div>
                    <p className="font-sans text-xs text-anthracite-400 mb-1">Distanza da Ancona</p>
                    <p className="font-serif text-xl text-terracotta-400 font-light">~8 km</p>
                  </div>
                  <div>
                    <p className="font-sans text-xs text-anthracite-400 mb-1">Prezzo medio (Osimo, mar. 2026)</p>
                    <p className="font-serif text-xl text-terracotta-400 font-light">1.344 €/m²</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Cosa vedere e vivere ad Aspio */}
          <section className="max-w-7xl mx-auto px-6 pb-20">
            <div className="mb-10">
              <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-3">Esperienze</p>
              <h3 className="font-serif text-2xl text-anthracite-800 font-light">Cosa vedere e vivere ad Aspio</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  icon: 'ri-community-line',
                  title: 'Parrocchia di San Giuseppe Lavoratore',
                  desc: 'Il principale riferimento religioso e comunitario della località, legato allo sviluppo residenziale dell\'Aspio nel secondo Novecento.',
                },
                {
                  icon: 'ri-drop-line',
                  title: 'Terme dell\'Aspio',
                  desc: 'Un luogo identitario dell\'area, nel vicino territorio di Camerano, legato alle acque salsobromoiodiche e alla tradizione termale locale.',
                },
                {
                  icon: 'ri-map-pin-2-line',
                  title: 'Vicinanza a Osimo e Ancona',
                  desc: 'Uno dei punti di forza dell\'Aspio è la facilità dei collegamenti con il centro di Osimo e con il capoluogo di regione.',
                },
                {
                  icon: 'ri-landscape-line',
                  title: 'Porta di accesso al Conero',
                  desc: 'Per posizione, Aspio si presta bene come base pratica per muoversi tra città, fondovalle e Riviera del Conero.',
                },
              ].map((item) => (
                <div key={item.title} className="bg-white border border-stone-100 rounded-lg p-6">
                  <div className="w-10 h-10 flex items-center justify-center mb-4">
                    <i className={`${item.icon} text-terracotta-300 text-2xl`}></i>
                  </div>
                  <h4 className="font-serif text-base text-anthracite-800 font-light mb-2">{item.title}</h4>
                  <p className="font-sans text-sm text-anthracite-400 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Fonti Aspio */}
          <section className="max-w-7xl mx-auto px-6 pb-20">
            <div className="border-t border-stone-200 pt-10">
              <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-6">Fonti</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-6 font-sans text-sm text-anthracite-500">
                {/* Comune di Osimo */}
                <div>
                  <p className="font-semibold text-anthracite-700 mb-2">Comune di Osimo</p>
                  <ul className="space-y-1">
                    <li><a href="https://cdq.comune.osimo.an.it/consigli-di-quartiere-di-osimo/" target="_blank" rel="nofollow noopener noreferrer" className="underline hover:text-terracotta-400 transition-colors">Consigli di quartiere di Osimo</a></li>
                    <li><a href="https://www.comune.osimo.an.it/elaborati-nuovo-p-u-c-serie-a/" target="_blank" rel="nofollow noopener noreferrer" className="underline hover:text-terracotta-400 transition-colors">Elaborati nuovo P.U.C. – Serie A</a></li>
                  </ul>
                </div>
                {/* Parrocchia */}
                <div>
                  <p className="font-semibold text-anthracite-700 mb-2">Parrocchia di San Giuseppe Lavoratore</p>
                  <ul className="space-y-1">
                    <li><a href="https://www.diocesi.ancona.it/wd-annuario-enti/zona-pastorale-osimo-229/parrocchia-s-giuseppe-lavoratore-allaspio-268/" target="_blank" rel="nofollow noopener noreferrer" className="underline hover:text-terracotta-400 transition-colors">Diocesi di Ancona-Osimo</a></li>
                    <li><a href="https://www.chieseitaliane.chiesacattolica.it/SCHEDA%3D15911%26Chiesa_di_San_Giuseppe_Lavoratore__Ancona%2C_Aspio" target="_blank" rel="nofollow noopener noreferrer" className="underline hover:text-terracotta-400 transition-colors">Chiese Italiane – scheda edificio</a></li>
                  </ul>
                </div>
                {/* Terme dell'Aspio */}
                <div>
                  <p className="font-semibold text-anthracite-700 mb-2">Terme dell&apos;Aspio</p>
                  <ul className="space-y-1">
                    <li><a href="https://comune.camerano.an.it/vivere-il-comune/terme-dellaspio/" target="_blank" rel="nofollow noopener noreferrer" className="underline hover:text-terracotta-400 transition-colors">Comune di Camerano</a></li>
                  </ul>
                </div>
                {/* Distanze */}
                <div>
                  <p className="font-semibold text-anthracite-700 mb-2">Distanze</p>
                  <ul className="space-y-1">
                    <li><a href="https://www.rome2rio.com/it/s/Camerano-Aspio/Osimo" target="_blank" rel="nofollow noopener noreferrer" className="underline hover:text-terracotta-400 transition-colors">Camerano Aspio → Osimo (Rome2rio)</a></li>
                    <li><a href="https://www.rome2rio.com/it/s/Aspio-Terme/Ancona" target="_blank" rel="nofollow noopener noreferrer" className="underline hover:text-terracotta-400 transition-colors">Aspio Terme → Ancona (Rome2rio)</a></li>
                  </ul>
                </div>
                {/* Mercato immobiliare */}
                <div>
                  <p className="font-semibold text-anthracite-700 mb-2">Mercato immobiliare</p>
                  <ul className="space-y-1">
                    <li><a href="https://www.immobiliare.it/mercato-immobiliare/marche/osimo/" target="_blank" rel="nofollow noopener noreferrer" className="underline hover:text-terracotta-400 transition-colors">immobiliare.it – Osimo</a></li>
                    <li><a href="https://www.immobiliare.it/mercato-immobiliare/marche/ancona-provincia/" target="_blank" rel="nofollow noopener noreferrer" className="underline hover:text-terracotta-400 transition-colors">immobiliare.it – Provincia di Ancona</a></li>
                  </ul>
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* ── OFFAGNA ONLY ── */}
      {isOffagna && (
        <>
          {/* Intro + Rocca */}
          <section
            ref={offagnaParagraph.ref}
            className={`fade-in-up ${offagnaParagraph.isVisible ? 'visible' : ''} max-w-7xl mx-auto px-6 py-20`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div>
                <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Il borgo</p>
                <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-6 leading-snug">
                  Offagna: la rocca medievale tra Ancona, il Conero e le colline del Musone
                </h2>
                <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                  Offagna è uno dei borghi storici più riconoscibili dell&apos;entroterra anconetano. Sorge su un&apos;altura che domina la valle del Musone e conserva un impianto compatto, raccolto attorno alla sua fortificazione più celebre, la Rocca di Offagna, vero simbolo del paese. Oggi Offagna fa parte del circuito dei Borghi più Belli d&apos;Italia ed è anche Bandiera Arancione del Touring Club Italiano, riconoscimenti che ne confermano il valore storico, paesaggistico e turistico.
                </p>
                <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-8">
                  Il borgo ha una posizione molto favorevole: è vicino a Osimo, ad Ancona e alla Riviera del Conero, ma mantiene un carattere pienamente collinare e medievale. Al 1° gennaio 2025 conta 2.120 abitanti ed è posto a circa 306–309 metri sul livello del mare, con un tessuto urbano ancora facilmente leggibile e ben distinto dal paesaggio circostante.
                </p>
                <h3 className="font-serif text-xl text-anthracite-800 font-light mb-4">La Rocca e il cuore storico del paese</h3>
                <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-4">
                  L&apos;elemento più identitario di Offagna è la Rocca, costruita tra il 1454 e il 1456 su una rupe tufacea come fortificazione della Repubblica di Ancona. Le fonti ufficiali la descrivono come un&apos;opera di transizione tra l&apos;epoca delle armi bianche e quella delle armi da fuoco: ha merlature a coda di rondine, un alto mastio, camminamenti di ronda e circa 50 troniere. È uno degli esempi più forti di architettura militare marchigiana del Quattrocento.
                </p>
                <p className="font-sans text-base text-anthracite-500 leading-relaxed">
                  All&apos;interno della Rocca è ospitata una collezione dedicata alle armi antiche, mentre nel centro storico si sviluppa anche un piccolo polo museale che comprende il Museo della Liberazione di Ancona e il Museo di Scienze Naturali &quot;Luigi Paolucci&quot;. Il centro conserva inoltre la Chiesa di San Tommaso Apostolo, la Chiesa del Santissimo Sacramento, la Chiesa di Santa Lucia, la torre dell&apos;orologio e l&apos;antico monastero di Santa Zita.
                </p>
              </div>
              <div className="rounded-2xl overflow-hidden h-[480px]">
                <img
                  src="https://static.readdy.ai/image/13e0fae89bbbd02d36954a6d83bda5fb/c2a6d3e30bcab07409b73bc308c80d63.png"
                  alt="Offagna, borgo medievale con la Rocca"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </section>

          {/* Vivere a Offagna */}
          <section
            ref={offagnaLife.ref}
            className={`fade-in-up ${offagnaLife.isVisible ? 'visible' : ''} bg-ivory-50 py-20`}
          >
            <div className="max-w-7xl mx-auto px-6">
              <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Vivere a Offagna</p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start mb-14">
                <div>
                  <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-6 leading-snug">
                    Posizione strategica, qualità medievale
                  </h2>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-4">
                    Offagna è interessante anche per la sua posizione pratica. La distanza da Osimo è di circa 10 km, con tempi rapidi di collegamento; Ancona si raggiunge in circa 20 minuti in auto, mentre Sirolo e la Riviera del Conero sono a circa 26 minuti. Questo permette di vivere in un borgo storico senza rinunciare alla vicinanza con il mare, il capoluogo e i servizi principali.
                  </p>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed">
                    Sul piano immobiliare, Offagna presenta valori medi inferiori alla media provinciale. A marzo 2026 il prezzo medio richiesto per gli immobili residenziali in vendita era di 1.340 €/m², contro i 1.602 €/m² della provincia di Ancona. È quindi una località storica di qualità, ben posizionata e con valori ancora sotto la media provinciale.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {OFFAGNA_STATS.map((stat) => (
                    <div key={stat.label} className="bg-white rounded-2xl p-6 border border-ivory-200">
                      <div className="w-10 h-10 flex items-center justify-center mb-3">
                        <i className={`${stat.icon} text-terracotta-300 text-xl`} />
                      </div>
                      <p className="font-serif text-2xl text-anthracite-800 font-light mb-1">{stat.value}</p>
                      <p className="font-sans text-xs text-anthracite-400 leading-snug">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Esperienze */}
          <section
            ref={offagnaAttractions.ref}
            className={`fade-in-up ${offagnaAttractions.isVisible ? 'visible' : ''} max-w-7xl mx-auto px-6 py-20`}
          >
            <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Esperienze</p>
            <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-12 leading-snug">
              Cosa vedere e vivere a Offagna
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {OFFAGNA_ATTRACTIONS.map((item) => (
                <div key={item.title} className="bg-ivory-50 rounded-2xl p-8 border border-ivory-200">
                  <div className="w-10 h-10 flex items-center justify-center mb-4">
                    <i className={`${item.icon} text-terracotta-300 text-2xl`} />
                  </div>
                  <h3 className="font-serif text-xl text-anthracite-800 font-light mb-3">{item.title}</h3>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Fonti */}
          <section className="max-w-7xl mx-auto px-6 pb-20">
            <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-6">Fonti</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div>
                <p className="font-sans text-xs font-semibold text-anthracite-600 uppercase tracking-wider mb-3">Comune, riconoscimenti e territorio</p>
                <ul className="space-y-2">
                  <li><a href="https://borghipiubelliditalia.it/borgo/offagna/" target="_blank" rel="nofollow noopener noreferrer" className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors border-l-2 border-ivory-200 pl-3 block">Borghi più Belli d&apos;Italia – Offagna</a></li>
                  <li><a href="https://www.touringclub.it/destinazioni/offagna" target="_blank" rel="nofollow noopener noreferrer" className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors border-l-2 border-ivory-200 pl-3 block">Touring Club – Bandiera Arancione Offagna</a></li>
                  <li><a href="https://www.tuttitalia.it/marche/72-offagna/" target="_blank" rel="nofollow noopener noreferrer" className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors border-l-2 border-ivory-200 pl-3 block">tuttitalia.it – Offagna</a></li>
                  <li><a href="https://www.tuttitalia.it/marche/72-offagna/statistiche/popolazione-eta-sesso-stato-civile-2025/" target="_blank" rel="nofollow noopener noreferrer" className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors border-l-2 border-ivory-200 pl-3 block">tuttitalia.it – Popolazione Offagna 2025</a></li>
                  <li><a href="https://www.rome2rio.com/it/s/Offagna/Osimo" target="_blank" rel="nofollow noopener noreferrer" className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors border-l-2 border-ivory-200 pl-3 block">rome2rio – Offagna → Osimo</a></li>
                  <li><a href="https://www.rome2rio.com/it/s/Offagna/Ancona" target="_blank" rel="nofollow noopener noreferrer" className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors border-l-2 border-ivory-200 pl-3 block">rome2rio – Offagna → Ancona</a></li>
                  <li><a href="https://www.rome2rio.com/it/s/Offagna/Sirolo" target="_blank" rel="nofollow noopener noreferrer" className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors border-l-2 border-ivory-200 pl-3 block">rome2rio – Offagna → Sirolo</a></li>
                </ul>
              </div>
              <div>
                <p className="font-sans text-xs font-semibold text-anthracite-600 uppercase tracking-wider mb-3">Rocca, musei ed eventi</p>
                <ul className="space-y-2">
                  <li><a href="https://www.visitoffagna.it/" target="_blank" rel="nofollow noopener noreferrer" className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors border-l-2 border-ivory-200 pl-3 block">visitoffagna.it</a></li>
                  <li><a href="https://www.visitoffagna.it/la-rocca/" target="_blank" rel="nofollow noopener noreferrer" className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors border-l-2 border-ivory-200 pl-3 block">visitoffagna.it – La Rocca</a></li>
                  <li><a href="https://www.visitoffagna.it/scopri-offagna" target="_blank" rel="nofollow noopener noreferrer" className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors border-l-2 border-ivory-200 pl-3 block">visitoffagna.it – Scopri Offagna</a></li>
                  <li><a href="https://www.visitoffagna.it/polo-museale-offagna" target="_blank" rel="nofollow noopener noreferrer" className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors border-l-2 border-ivory-200 pl-3 block">visitoffagna.it – Polo Museale</a></li>
                  <li><a href="https://www.visitoffagna.it/scopri-offagna/museo-luigi-paolucci" target="_blank" rel="nofollow noopener noreferrer" className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors border-l-2 border-ivory-200 pl-3 block">visitoffagna.it – Museo Luigi Paolucci</a></li>
                  <li><a href="https://www.visitoffagna.it/scopri-offagna/museo-della-liberazione-di-ancona" target="_blank" rel="nofollow noopener noreferrer" className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors border-l-2 border-ivory-200 pl-3 block">visitoffagna.it – Museo della Liberazione</a></li>
                  <li><a href="https://www.visitoffagna.it/da-non-perdere-offagna/feste-medievali" target="_blank" rel="nofollow noopener noreferrer" className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors border-l-2 border-ivory-200 pl-3 block">visitoffagna.it – Feste Medievali</a></li>
                  <li><a href="https://www.visitoffagna.it/eventi-offagna/feste-medievali" target="_blank" rel="nofollow noopener noreferrer" className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors border-l-2 border-ivory-200 pl-3 block">visitoffagna.it – Feste Medievali (eventi)</a></li>
                </ul>
              </div>
              <div>
                <p className="font-sans text-xs font-semibold text-anthracite-600 uppercase tracking-wider mb-3">Mercato immobiliare</p>
                <ul className="space-y-2">
                  <li><a href="https://www.immobiliare.it/mercato-immobiliare/marche/offagna/" target="_blank" rel="nofollow noopener noreferrer" className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors border-l-2 border-ivory-200 pl-3 block">immobiliare.it – Offagna</a></li>
                  <li><a href="https://www.immobiliare.it/mercato-immobiliare/marche/ancona-provincia/" target="_blank" rel="nofollow noopener noreferrer" className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors border-l-2 border-ivory-200 pl-3 block">immobiliare.it – Provincia di Ancona</a></li>
                </ul>
              </div>
            </div>
          </section>

          {/* Video Offagna */}
          <section className="max-w-7xl mx-auto px-6 pb-20">
            <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Video</p>

            <div className="w-full max-w-3xl rounded-2xl overflow-hidden border border-ivory-200 aspect-video">
              <iframe
                className="w-full h-full"
                src="https://www.youtube.com/embed/XSnebi2QVek"
                title="Video su Offagna"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                loading="lazy"
              />
            </div>
          </section>
        </>
      )}

      {/* ── MONTE SICURO ONLY ── */}
      {isMonteSicuro && (
        <>
          {/* Intro + storia + castello */}
          <section
            ref={monteSicuroParagraph.ref}
            className={`fade-in-up ${monteSicuroParagraph.isVisible ? 'visible' : ''} max-w-7xl mx-auto px-6 py-20`}
          >
            <div className="flex flex-col gap-12">
              {/* Riga 1: intro testo + immagine */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
                <div>
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Il borgo</p>
                  <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                    Montesicuro: il castello di Ancona tra colline, memoria e comunità
                  </h2>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                    Montesicuro è una frazione del Comune di Ancona, sulle colline a sud-ovest del capoluogo. Più che un borgo turistico in senso classico, è un piccolo centro storico con una forte identità locale, legato alla storia dei castelli anconetani e ancora riconoscibile per il suo impianto medievale, per la dimensione raccolta e per il rapporto diretto con il paesaggio rurale che circonda la città.
                  </p>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed">
                    Per lungo tempo Montesicuro fu comune autonomo; venne poi accorpato ad Ancona nel 1928. Le fonti locali lo indicano come uno dei castelli del sistema difensivo della Repubblica di Ancona, cioè quella rete di centri fortificati che proteggevano il territorio del capoluogo. Questa origine spiega perché, ancora oggi, passeggiare nel borgo significhi attraversare un tracciato urbano che conserva la memoria dell&apos;antico castello.
                  </p>
                </div>
                <div className="rounded-xl overflow-hidden aspect-[16/9]">
                  <img
                    src="https://static.readdy.ai/image/13e0fae89bbbd02d36954a6d83bda5fb/4bb3cdef1b8250e51f6f4aa9d524ab56.png"
                    alt="Montesicuro borgo collinare anconetano"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>

              {/* Riga 2: borgo storico + tradizioni affiancati */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
                <div className="border-l-2 border-terracotta-200 pl-6">
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Il borgo storico e i suoi riferimenti principali</p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Il cuore religioso del paese è la Chiesa di Santo Stefano, parrocchiale di Montesicuro. Le fonti ecclesiastiche permettono di datare la parte centrale dell&apos;edificio alla seconda metà del Cinquecento, mentre le navate laterali furono aggiunte nel Settecento. È uno dei punti di riferimento più forti del borgo, anche per la continuità della vita parrocchiale e comunitaria che ancora oggi caratterizza la frazione.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Un altro luogo simbolico è la Chiesa di San Pancrazio, situata fuori dal centro abitato lungo la strada verso l&apos;Aspio. Il censimento delle chiese italiane la data probabilmente alla prima metà del Cinquecento; negli ultimi anni è tornata al centro dell&apos;attenzione locale anche come bene culturale da valorizzare, insieme al patrimonio diffuso del borgo.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed">
                    Le testimonianze più recenti sul borgo insistono proprio su questo doppio livello: da una parte la storia medievale di Montesicuro, dall&apos;altra una comunità che continua a promuoverne la memoria attraverso iniziative culturali, attività associative e nuovi progetti di valorizzazione. Nel 2026, per esempio, è nata una Pro Loco Montesicuro APS con l&apos;obiettivo dichiarato di rafforzare il senso di appartenenza e rilanciare il borgo.
                  </p>
                </div>
                <div className="border-l-2 border-terracotta-200 pl-6">
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Tradizioni e identità locale</p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Tra gli elementi che definiscono maggiormente l&apos;identità di Montesicuro c&apos;è il presepe meccanico, ricordato dalla stampa locale e dalla promozione turistica di Ancona come una tradizione consolidata del borgo. Accanto a questo restano vive anche le ricorrenze legate al patrono San Pancrazio e gli eventi comunitari che negli ultimi anni hanno contribuito a riportare attenzione sul paese.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed">
                    Più che puntare su grandi attrazioni, Montesicuro si racconta bene come un luogo di memoria anconetana: una frazione storica che conserva il tracciato del castello, la parrocchia cinquecentesca, la piccola chiesa rurale di San Pancrazio e un tessuto di relazioni locali ancora vivo.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Vivere a Montesicuro */}
          <section
            ref={monteSicuroLife.ref}
            className={`fade-in-up ${monteSicuroLife.isVisible ? 'visible' : ''} bg-ivory-50 py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div>
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Vivere a Montesicuro</p>
                  <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                    Colline, comunità e vicinanza ad Ancona
                  </h2>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                    Montesicuro non è isolato: fa parte del sistema collinare di Ancona e mantiene un legame diretto con la città. Proprio questa posizione intermedia è uno dei suoi aspetti più interessanti: si vive in un contesto più silenzioso e rurale, ma restando nell&apos;orbita del capoluogo, dei suoi servizi e delle sue connessioni.
                  </p>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                    La presenza di scuole, attività ricettive e una comunità ancora attiva conferma che non si tratta di un borgo svuotato, ma di una frazione con una vita propria. RaiNews lo descrive come un centro di poco più di 300 abitanti, con una vita sociale e culturale autonoma nonostante il rischio di spopolamento che interessa molte frazioni storiche.
                  </p>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed">
                    Montesicuro va descritto con equilibrio: non come &quot;borgo monumentale&quot; pieno di emergenze maggiori, ma come piccolo castello collinare in cui il valore sta soprattutto nell&apos;atmosfera, nella storia locale, nella continuità delle tradizioni e nel paesaggio agricolo anconetano. È questa coerenza, più che l&apos;effetto scenografico, a renderlo interessante per chi cerca le Marche meno esposte e più quotidiane.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-5">
                  {MONTESICURO_STATS.map((stat) => (
                    <div key={stat.label} className="p-6 bg-white rounded-2xl border border-ivory-200 flex flex-col gap-3">
                      <div className="w-10 h-10 flex items-center justify-center text-terracotta-300">
                        <i className={`${stat.icon} text-2xl`}></i>
                      </div>
                      <p className="font-serif text-xl text-anthracite-800 font-light leading-snug">{stat.value}</p>
                      <p className="font-sans text-xs text-anthracite-400 tracking-wide uppercase">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Fonti Montesicuro */}
          <section className="max-w-7xl mx-auto px-6 pb-2 pt-0">
            <div className="border-l-2 border-ivory-200 pl-6">
              <p className="font-sans text-xs tracking-widest uppercase text-anthracite-400 mb-4">Fonti</p>
              <ul className="flex flex-col gap-2">
                {([
                  { label: 'letsmarche.it — Ancona, borgo di Montesicuro', url: 'https://letsmarche.it/-/ancona-borgo-di-montesicuro' },
                  { label: 'rainews.it — Borghi di Ancona: Montesicuro (TGR Marche, 2023)', url: 'https://www.rainews.it/tgr/marche/video/2023/09/borghi-ancona-197268ea-f5c7-4426-8494-074434d97ba0.html' },
                  { label: 'diocesi.ancona.it — Parrocchia S. Stefano in Montesicuro', url: 'https://www.diocesi.ancona.it/wd-annuario-enti/zona-pastorale-agugliano-1659/parrocchia-s-stefano-in-montesicuro-271/' },
                  { label: 'chieseitaliane.chiesacattolica.it', url: 'https://chieseitaliane.chiesacattolica.it/chieseitaliane/AccessoEsterno.do?code=15927&mode=guest&type=auto', sub: [
                    { label: 'Cappella di San Pancrazio — Montesicuro', url: 'https://www.chieseitaliane.chiesacattolica.it/SCHEDA=15903&Cappella_di_San_Pancrazio__Montesicuro,_Ancona' },
                  ]},
                  { label: 'ilrestodelcarlino.it', url: 'https://www.ilrestodelcarlino.it/ancona/cronaca/il-borgo-amico-da-rilanciare-f83ff80f', sub: [
                    { label: 'A Montesicuro un gruppo di…', url: 'https://www.ilrestodelcarlino.it/ancona/cronaca/a-montesicuro-un-gruppo-di-64b328f0' },
                  ]},
                  { label: 'pinocchio-montesicuro.edu.it — Scuola di Montesicuro', url: 'https://pinocchio-montesicuro.edu.it/' },
                  { label: 'anconatourism.it — Natale ad Ancona (presepe meccanico)', url: 'https://anconatourism.it/archivio-news/natale-ancona/' },
                ] as Array<{ label: string; url: string; sub?: Array<{ label: string; url: string }> }>).map((source) => (
                  <li key={source.url} className="flex flex-col gap-1">
                    <div className="flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <i className="ri-external-link-line text-xs text-terracotta-300"></i>
                      </div>
                      <a
                        href={source.url}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors leading-relaxed break-all cursor-pointer"
                      >
                        {source.label}
                      </a>
                    </div>
                    {source.sub && (
                      <ul className="ml-6 flex flex-col gap-1">
                        {source.sub.map((sub) => (
                          <li key={sub.url} className="flex items-start gap-2">
                            <div className="w-3 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                              <i className="ri-corner-down-right-line text-xs text-anthracite-300"></i>
                            </div>
                            <a
                              href={sub.url}
                              target="_blank"
                              rel="nofollow noopener noreferrer"
                              className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors leading-relaxed break-all cursor-pointer"
                            >
                              {sub.label}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Cosa vedere */}
          <section
            ref={monteSicuroAttractions.ref}
            className={`fade-in-up ${monteSicuroAttractions.isVisible ? 'visible' : ''} py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Esperienze</p>
              <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-12 leading-snug">
                Cosa vedere e vivere a Montesicuro
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
                {MONTESICURO_ATTRACTIONS.map((attr) => (
                  <article key={attr.title} className="bg-ivory-50 rounded-2xl p-7 border border-ivory-200 flex flex-col gap-4">
                    <div className="w-11 h-11 flex items-center justify-center text-terracotta-300 bg-white rounded-xl border border-ivory-200">
                      <i className={`${attr.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-serif text-lg text-anthracite-800 font-medium leading-snug">{attr.title}</h3>
                    <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{attr.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* ── MONTEFANO ONLY ── */}
      {isMontefano && (
        <>
          {/* Intro + storia + teatro */}
          <section
            ref={montefanoParagraph.ref}
            className={`fade-in-up ${montefanoParagraph.isVisible ? 'visible' : ''} max-w-7xl mx-auto px-6 py-20`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div>
                <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Il borgo</p>
                <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                  Montefano: il borgo del teatro, della fotografia e delle colline maceratesi
                </h2>
                <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                  Montefano è un borgo collinare della provincia di Macerata, adagiato tra l&apos;Appennino e il mare Adriatico. Le fonti istituzionali lo descrivono come un centro ricco di patrimonio storico e religioso, con un&apos;identità culturale molto marcata e un&apos;atmosfera raccolta che lo rende una tappa interessante dell&apos;entroterra marchigiano. Al 1° gennaio 2026 conta 3.304 abitanti.
                </p>
                <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-8">
                  Più che per un solo monumento, Montefano si riconosce per un equilibrio raro tra architettura, vita culturale e memoria locale. Il centro ruota attorno a Piazza Bracaccini e al sistema di edifici storici che comprendono la Collegiata di San Donato, il Teatro La Rondinella, l&apos;ex Palazzo Municipale e il Museo Ghergo. In questo senso è un borgo che si racconta bene attraverso i suoi interni: il teatro, la chiesa, il museo, i loggiati e gli spazi civici.
                </p>
                <div className="border-l-2 border-terracotta-200 pl-6 mb-8">
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Il teatro e la vocazione culturale</p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Uno dei luoghi più rappresentativi di Montefano è il Teatro La Rondinella, piccolo teatro storico con sala a U e due ordini di palchi. Le fonti turistiche lo indicano come una perla ottocentesca progettata da Virginio Tombolini, già legato alla Fenice di Venezia, mentre la scheda comunale conferma la centralità del teatro nella vita culturale del paese e la possibilità di visitarlo su prenotazione.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    A questa dimensione teatrale si affianca quella fotografica. Montefano ha dato i natali ad Arturo Ghergo, nato qui il 20 agosto 1901, fotografo celebre per i ritratti delle dive del cinema e dello spettacolo. Il Museo Ghergo, allestito nel Palazzo dei Priori / ex Palazzo Comunale sotto il loggiato che affaccia su Piazza Bracaccini, conserva opere, strumenti di lavoro e spazi espositivi dedicati alla sua figura; attorno a questa eredità si è sviluppato anche il Festival di Fotografia Arturo Ghergo.
                  </p>
                </div>
                <div className="border-l-2 border-terracotta-200 pl-6">
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Il borgo storico e i suoi luoghi principali</p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Nel cuore del paese si trova la Collegiata di San Donato, che custodisce le spoglie di Sant&apos;Onorio e San Saverio e conserva una tela con San Donato e San Giovanni Nepomuceno ai lati dell&apos;Assunta. La chiesa è uno dei punti di riferimento religiosi più forti del centro storico e si affaccia direttamente sulla piazza principale.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Montefano conserva anche tracce del suo passato difensivo. Il Torrione scarpato, all&apos;interno del centro storico, fu ricostruito dagli osimani sui ruderi di una fortificazione precedente distrutta durante l&apos;occupazione dello Sforza nel 1434; i merli ghibellini ne sottolineano ancora il carattere militare. Questo elemento aggiunge al borgo una memoria medievale più netta, accanto agli edifici religiosi e civili di epoca successiva.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed">
                    Le fonti turistiche ricordano inoltre un dato identitario importante: nel 1501, a Palazzo dei Priori, nacque Marcello Cervini, il futuro Papa Marcello II. Anche per questo Montefano viene talvolta raccontato come &quot;città papale&quot;, cioè come un borgo che ha lasciato una traccia nella storia ecclesiastica oltre che in quella culturale.
                  </p>
                </div>
              </div>

              {/* Photo grid */}
              <div className="grid grid-cols-1 gap-4">
                <div className="rounded-xl overflow-hidden aspect-[3/4] bg-ivory-50">
                  <img
                    src={`https://static.readdy.ai/image/13e0fae89bbbd02d36954a6d83bda5fb/62ee0f3df3ede92366702273361cafd5.png`}
                    alt="Montefano centro storico"
                    className="w-full h-full object-contain object-center"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Vivere a Montefano */}
          <section
            ref={montefanoLife.ref}
            className={`fade-in-up ${montefanoLife.isVisible ? 'visible' : ''} bg-ivory-50 py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div>
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Vivere a Montefano</p>
                  <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                    Posizione, accessibilità e mercato immobiliare
                  </h2>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                    Montefano è ben collegato con i centri maggiori del territorio. Macerata dista circa 17,1 km su strada e si raggiunge in circa 18 minuti d&apos;auto; Recanati è ancora più vicina, a circa 12,5 km, con tempi di percorrenza di circa 14-15 minuti. Anche Ancona resta accessibile, con una distanza stradale di circa 28,2 km e tempi intorno ai 31-32 minuti in auto.
                  </p>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed">
                    Dal punto di vista immobiliare, Montefano mantiene valori medi contenuti. Ad aprile 2026 il prezzo medio richiesto per gli immobili residenziali in vendita era di 774 €/m², contro una media provinciale di 1.398 €/m². È quindi corretto raccontarlo come un borgo accessibile, con un buon equilibrio tra qualità del contesto storico e valori immobiliari ancora sotto la media della provincia di Macerata.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-5">
                  {MONTEFANO_STATS.map((stat) => (
                    <div key={stat.label} className="p-6 bg-white rounded-2xl border border-ivory-200 flex flex-col gap-3">
                      <div className="w-10 h-10 flex items-center justify-center text-terracotta-300">
                        <i className={`${stat.icon} text-2xl`}></i>
                      </div>
                      <p className="font-serif text-2xl text-anthracite-800 font-light">{stat.value}</p>
                      <p className="font-sans text-xs text-anthracite-400 tracking-wide uppercase">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Cosa vedere */}
          <section
            ref={montefanoAttractions.ref}
            className={`fade-in-up ${montefanoAttractions.isVisible ? 'visible' : ''} py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Esperienze</p>
              <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-12 leading-snug">
                Cosa vedere e vivere a Montefano
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {MONTEFANO_ATTRACTIONS.map((attr) => (
                  <article key={attr.title} className="bg-ivory-50 rounded-2xl p-7 border border-ivory-200 flex flex-col gap-4">
                    <div className="w-11 h-11 flex items-center justify-center text-terracotta-300 bg-white rounded-xl border border-ivory-200">
                      <i className={`${attr.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-serif text-lg text-anthracite-800 font-medium leading-snug">{attr.title}</h3>
                    <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{attr.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* Fonti Montefano */}
          <section className="max-w-7xl mx-auto px-6 pb-2 pt-0">
            <div className="border-l-2 border-ivory-200 pl-6">
              <p className="font-sans text-xs tracking-widest uppercase text-anthracite-400 mb-4">Fonti</p>
              <ul className="flex flex-col gap-2">
                {[
                  { label: 'Comune di Montefano', url: 'https://www.comune.montefano.mc.it/' },
                  { label: 'Comune di Macerata — Scheda turistica Montefano', url: 'https://www.comune.macerata.it/turismo/borghi/montefano' },
                  { label: 'tuttitalia.it — Montefano', url: 'https://www.tuttitalia.it/marche/96-montefano/' },
                  { label: 'immobiliare.it — Mercato Montefano', url: 'https://www.immobiliare.it/mercato-immobiliare/marche/montefano/' },
                  { label: 'rivieradelconero.info', url: 'https://www.rivieradelconero.info/' },
                  { label: 'rome2rio.com — Montefano', url: 'https://www.rome2rio.com/it/s/Montefano' },
                ].map((source) => (
                  <li key={source.url} className="flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-external-link-line text-xs text-terracotta-300"></i>
                    </div>
                    <a
                      href={source.url}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors leading-relaxed break-all cursor-pointer"
                    >
                      {source.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Video Montefano */}
          <section className="max-w-7xl mx-auto px-6 pt-10 pb-20">
            <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Video</p>
            <div className="max-w-sm rounded-2xl overflow-hidden border border-ivory-200 aspect-[9/16]">
              <iframe
                className="w-full h-full"
                src="https://www.youtube.com/embed/8yiykIjWQos"
                title="Video Montefano"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                loading="lazy"
              />
            </div>
          </section>
        </>
      )}

      {/* ── SARNANO ONLY ── */}
      {isSarnano && (
        <>
          {/* Intro + storia + Piazza Alta */}
          <section
            ref={sarnanoParagraph.ref}
            className={`fade-in-up ${sarnanoParagraph.isVisible ? 'visible' : ''} max-w-7xl mx-auto px-6 py-20`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div>
                <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Il borgo</p>
                <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                  Sarnano: il borgo medievale tra Piazza Alta, cascate e Sibillini
                </h2>
                <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                  Sarnano è uno dei borghi più riconoscibili dell&apos;entroterra maceratese. Sorge ai piedi dei Monti Sibillini e conserva un centro storico medievale ancora molto leggibile, tanto da far parte dei Borghi più Belli d&apos;Italia e da essere anche Bandiera Arancione. Il portale turistico ufficiale lo racconta come un castrum ben conservato, in cui tutte le strade convergono verso il nucleo più antico del paese.
                </p>
                <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-8">
                  Il borgo si sviluppa su un&apos;altura alla destra del Tennacola e mantiene un impianto storico compatto, con accesso da Porta Brunforte e fulcro nella Piazza Alta, che costituisce il cuore dell&apos;abitato originario. È proprio questa struttura urbana, più che un singolo monumento, a rendere Sarnano uno dei centri più affascinanti della fascia pedemontana marchigiana.
                </p>
                <div className="border-l-2 border-terracotta-200 pl-6 mb-8">
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Piazza Alta e il cuore storico</p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Il centro monumentale di Sarnano coincide con Piazza Alta, dove si concentrano i principali edifici civili e religiosi del borgo: la Chiesa di Santa Maria di Piazza Alta o Santa Maria Assunta, il Palazzo del Popolo, il Palazzo del Podestà, il Palazzo dei Priori, il Teatro della Vittoria, la Pinacoteca Comunale e i Musei Civici.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    La Chiesa di Santa Maria Assunta è la principale chiesa del centro storico. Le fonti comunali indicano che la costruzione si concluse nel 1396 con l&apos;erezione della torre campanaria e ricordano che in origine era nota anche come Santa Maria intra moenia, cioè dentro le mura.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed">
                    Accanto alla chiesa si trova la Pinacoteca Comunale, che conserva alcune tra le opere più importanti del patrimonio artistico locale. La scheda ufficiale del Comune segnala in particolare L&apos;Ultima Cena di Simone De Magistris, una Crocefissione di Stefano Folchetti del 1513 e una Madonna adorante il Bambino con angeli musicanti di Vittore Crivelli.
                  </p>
                </div>
                <div className="border-l-2 border-terracotta-200 pl-6">
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Teatro, cultura e paesaggio</p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Nel primo piano dell&apos;antico Palazzo del Popolo si trova il Teatro della Vittoria, piccolo teatro storico che accoglie poco più di 100 spettatori. È uno dei luoghi culturali più rappresentativi del borgo e uno dei simboli della sua vita civile.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Sarnano, però, non è solo centro storico. Il portale turistico ufficiale insiste molto sul rapporto tra il borgo e la montagna: trekking, bike, sport invernali, parapendio, terme e paesaggi dei Sibillini fanno parte integrante della sua identità contemporanea. In questo senso Sarnano unisce davvero storia medievale e turismo outdoor.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed">
                    Tra le esperienze naturalistiche più note c&apos;è la Via delle Cascate Perdute, un itinerario molto amato che collega alcune cascate nei pressi del paese e rappresenta oggi una delle passeggiate più caratteristiche del territorio.
                  </p>
                </div>
              </div>

              {/* Photo grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 rounded-xl overflow-hidden aspect-[16/9]">
                  <img
                    src="https://static.readdy.ai/image/13e0fae89bbbd02d36954a6d83bda5fb/ebc896dc3f785dc9ddbfa9e9d25939ab.png"
                    alt="Sarnano Piazza Alta centro storico"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <div className="rounded-xl overflow-hidden aspect-square">
                  <img
                    src="https://static.readdy.ai/image/13e0fae89bbbd02d36954a6d83bda5fb/01df4cb007c718987ac1433a512f3ba1.webp"
                    alt="Sarnano Chiesa di Santa Maria Assunta"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="rounded-xl overflow-hidden aspect-square">
                  <img
                    src="https://static.readdy.ai/image/13e0fae89bbbd02d36954a6d83bda5fb/c8ea2a41eb14b32e41fdcaebce92c6c4.png"
                    alt="Sarnano Cascate Perdute"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Vivere a Sarnano */}
          <section
            ref={sarnanoLife.ref}
            className={`fade-in-up ${sarnanoLife.isVisible ? 'visible' : ''} bg-ivory-50 py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div>
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Vivere a Sarnano</p>
                  <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                    Posizione, montagna e mercato immobiliare
                  </h2>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                    Sarnano conta 3.058 abitanti e l&apos;altitudine della casa comunale è di 526 metri sul livello del mare, mentre il territorio comunale si estende da 317 a 1.914 metri. Questo dato spiega bene la sua doppia natura di borgo storico e località montana.
                  </p>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed">
                    Anche sul piano immobiliare il paese resta accessibile. A maggio 2026 il prezzo medio richiesto per gli immobili in vendita a Sarnano era di 837 €/m², contro 1.389 €/m² della media provinciale di Macerata. È quindi corretto descriverlo come un borgo di qualità storica e paesaggistica con valori medi ancora inferiori alla media della provincia.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-5">
                  {SARNANO_STATS.map((stat) => (
                    <div key={stat.label} className="p-6 bg-white rounded-2xl border border-ivory-200 flex flex-col gap-3">
                      <div className="w-10 h-10 flex items-center justify-center text-terracotta-300">
                        <i className={`${stat.icon} text-2xl`}></i>
                      </div>
                      <p className="font-serif text-2xl text-anthracite-800 font-light">{stat.value}</p>
                      <p className="font-sans text-xs text-anthracite-400 tracking-wide uppercase">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Cosa vedere */}
          <section
            ref={sarnanoAttractions.ref}
            className={`fade-in-up ${sarnanoAttractions.isVisible ? 'visible' : ''} py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Esperienze</p>
              <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-12 leading-snug">
                Cosa vedere e vivere a Sarnano
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {SARNANO_ATTRACTIONS.map((attr) => (
                  <article key={attr.title} className="bg-ivory-50 rounded-2xl p-7 border border-ivory-200 flex flex-col gap-4">
                    <div className="w-11 h-11 flex items-center justify-center text-terracotta-300 bg-white rounded-xl border border-ivory-200">
                      <i className={`${attr.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-serif text-lg text-anthracite-800 font-medium leading-snug">{attr.title}</h3>
                    <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{attr.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* Fonti Sarnano */}
          <section className="max-w-7xl mx-auto px-6 pb-2 pt-0">
            <div className="border-l-2 border-ivory-200 pl-6">
              <p className="font-sans text-xs tracking-widest uppercase text-anthracite-400 mb-4">Fonti</p>
              <ul className="flex flex-col gap-2">
                {[
                  { label: 'comune.sarnano.mc.it', url: 'https://www.comune.sarnano.mc.it/' },
                  { label: 'comune.sarnano.mc.it — Chiesa di Santa Maria Assunta', url: 'https://www.comune.sarnano.mc.it/vivere-il-comune/luoghi/chiesa-di-santa-maria-assunta/' },
                  { label: 'comune.sarnano.mc.it — Pinacoteca Comunale', url: 'https://www.comune.sarnano.mc.it/vivere-il-comune/luoghi/pinacoteca-comunale-3/' },
                  { label: 'comune.sarnano.mc.it — Teatro della Vittoria', url: 'https://www.comune.sarnano.mc.it/vivere-il-comune/luoghi/teatro-della-vittoria/' },
                  { label: 'sarnanoturismo.it', url: 'https://www.sarnanoturismo.it/' },
                  { label: 'sarnanoturismo.it — Centro storico', url: 'https://www.sarnanoturismo.it/centro-storico/' },
                  { label: 'visit.sarnanoturismo.it — Punti di interesse', url: 'https://visit.sarnanoturismo.it/poi' },
                  { label: 'borghipiubelliditalia.it — Sarnano', url: 'https://borghipiubelliditalia.it/borgo/sarnano/' },
                  { label: 'tuttitalia.it — Sarnano', url: 'https://www.tuttitalia.it/marche/21-sarnano/' },
                  { label: 'immobiliare.it — Mercato Sarnano', url: 'https://www.immobiliare.it/mercato-immobiliare/marche/sarnano/' },
                ].map((source) => (
                  <li key={source.url} className="flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <i className="ri-external-link-line text-xs text-terracotta-300"></i>
                    </div>
                    <a
                      href={source.url}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors leading-relaxed break-all cursor-pointer"
                    >
                      {source.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Video Sarnano */}
          <section className="max-w-7xl mx-auto px-6 pt-10 pb-20">
            <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Video</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="max-w-sm rounded-2xl overflow-hidden border border-ivory-200 aspect-[9/16]">
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/_xvxxhHn7jk"
                  title="Video Sarnano 1"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
              <div className="max-w-sm rounded-2xl overflow-hidden border border-ivory-200 aspect-[9/16]">
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/RKdi5X477OQ"
                  title="Video Sarnano 2"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
              <div className="max-w-sm rounded-2xl overflow-hidden border border-ivory-200 aspect-[9/16]">
                <iframe
                  className="w-full h-full"
                  src="https://www.youtube.com/embed/ZXXaXiu-i9M"
                  title="Video Sarnano 3"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
            </div>
          </section>
        </>
      )}

      {/* ── NUMANA ONLY ── */}
      {isNumana && (
        <>
          {/* Intro + storia + centro storico */}
          <section
            ref={numanaParagraph.ref}
            className={`fade-in-up ${numanaParagraph.isVisible ? 'visible' : ''} max-w-7xl mx-auto px-6 py-20`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div>
                <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Il borgo</p>
                <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                  Numana: il borgo del Conero tra Costarella, mare e antiche radici picene
                </h2>
                <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                  Numana è uno dei centri più iconici della Riviera del Conero. Il portale turistico ufficiale la presenta come la Regina del Conero, immersa tra il blu dell&apos;Adriatico e il verde del Parco, con un centro storico che conserva tracce antiche e un rapporto diretto con il mare. La città affonda le sue origini nell&apos;età dei Piceni, ai quali sono legati molti dei reperti archeologici custoditi oggi nel borgo.
                </p>
                <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-8">
                  Più che per un solo monumento, Numana si riconosce per l&apos;equilibrio tra parte alta e parte bassa: il promontorio storico che guarda il porto, le scalinate che scendono verso il mare, le spiagge a ridosso del centro e una sequenza di panorami che la rendono una delle località più riconoscibili della costa marchigiana. Italia.it la descrive come un colorato borgo di pescatori con grandi spiagge facilmente accessibili e un centro sviluppato lungo la Costarella, la scalinata che collega il borgo al mare.
                </p>
                <div className="border-l-2 border-terracotta-200 pl-6 mb-8">
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Il centro storico e i suoi luoghi simbolo</p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Uno degli elementi più caratteristici di Numana è proprio la <strong className="text-anthracite-700">Costarella</strong>, la via più suggestiva del centro: una lunga scalinata fiorita che un tempo veniva percorsa ogni giorno dai pescatori per raggiungere il porto. Le case in pietra calcarea del Conero che la fiancheggiano raccontano ancora oggi l&apos;anima più antica e marinara del borgo.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    In cima al promontorio si trova l&apos;<strong className="text-anthracite-700">Arco di Torre</strong>, simbolo di Numana e unico resto medievale dell&apos;antica città secondo la scheda del Comune. Le fonti ufficiali spiegano che l&apos;origine esatta dell&apos;opera resta incerta: potrebbe essere stata parte del campanile di una chiesa, delle mura urbane o di una torre di avvistamento; ciò che è certo è la sua posizione dominante sul porto e sul mare.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed mb-4">
                    Tra i principali riferimenti religiosi del centro c&apos;è il <strong className="text-anthracite-700">Santuario del Santissimo Crocifisso</strong>, oggi nella chiesa di Cristo Re in Piazza del Santuario. Il Comune ricorda che il primo santuario fu costruito tra il 1561 e il 1566 e che l&apos;edificio attuale, ricostruito nel Novecento, venne inaugurato il 6 luglio 1969. Il portale turistico sottolinea anche il valore del Crocifisso ligneo custodito al suo interno, fulcro della devozione numanese.
                  </p>
                  <p className="font-sans text-sm text-anthracite-500 leading-relaxed">
                    Un altro luogo chiave è l&apos;<strong className="text-anthracite-700">Antiquarium Statale</strong>, nel cuore del centro storico. Qui sono raccolti reperti provenienti dalle necropoli picene della zona, tra cui il corredo della celebre Tomba della Regina, che testimonia il ruolo importante di Numana nel mondo preromano. È una tappa essenziale per capire quanto profonde siano le radici storiche del borgo.
                  </p>
                </div>
              </div>

              {/* Photo grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 rounded-xl overflow-hidden aspect-[16/9]">
                  <img
                    src="https://static.readdy.ai/image/13e0fae89bbbd02d36954a6d83bda5fb/2139fa7cf88e520b50e956b3decffbd1.png"
                    alt="Numana vista panoramica sulla Riviera del Conero"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <div className="rounded-xl overflow-hidden aspect-square">
                  <img
                    src="https://static.readdy.ai/image/13e0fae89bbbd02d36954a6d83bda5fb/a898669486a3d16f9d3dd4485c9aff58.png"
                    alt="Numana Costarella scalinata storica"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="rounded-xl overflow-hidden aspect-square">
                  <img
                    src="https://static.readdy.ai/image/13e0fae89bbbd02d36954a6d83bda5fb/be509ffa89a89376d605d5cc1f9c8974.png"
                    alt="Numana Spiaggia del Frate"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Vivere a Numana */}
          <section
            ref={numanaLife.ref}
            className={`fade-in-up ${numanaLife.isVisible ? 'visible' : ''} bg-ivory-50 py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                <div>
                  <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Vivere a Numana</p>
                  <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-8 leading-snug">
                    Mare, spiagge e qualità della vita
                  </h2>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                    Numana vive in rapporto diretto con il mare. Dal centro si raggiungono facilmente la Spiaggiola e la Spiaggia del Frate, detta anche Sottosanta. Quest&apos;ultima è descritta dal portale turistico ufficiale come una caletta di ghiaia fine incorniciata dal verde del Parco del Conero e dal bianco della roccia calcarea, raggiungibile a piedi dal centro storico in pochi minuti.
                  </p>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-6">
                    La posizione è uno dei suoi punti di forza. Sirolo dista appena 1,9 km a piedi o circa 2,4 km in taxi, mentre Ancona si raggiunge in circa 18,4 km su strada. Questo rende Numana una località capace di unire il fascino del borgo costiero con la praticità dei collegamenti verso il capoluogo e il resto della Riviera del Conero.
                  </p>
                  <p className="font-sans text-base text-anthracite-500 leading-relaxed">
                    Anche dal punto di vista demografico e immobiliare, Numana conferma il suo profilo di località costiera molto richiesta. Al 1° gennaio 2026 il Comune conta 3.737 abitanti. Sul mercato residenziale, a maggio 2026 il prezzo medio richiesto per gli immobili in vendita nel comune era di 3.433 €/m², mentre nella zona Numana Paese si attestava a 3.492 €/m²: valori che la collocano tra i mercati più forti della costa anconetana.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-5">
                  {NUMANA_STATS.map((stat) => (
                    <div key={stat.label} className="p-6 bg-white rounded-2xl border border-ivory-200 flex flex-col gap-3">
                      <div className="w-10 h-10 flex items-center justify-center text-terracotta-300">
                        <i className={`${stat.icon} text-2xl`}></i>
                      </div>
                      <p className="font-serif text-2xl text-anthracite-800 font-light">{stat.value}</p>
                      <p className="font-sans text-xs text-anthracite-400 tracking-wide uppercase">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Esperienze */}
          <section
            ref={numanaAttractions.ref}
            className={`fade-in-up ${numanaAttractions.isVisible ? 'visible' : ''} py-20 px-6`}
          >
            <div className="max-w-7xl mx-auto">
              <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-5">Esperienze</p>
              <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-12 leading-snug">
                Cosa vedere e vivere a Numana
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {NUMANA_ATTRACTIONS.map((attr) => (
                  <article key={attr.title} className="bg-ivory-50 rounded-2xl p-7 border border-ivory-200 flex flex-col gap-4">
                    <div className="w-11 h-11 flex items-center justify-center text-terracotta-300 bg-white rounded-xl border border-ivory-200">
                      <i className={`${attr.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-serif text-lg text-anthracite-800 font-medium leading-snug">{attr.title}</h3>
                    <p className="font-sans text-sm text-anthracite-500 leading-relaxed">{attr.text}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* Fonti Numana */}
          <section className="max-w-7xl mx-auto px-6 pb-2 pt-0">
            <div className="border-l-2 border-ivory-200 pl-6">
              <p className="font-sans text-xs tracking-widest uppercase text-anthracite-400 mb-4">Fonti</p>
              <ul className="flex flex-col gap-2">
                {([
                  { label: 'turismonumana.it', url: 'https://www.turismonumana.it/', sub: [
                    { label: 'Centro storico', url: 'https://www.turismonumana.it/luoghi/questa-numana/centro' },
                    { label: 'La Costarella', url: 'https://www.turismonumana.it/luoghi/numana-centro/la-costarella' },
                    { label: 'Museo dell\'Antiquarium', url: 'https://www.turismonumana.it/luoghi/numana-centro/museo-dell-antiquarium' },
                    { label: 'Spiaggia del Frate', url: 'https://www.turismonumana.it/spiagge/del-frate/' },
                  ]},
                  { label: 'comune.numana.an.it — Arco di Torre', url: 'https://www.comune.numana.an.it/it/vivere/arco-di-torre' },
                  { label: 'comune.numana.an.it — Santuario del Crocifisso', url: 'https://www.comune.numana.an.it/it/vivere/il-santuario-del-crocifisso' },
                  { label: 'italia.it — Numana', url: 'https://www.italia.it/it/marche/numana' },
                  { label: 'tuttitalia.it — Numana', url: 'https://www.tuttitalia.it/marche/16-numana/' },
                  { label: 'immobiliare.it — Mercato Numana', url: 'https://www.immobiliare.it/mercato-immobiliare/marche/numana/', sub: [
                    { label: 'Mercato Numana Paese', url: 'https://www.immobiliare.it/mercato-immobiliare/marche/numana/numana/' },
                  ]},
                  { label: 'rome2rio.com — Numana → Ancona', url: 'https://www.rome2rio.com/s/Numana/Ancona' },
                  { label: 'rome2rio.com — Numana → Sirolo', url: 'https://www.rome2rio.com/s/Numana/Sirolo' },
                ] as Array<{ label: string; url: string; sub?: Array<{ label: string; url: string }> }>).map((source) => (
                  <li key={source.url} className="flex flex-col gap-1">
                    <div className="flex items-start gap-2">
                      <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <i className="ri-external-link-line text-xs text-terracotta-300"></i>
                      </div>
                      <a
                        href={source.url}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors leading-relaxed break-all cursor-pointer"
                      >
                        {source.label}
                      </a>
                    </div>
                    {source.sub && (
                      <ul className="ml-6 flex flex-col gap-1">
                        {source.sub.map((sub) => (
                          <li key={sub.url} className="flex items-start gap-2">
                            <div className="w-3 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                              <i className="ri-corner-down-right-line text-xs text-anthracite-300"></i>
                            </div>
                            <a
                              href={sub.url}
                              target="_blank"
                              rel="nofollow noopener noreferrer"
                              className="font-sans text-xs text-anthracite-400 hover:text-terracotta-300 transition-colors leading-relaxed break-all cursor-pointer"
                            >
                              {sub.label}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Video Numana */}
          <section className="max-w-7xl mx-auto px-6 pt-10 pb-20">
            <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">Video</p>
            <div className="max-w-sm rounded-2xl overflow-hidden border border-ivory-200 aspect-[9/16]">
              <iframe
                className="w-full h-full"
                src="https://www.youtube.com/embed/rmxS_PqLX_8"
                title="Video Numana"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                loading="lazy"
              />
            </div>
          </section>
        </>
      )}

      {/* CTA */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-3xl text-anthracite-800 font-light mb-6">
            {t('borgo.cta.title', { name: borgo.name })}
          </h2>
          <p className="font-sans text-sm text-anthracite-500 mb-8 leading-relaxed">
            {t('borgo.cta.text')}
          </p>
          <Link
            to="/contatti"
            className="inline-block font-sans text-sm tracking-widest uppercase px-10 py-4 bg-terracotta-300 text-white hover:bg-terracotta-400 transition-all duration-300 rounded-full cursor-pointer whitespace-nowrap"
          >
            {t('borgo.cta.btn')}
          </Link>
        </div>
      </section>
    </main>
  );
};

export default BorgoDetailPage;
